/**
 * Tab7_Exercise.jsx — Exercise Control (EXCON) / Daily Reporting
 * PSP/CRE Tool v3 — TAB7-DAILY-SCAFFOLD + TAB7-SCHEMATIC-MAP + TAB7-REPORTS-MOVE
 *
 * V3 implemented:
 *   - Tactical schematic map (SVG, data-driven from scenario force structure)
 *   - Daily operational reporting scaffold (actual vs planned delta, CASREP log)
 *   - Real-time battlefield reports section (METHANE, 9-LINE, MASCAL Esc, OOSR)
 *
 * V4 implemented:
 *   - V4-TAB7-002: MRoE Dilemma Inject — EPW/EWIA GC III Art.12 ethical-legal-resource
 *     dilemma with three decision options and consequence calculation
 *   - V4-TAB7-003: Civilian Encounter Inject — GC IV Art. 16 individual + mass scenarios
 *   - V4-TAB7-004: MASCAL Event Inject — AJMedP-1 ss 3.19 artillery / IED / airstrike
 *   - V4-TAB7-005: MEDEVAC Asset Loss — AJMedP-2 golden hour / sortie deficit / CASEVAC
 *   - V4-TAB7-006: MTF Displacement — AJMedP-1 ss 4.8 coverage gap / pre-notification
 *   - V4-TAB7-007: LOC Interdiction — ATP-4.02 blood DOS clock / air resupply / rationing
 *   - ENH-031b: inject decisions wired to calculateCasualties() recalculation; delta panel
 *
 *   - TAB7-R2E: R2E Loss Inject + Branch Plan Framework (AJMedP-1 cascade · golden hour breach ·
 *     branch plan checklist · STRATEVAC uplift · 4 decision options with teaching debrief)
 *
 * V4 planned:
 *   (none -- all V4 Category 1 items complete as of v2.9.0)
 *
 * Author: Dr. Tatar Radu MD
 * Version: 2.9.0 (April 9, 2026)
 */

import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useEffect
} from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Slider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  CircularProgress,
  Snackbar
} from '@mui/material';

import ExpandMoreIcon        from '@mui/icons-material/ExpandMore';
import ArrowBackIcon         from '@mui/icons-material/ArrowBack';
import ConstructionIcon      from '@mui/icons-material/Construction';
import MapIcon               from '@mui/icons-material/Map';
import TimelineIcon          from '@mui/icons-material/Timeline';
import ReportProblemIcon     from '@mui/icons-material/ReportProblem';
import AddCircleOutlineIcon  from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon     from '@mui/icons-material/DeleteOutline';
import FlashOnIcon           from '@mui/icons-material/FlashOn';
import WarningAmberIcon      from '@mui/icons-material/WarningAmber';
import AirplanemodeActiveIcon from '@mui/icons-material/AirplanemodeActive';
import CheckCircleIcon       from '@mui/icons-material/CheckCircle';
import ErrorIcon             from '@mui/icons-material/Error';
import GetAppIcon            from '@mui/icons-material/GetApp';
import ArrowUpwardIcon       from '@mui/icons-material/ArrowUpward';
import GavelIcon             from '@mui/icons-material/Gavel';
import SchoolIcon            from '@mui/icons-material/School';
import PeopleIcon            from '@mui/icons-material/People';
import TimerIcon             from '@mui/icons-material/Timer';
import PlayArrowIcon         from '@mui/icons-material/PlayArrow';
import StopIcon              from '@mui/icons-material/Stop';
import AssessmentIcon        from '@mui/icons-material/Assessment';
import ListAltIcon           from '@mui/icons-material/ListAlt';
import ShowChartIcon         from '@mui/icons-material/ShowChart';
import HealthAndSafetyIcon   from '@mui/icons-material/HealthAndSafety';
import NavigateNextIcon      from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon    from '@mui/icons-material/NavigateBefore';
import CalendarTodayIcon     from '@mui/icons-material/CalendarToday';
import AlarmIcon             from '@mui/icons-material/Alarm';

import {
  formatNineLine,
  formatMETHANE,
  formatOOSR,
  formatBlueLightProtocol
} from '../services/ReportFormattersService.js';

import { useTranslation } from 'react-i18next';
import { useUserRole } from '../context/UserRoleContext';          // P2-S2
import Tab7TrainerPanel from './Tab7_TrainerPanel';               // P2-S2
import { realtimeService } from '../services/realtimeService';    // P2-S4
import { exerciseService } from '../services/exerciseService';    // P2-S6
import { lsWrite, lsRead } from '../utils/lsStore.js';
import { DEFAULT_START_TIME_Z, endInstant, formatZulu, dayLabelZulu } from '../constants/exerciseDates';
import { calculateCasualties } from '../calculationEngine/casualtyCalculator.js'; // ENH-031b
import { getBaseline as getCalculatedBaseline } from '../services/calculationService.js'; // ENH-031b
import { MROE_STATES } from '../constants/mroeConfig.js';
import { Stage, Layer, Circle, Line, Text as KonvaText, Group, Rect as KonvaRect } from 'react-konva'; // V4-TAB7-001

// ============================================================================
// CONSTANTS
// ============================================================================

const TAB7_STORAGE_KEYS = {
  DAILY_LOG:    'psp_tab7_daily_log',
  CASREP_LOG:   'psp_tab7_casrep_log',
  MROE_INJECT:  'psp_tab7_mroe_inject',
  CIV_INJECT:    'psp_tab7_civ_inject',    // V4-TAB7-003
  MASCAL_INJECT:      'psp_tab7_mascal_inject',      // V4-TAB7-004
  MEDEVAC_LOSS_INJECT: 'psp_tab7_medevac_loss_inject',  // V4-TAB7-005
  MTF_DISPLACE_INJECT:  'psp_tab7_mtf_displace_inject',   // V4-TAB7-006
  LOC_INTERDICT_INJECT: 'psp_tab7_loc_interdict_inject',  // V4-TAB7-007
  R2E_LOSS_INJECT:      'psp_tab7_r2e_loss_inject',        // TAB7-R2E
  INJECT_RESULT:        'psp_tab7_inject_result',          // ENH-031b
  SESSION_HISTORY:      'psp_tab7_session_history',        // F2/F3
  EXERCISE_CLOCK:       'psp_tab7_exercise_clock',         // F4
  INJECT_SCRIPT:        'psp_tab7_inject_script',          // F5
  COUNTERMEASURES:      'psp_tab7_countermeasures',        // Session A — positive injects
  DEBRIEF_SNAPSHOTS:    'psp_tab8_debrief_snapshots',      // Session B — calculator snapshots for Tab 8
  EXERCISE_LEVEL:       'psp_tab7_exercise_level',         // Session C — scenario difficulty level
  COA_BASELINE_SLOT:    'psp_tab7_coa_baseline_slot',      // COA-BASELINE-001
  COUNTERATTACK_INJECT: 'psp_tab7_counterattack_inject',   // TAB7-CA
  WEATHER_INJECT:       'psp_tab7_weather_inject',         // TAB7-WX
  CONVOY_INJECT:        'psp_tab7_convoy_inject',          // TAB7-CV
};

// R2-09: every write of the exercise clock tells the other tabs (Tab 2 locks the start date once the clock has started)
const writeExerciseClock = (clock) => {
  lsWrite(TAB7_STORAGE_KEYS.EXERCISE_CLOCK, clock);
  try { window.dispatchEvent(new Event('psp-exercise-clock')); } catch (_e) { /* no window (tests) */ }
};

// IMP-004: Quick-load preset scenarios for each inject type
const INJECT_PRESETS = {
  MROE: [
    { key: 'light_epw', label: 'Light load (12 EPW)',  fields: { epw_count: 12 } },
    { key: 'heavy_epw', label: 'Heavy surge (75 EPW)', fields: { epw_count: 75 } },
  ],
  CIV: [
    { key: 'individual', label: 'Individual (15 civ)', fields: { scenario_type: 'INDIVIDUAL', civ_count: 15  } },
    { key: 'mass',       label: 'Mass (250 civ)',      fields: { scenario_type: 'MASS',       civ_count: 250 } },
  ],
  MASCAL: [
    { key: 'artillery', label: 'Artillery (18, 2h)', fields: { event_type: 'ARTILLERY', casualty_count: 18, arrival_window_hours: 2 } },
    { key: 'ied',       label: 'IED (45, 1h)',       fields: { event_type: 'IED',       casualty_count: 45, arrival_window_hours: 1 } },
    { key: 'airstrike', label: 'Airstrike (82, 4h)', fields: { event_type: 'AIRSTRIKE', casualty_count: 82, arrival_window_hours: 4 } },
  ],
  MEDEVAC: [
    { key: 'ied_1', label: 'IED / 1 ac',           fields: { loss_cause: 'IED',               platforms_lost: 1 } },
    { key: 'ad_2',  label: 'AD engagement / 2 ac',  fields: { loss_cause: 'AD_ENGAGEMENT',     platforms_lost: 2 } },
    { key: 'wx_3',  label: 'Wx grounding / 3 ac',   fields: { loss_cause: 'WEATHER_GROUNDING', platforms_lost: 3 } },
  ],
  MTF: [
    { key: 'indirect',    label: 'Indirect fire (8km, 4h)',  fields: { displacement_cause: 'INDIRECT_FIRE', move_distance_km: 8,  estimated_gap_hrs: 4  } },
    { key: 'advance',     label: 'Enemy advance (20km, 6h)', fields: { displacement_cause: 'ENEMY_ADVANCE', move_distance_km: 20, estimated_gap_hrs: 6  } },
    { key: 'operational', label: 'Operational (35km, 12h)',  fields: { displacement_cause: 'OPERATIONAL',   move_distance_km: 35, estimated_gap_hrs: 12 } },
  ],
  LOC: [
    { key: 'bridge', label: 'Bridge / 2d',      fields: { interdiction_cause: 'BRIDGE_DESTRUCTION', route_closed_days: 2 } },
    { key: 'mine',   label: 'Mine threat / 5d', fields: { interdiction_cause: 'MINE_THREAT',        route_closed_days: 5 } },
    { key: 'enemy',  label: 'Enemy ctrl / 7d',  fields: { interdiction_cause: 'ENEMY_CONTROL',      route_closed_days: 7 } },
  ],
  R2E: [
    { key: 'airstrike',  label: 'Airstrike / DESTROYED',   fields: { loss_cause: 'AIRSTRIKE',           loss_severity: 'DESTROYED' } },
    { key: 'enemy_fire', label: 'Enemy fire / DISABLED',   fields: { loss_cause: 'ENEMY_FIRE',          loss_severity: 'DISABLED'  } },
    { key: 'displaced',  label: 'Forced disp / DISPLACED', fields: { loss_cause: 'FORCED_DISPLACEMENT',  loss_severity: 'DISPLACED' } },
  ],
};

const ESCALATION_THRESHOLD = 0.30; // 30% above plan → escalate

const CASREP_SEVERITIES = ['INFO', 'WARNING', 'CRITICAL'];

// TAB7-REPORTS-MOVE: 4 real-time battlefield reports
const BATTLEFIELD_REPORTS = [
  {
    id: 'methane',
    name: 'METHANE',
    fullName: 'Major Incident Report',
    stanag: 'NATO MASCAL',
    description: 'Mass casualty incident report — filed at point of incident by incident commander.',
    icon: <ReportProblemIcon color="error" />,
    formatter: formatMETHANE,
    filename: 'METHANE_Report.txt'
  },
  {
    id: 'nine_line',
    name: '9-LINE MEDEVAC',
    fullName: 'MEDEVAC Request Format',
    stanag: 'STANAG 2087',  // QM-6I-001: AAMedP-1.5 Forward Aeromedical Evacuation (9-line request format)
    description: 'Per-patient MEDEVAC request — filed at point of injury during evacuation request.',
    icon: <AirplanemodeActiveIcon color="primary" />,
    formatter: formatNineLine,
    filename: '9LINE_MEDEVAC_Request.txt'
  },
  {
    id: 'blue_light',
    name: 'MASCAL Escalation',
    fullName: 'MASCAL Escalation Protocol',
    stanag: 'AMedP-1.10',
    description: 'Surge capacity escalation — activated when MTF reaches MASCAL threshold during operations.',
    icon: <FlashOnIcon color="warning" />,
    formatter: formatBlueLightProtocol,
    filename: 'MASCAL_Escalation_Protocol.txt'
  },
  {
    id: 'oosr',
    name: 'OOSR',
    fullName: 'Out of Service Report',
    stanag: 'Internal',
    description: 'MTF out-of-service notification — filed when a treatment facility is rendered non-operational.',
    icon: <WarningAmberIcon color="warning" />,
    formatter: formatOOSR,
    filename: 'Out_of_Service_Report.txt'
  },
];

// Map role colors / sizing
const ROLE_STYLES = {
  ROLE1: { fill: '#2e7d32', r: 10, label: 'R1' },
  ROLE2B: { fill: '#f57f17', r: 13, label: 'R2B' },
  ROLE2E: { fill: '#e65100', r: 15, label: 'R2E' },
  ROLE3: { fill: '#b71c1c', r: 18, label: 'R3' },
};

// ============================================================================
// SESSION C — SCENARIO LEVEL PRESETS
// ============================================================================

const LEVEL_PRESETS = {
  1: {
    label:            'Level 1 — Introductory',
    description:      'Single scripted MASCAL inject, full information, 5-day operation. Good first exercise for new medical staff officers.',
    compression_min:  30,
    color:            'success',
    script: [
      { label: 'MASCAL Event — Artillery strike, 2 brigades', inject_type: 'MASCAL', op_day: 3 },
    ],
  },
  2: {
    label:            'Level 2 — Standard',
    description:      'Two simultaneous injects (MASCAL + MEDEVAC loss), 7-day operation. Tests multi-threat prioritisation under time pressure.',
    compression_min:  15,
    color:            'info',
    script: [
      { label: 'MASCAL Event — IED mass casualty', inject_type: 'MASCAL',  op_day: 3 },
      { label: 'MEDEVAC Asset Loss — 2 helicopters downed', inject_type: 'MEDEVAC', op_day: 5 },
    ],
  },
  3: {
    label:            'Level 3 — LSCO Complex',
    description:      'Large-Scale Combat: LOC interdiction D+2, MASCAL D+4, R2E loss D+6 — cascading crises within 48h. 10-day operation. Blood starts stressed.',
    compression_min:  15,
    color:            'warning',
    script: [
      { label: 'LOC Interdiction — Supply route cut',    inject_type: 'LOC',     op_day: 2 },
      { label: 'MASCAL Event — Airstrike mass casualty', inject_type: 'MASCAL',  op_day: 4 },
      { label: 'R2E Loss — Role 3 hospital destroyed',   inject_type: 'R2E',     op_day: 6 },
    ],
  },
  4: {
    label:            'Level 4 — Freestyle (EXCON)',
    description:      'No preset injects. Trainer fires events manually via the Inject Script. Full trainer control — suitable for advanced or evaluated exercises.',
    compression_min:  15,
    color:            'error',
    script:           [],
  },
};

// ============================================================================
// COMPONENT
// ============================================================================

// ── SF-3: Student simplified panel — static inject config ────────────────────
// Option labels exactly match the existing trainer inject panels.
// R2E options use string decision keys (not single letters) — displayKey maps them to A/B/C/D.
const STUDENT_INJECT_CONFIG = {
  MROE: {
    title: 'MRoE Dilemma',
    situation: 'EPW/EWIA requiring urgent medical care have arrived at your R2B facility. A Medical Rules of Engagement decision is required.',
    options: [
      { key: 'A', label: 'Full Compliance' },
      { key: 'B', label: '2h Intake Hold' },
      { key: 'C', label: 'STRATEVAC EPW' },
    ],
  },
  CIV: {
    title: 'Civilian Encounter',
    situation: 'Civilian casualties have arrived at the military treatment facility requiring medical care.',
    options: {
      INDIVIDUAL: [
        { key: 'A', label: 'Treat Immediately' },
        { key: 'B', label: 'Triage and Queue' },
        { key: 'C', label: 'Refuse Treatment' },
        { key: 'D', label: 'Stabilize + HNS Transfer' },
      ],
      MASS: [
        { key: 'A', label: 'Triage All, Treat T1 — HNS for T2/T3' },
        { key: 'B', label: 'Stabilize All + STRATEVAC' },
        { key: 'C', label: 'Redirect All to HNS' },
        { key: 'D', label: 'Refuse — MTF at Capacity' },
      ],
    },
  },
  MASCAL: {
    title: 'MASCAL Event',
    situation: 'Mass casualty event — patient inflow exceeds current medical capacity.',
    options: [
      { key: 'A', label: 'Activate MASCAL Protocol' },
      { key: 'B', label: 'Absorb Without Declaring MASCAL' },
      { key: 'C', label: 'MASCAL Protocol + STRATEVAC T2/T3' },
      { key: 'D', label: 'Adjacent MTF Mutual Support' },
    ],
  },
  MEDEVAC: {
    title: 'MEDEVAC Asset Loss',
    situation: 'Medical evacuation helicopter asset lost. MEDEVAC capacity is reduced.',
    options: [
      { key: 'A', label: 'Redistribute + Ground MEDEVAC Surge' },
      { key: 'B', label: 'Absorb Loss, Continue Routine Tasking' },
      { key: 'C', label: 'Request Mutual Aid from Adjacent Formation' },
      { key: 'D', label: 'CASEVAC Substitution (Non-Medical Aircraft)' },
    ],
  },
  MTF: {
    title: 'MTF Displacement',
    situation: 'Role 2B medical facility must displace. A movement decision is required.',
    options: [
      { key: 'A', label: 'Pre-Notify + Staged Displacement' },
      { key: 'B', label: 'Move MTF + Divert Inbound to Adjacent MTF' },
      { key: 'C', label: 'Delay Move Until Patients Cleared' },
      { key: 'D', label: 'Move Without Pre-Notification' },
    ],
  },
  LOC: {
    title: 'LOC Interdiction',
    situation: 'Main Supply Route interdicted. Medical resupply route is blocked.',
    options: [
      { key: 'A', label: 'Air Resupply (STRATEVAC Cargo Mission)' },
      { key: 'B', label: 'Blood Rationing Protocol' },
      { key: 'C', label: 'Accept Risk, Monitor DOS Daily' },
      { key: 'D', label: 'Emergency HNS / NATO Partner Acquisition' },
    ],
  },
  R2E: {
    title: 'R2E Loss',
    situation: 'Role 2 Enhanced surgical facility has been lost or is non-operational.',
    options: [
      { key: 'R2B_SURGE_STRATEVAC', displayKey: 'A', label: 'R2B Surge + STRATEVAC Uplift' },
      { key: 'ADJACENT_R2E',        displayKey: 'B', label: 'Request Adjacent Formation Surgical Capacity' },
      { key: 'CONTINUE_R2B_ROUTINE', displayKey: 'C', label: 'Continue Routine Ops from R2B' },
      { key: 'HALT_OPERATIONS',     displayKey: 'D', label: 'Halt Operations Pending R2E Reconstitution' },
    ],
  },
  // TAB7-CA
  COUNTERATTACK: {
    title: 'Enemy Counterattack',
    situation: 'Brigade under enemy counterattack — WIA surge inbound. A medical response decision is required.',
    options: [
      { key: 'A', label: 'Pre-position FRT + Escalate MEDEVAC' },
      { key: 'B', label: 'Absorb Within Current R2B Capacity' },
      { key: 'C', label: 'Emergency Blood Request + Blue Light' },
      { key: 'D', label: 'STRATEVAC Uplift + Mutual R2B Support' },
    ],
  },
  // TAB7-WX
  WEATHER: {
    title: 'Weather Change',
    situation: 'Adverse weather is degrading or suspending MEDEVAC helicopter operations. A CASEVAC adaptation decision is required.',
    options: [
      { key: 'A', label: 'Full MEDEVAC Grounding — Ground CASEVAC Only' },
      { key: 'B', label: 'Ground CASEVAC + DNBI Shelter Activation' },
      { key: 'C', label: 'Selective MEDEVAC — Critical (T1) Only' },
      { key: 'D', label: 'Weather Waiver — Continue Selective Operations' },
    ],
  },
  // TAB7-CV
  CONVOY: {
    title: 'Supply Convoy Ambush',
    situation: 'Medical resupply convoy ambushed — blood stock and Class VIII lost. A blood supply decision is required.',
    options: [
      { key: 'A', label: 'Emergency Air Resupply (24h)' },
      { key: 'B', label: 'Blood Rationing Protocol' },
      { key: 'C', label: 'Request HNS Blood Support' },
      { key: 'D', label: 'Accept Degraded Stock — Continue as Planned' },
    ],
  },
};

const Tab7Exercise = forwardRef(function Tab7Exercise({ scenario, onNavigate, onComplete, onScenarioUpdate }, ref) {
  const { t } = useTranslation();
  const { userRole, sessionCode, studentId, userName, exerciseId } = useUserRole(); // P2-S2/S4/S5/S6

  // -- State -----------------------------------------------------------------
  const stageRef = useRef(null);                          // V4-TAB7-001: Konva Stage ref for PNG export
  const [nodePositions,    setNodePositions]    = useState({}); // V4-TAB7-001: drag position overrides keyed by node label
  const [showCoverageArcs, setShowCoverageArcs] = useState(false);
  const [showMSRs,         setShowMSRs]         = useState(true);
  const [phaseExpanded, setPhaseExpanded] = useState({
    map: true, daily: false, reports: false, inject: false,
    civInject: false, mascalInject: false, medevacLossInject: false,
    mtfDisplaceInject: false, locInterdictInject: false, r2eLossInject: false, injectImpact: false,
    counterattackInject: false, weatherInject: false, convoyInject: false,
    injectScript: false, debrief: false, sitrep: false,
    projections: true, countermeasures: false,             // Session A
  });
  const [selectedDay,  setSelectedDay]  = useState(1);
  const [dailyLog,     setDailyLog]     = useState({});
  const [casrepLog,    setCasrepLog]    = useState([]);
  const [newCasrep,    setNewCasrep]    = useState({ day: 1, event: '', severity: 'WARNING' });
  const [baseline,     setBaseline]     = useState(null);
  // COA-BASELINE-001: Exercise baseline selector — 'LIVE' | 'COA_A' | 'COA_B'
  const [coaBaselineSlot, setCoaBaselineSlot] = useState(
    () => lsRead(TAB7_STORAGE_KEYS.COA_BASELINE_SLOT) || 'LIVE'
  );
  const [availableCoaSlots, setAvailableCoaSlots] = useState(() => ({
    COA_A: lsRead('psp_cre_coa_1') || null,
    COA_B: lsRead('psp_cre_coa_2') || null,
  }));
  const [mroeInject,   setMroeInject]   = useState(() => {
    const saved = lsRead(TAB7_STORAGE_KEYS.MROE_INJECT);
    return saved || { active: false, day: 3, epw_count: 25, decision: null, decided_at: null,
      threat_class: null, rec_action: null, confidence: null, decision_notes: '' };
  });
  const [civInject, setCivInject] = useState(() => {
    const saved = lsRead(TAB7_STORAGE_KEYS.CIV_INJECT);
    return saved || {
      active: false,
      day: 5,
      scenario_type: 'INDIVIDUAL',
      civ_count: 200,
      decision: null,
      decided_at: null,
      threat_class: null, rec_action: null, confidence: null, decision_notes: '',
    };
  });
  const [mascalInject, setMascalInject] = useState(() => {
    const saved = lsRead(TAB7_STORAGE_KEYS.MASCAL_INJECT);
    return saved || {
      active: false,
      day: 4,
      event_type: 'ARTILLERY',
      casualty_count: 45,
      arrival_window_hours: 2,
      decision: null,
      decided_at: null,
      threat_class: null, rec_action: null, confidence: null, decision_notes: '',
    };
  });
  const [medevacLossInject, setMedevacLossInject] = useState(() => {
    const saved = lsRead(TAB7_STORAGE_KEYS.MEDEVAC_LOSS_INJECT);
    return saved || {
      active: false,
      day: 3,
      platforms_lost: 1,
      loss_cause: 'IED',
      decision: null,
      decided_at: null,
      threat_class: null, rec_action: null, confidence: null, decision_notes: '',
    };
  });
  const [mtfDisplaceInject, setMtfDisplaceInject] = useState(() => {
    const saved = lsRead(TAB7_STORAGE_KEYS.MTF_DISPLACE_INJECT);
    return saved || {
      active: false,
      day: 5,
      displacement_cause: 'INDIRECT_FIRE',
      move_distance_km: 15,
      estimated_gap_hrs: 4,
      decision: null,
      decided_at: null,
      threat_class: null, rec_action: null, confidence: null, decision_notes: '',
    };
  });
  const [locInterdictInject, setLocInterdictInject] = useState(() => {
    const saved = lsRead(TAB7_STORAGE_KEYS.LOC_INTERDICT_INJECT);
    return saved || {
      active: false,
      day: 6,
      interdiction_cause: 'BRIDGE_DESTRUCTION',
      route_closed_days: 3,
      decision: null,
      decided_at: null,
      threat_class: null, rec_action: null, confidence: null, decision_notes: '',
    };
  });
  // TAB7-R2E: R2E Loss Inject + Branch Plan
  const defaultR2eLossInject = {
    active: false, day: 4, loss_cause: 'ENEMY_FIRE', loss_severity: 'DESTROYED',
    decision: null, decided_at: null, checklist: [false, false, false, false, false],
    threat_class: null, rec_action: null, confidence: null, decision_notes: '',
  };
  const [r2eLossInject, setR2eLossInject] = useState(() => {
    const saved = lsRead(TAB7_STORAGE_KEYS.R2E_LOSS_INJECT);
    return saved || defaultR2eLossInject;
  });

  // TAB7-CA: Enemy Counterattack Inject
  const [counterattackInject, setCounterattackInject] = useState(() => {
    const saved = lsRead(TAB7_STORAGE_KEYS.COUNTERATTACK_INJECT);
    return saved || {
      active: false, day: 5, brigade_index: 0,
      surge_casualties: 35, attack_type: 'GROUND_ASSAULT',
      decision: null, decided_at: null,
      threat_class: null, rec_action: null, confidence: null, decision_notes: '',
    };
  });
  // TAB7-WX: Weather Change Inject
  const [weatherInject, setWeatherInject] = useState(() => {
    const saved = lsRead(TAB7_STORAGE_KEYS.WEATHER_INJECT);
    return saved || {
      active: false, day: 7, weather_type: 'IMC', duration_days: 2,
      decision: null, decided_at: null,
      threat_class: null, rec_action: null, confidence: null, decision_notes: '',
    };
  });
  // TAB7-CV: Supply Convoy Ambush Inject
  const [convoyInject, setConvoyInject] = useState(() => {
    const saved = lsRead(TAB7_STORAGE_KEYS.CONVOY_INJECT);
    return saved || {
      active: false, day: 6, blood_units_lost: 50, class_viii_dos_lost: 3,
      decision: null, decided_at: null,
      threat_class: null, rec_action: null, confidence: null, decision_notes: '',
    };
  });

  // BUG-NEXT-003: restore inject result from localStorage across remounts / page refresh.
  // Previously useState(null) — result was lost on hard-refresh even though lsWrite saves it.
  const [injectResult, setInjectResult] = useState(
    () => lsRead(TAB7_STORAGE_KEYS.INJECT_RESULT) || null
  );
  const [injectCalculating, setInjectCalculating] = useState(false);

  // F2: Session history — auto-logged on each successful inject calculation
  const [sessionHistory, setSessionHistory] = useState(
    () => lsRead(TAB7_STORAGE_KEYS.SESSION_HISTORY) || []
  );

  // P3-IMP-001: Custom inject received from trainer via Realtime broadcast
  // null when none active; payload = { id, title, narrative, options[], op_day, fired_at }
  const [activeCustomInject,   setActiveCustomInject]   = useState(null);
  const [customInjectDecision, setCustomInjectDecision] = useState(null); // { key, label, delta_dow, delta_blood }

  // F4: Exercise clock
  const [exerciseClock, setExerciseClock] = useState(
    () => lsRead(TAB7_STORAGE_KEYS.EXERCISE_CLOCK) || { started_at: null, compression_min: 60, running: false }
  );
  const clockTickRef = useRef(null);
  const [clockDisplay, setClockDisplay] = useState({ elapsed: '00:00:00', op_day: 1 });

  // CLOCK-EXT-001: Derive current exercise calendar date from start date + op_day
  // R2-09: the date comes from the scenario (Tab 2 and this tab edit the same two fields); Zulu, UTC arithmetic
  const exerciseCalendarDate = (() => {
    if (!exerciseClock.exercise_start_date) return null;
    try {
      const iso = /Z$/.test(exerciseClock.exercise_start_date) ? exerciseClock.exercise_start_date : `${exerciseClock.exercise_start_date}Z`;
      const start = new Date(iso);
      return formatZulu(new Date(start.getTime() + (clockDisplay.op_day - 1) * 86_400_000));
    } catch (_) { return null; }
  })();

  // F5: Pre-planned inject script
  const [injectScript, setInjectScript] = useState(
    () => lsRead(TAB7_STORAGE_KEYS.INJECT_SCRIPT) || []
  );

  // SF-3: tracks which inject type the trainer just fired — drives student simplified panel
  const [activeStudentInjectType, setActiveStudentInjectType] = useState(null);
  const [scriptDraft, setScriptDraft] = useState({ label: '', inject_type: 'MASCAL', op_day: 1, scheduled_time: '' });

  // Scheduling (F5 extension) — auto-fires script entries at their wall-clock time
  const [schedulingActive,   setSchedulingActive]   = useState(false);
  const [nextScheduled,      setNextScheduled]       = useState(null); // { label, inject_type, scheduled_time, secondsRemaining }
  const injectScriptRef = useRef(injectScript);      // stable ref for interval closure

  // Session A — Countermeasures (positive injects)
  const defaultCountermeasures = {
    blood_request:     { active: false, units: 100,  arrival_day: null },
    ambulance_request: { active: false, count: 2,    arrival_day: null },
    medevac_request:   { active: false, count: 1,    arrival_day: null },
    frt_deploy:        { active: false, brigade_id: null, effective_day: null },
    axp_preposition:   { active: false, brigade_id: null, effective_day: null, distance_reduction_km: 10 },
    mtf_reposition:    { active: false, direction: 'FORWARD', distance_km: 15, effective_day: null },
  };
  const [countermeasures, setCountermeasures] = useState(
    () => lsRead(TAB7_STORAGE_KEYS.COUNTERMEASURES) || defaultCountermeasures
  );

  // P2-S4: session code of the exercise the trainer is currently managing
  // Set by Tab7TrainerPanel via onExerciseSelected callback → triggers channel connect
  const [trainerSessionCode, setTrainerSessionCode] = useState(null);

  // Session C — Exercise level + Snackbar
  const [exerciseLevel, setExerciseLevel] = useState(
    () => lsRead(TAB7_STORAGE_KEYS.EXERCISE_LEVEL) ?? null
  );
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // IMP-014 — Daily Medical SITREP state
  const [sitrepDay,     setSitrepDay]     = useState(1);
  const [sitrepEchelon, setSitrepEchelon] = useState('CORPS');
  const [sitrepSubUnit, setSitrepSubUnit] = useState('');   // selected div or bde id
  const [sitrepData,    setSitrepData]    = useState(null); // generated SITREP output

  useImperativeHandle(ref, () => ({
    flushState: () => {
      lsWrite(TAB7_STORAGE_KEYS.DAILY_LOG,   dailyLog);
      lsWrite(TAB7_STORAGE_KEYS.CASREP_LOG,  casrepLog);
      lsWrite(TAB7_STORAGE_KEYS.MROE_INJECT, mroeInject);
      lsWrite(TAB7_STORAGE_KEYS.CIV_INJECT,         civInject);
      lsWrite(TAB7_STORAGE_KEYS.MASCAL_INJECT,      mascalInject);
      lsWrite(TAB7_STORAGE_KEYS.MEDEVAC_LOSS_INJECT, medevacLossInject);
      lsWrite(TAB7_STORAGE_KEYS.MTF_DISPLACE_INJECT,  mtfDisplaceInject);
      lsWrite(TAB7_STORAGE_KEYS.LOC_INTERDICT_INJECT, locInterdictInject);
      lsWrite(TAB7_STORAGE_KEYS.R2E_LOSS_INJECT, defaultR2eLossInject);
      setR2eLossInject(defaultR2eLossInject);
      lsWrite(TAB7_STORAGE_KEYS.SESSION_HISTORY,      sessionHistory);
      writeExerciseClock(       exerciseClock);
      lsWrite(TAB7_STORAGE_KEYS.INJECT_SCRIPT,        injectScript);
      lsWrite(TAB7_STORAGE_KEYS.COA_BASELINE_SLOT,    coaBaselineSlot);
      lsWrite(TAB7_STORAGE_KEYS.COUNTERATTACK_INJECT, counterattackInject);
      lsWrite(TAB7_STORAGE_KEYS.WEATHER_INJECT,       weatherInject);
      lsWrite(TAB7_STORAGE_KEYS.CONVOY_INJECT,        convoyInject);
      lsWrite(TAB7_STORAGE_KEYS.COUNTERMEASURES, countermeasures);
      lsWrite(TAB7_STORAGE_KEYS.EXERCISE_LEVEL,  exerciseLevel);
    }
  }));

  // -- Restore from localStorage on mount ------------------------------------
  useEffect(() => {
    const savedLog    = lsRead(TAB7_STORAGE_KEYS.DAILY_LOG);
    const savedCasrep = lsRead(TAB7_STORAGE_KEYS.CASREP_LOG);
    const savedCiv    = lsRead(TAB7_STORAGE_KEYS.CIV_INJECT);
    const savedMascal = lsRead(TAB7_STORAGE_KEYS.MASCAL_INJECT);
    if (savedLog)    setDailyLog(savedLog);
    if (savedCasrep) setCasrepLog(savedCasrep);

    // Step 3: Check psp_approved_plan first (locked approved CoA from Tab 6).
    // Falls back to psp_exercise_baseline, then to calculationService baseline.
    const approvedPlan = lsRead('psp_approved_plan');
    const savedBase    = lsRead('psp_exercise_baseline');

    if (approvedPlan) {
      // BUG-S102-033 FIX: psp_approved_plan is stored as a FLAT scenario (writeApprovedPlan
      // does { ...snapshot, results: null, source: 'approved' }) — it is NOT a baseline object.
      // Wrapping it gives baseline.snapshot the correct value so Tab7 exercise injects use
      // the approved CoA's scenario rather than falling back to the last Tab5 calculation.
      const approvedEntry = {
        name:      approvedPlan.operation_parameters?.operation_name ?? 'Approved CoA',
        snapshot:  approvedPlan,
        results:   null,
        loaded_at: new Date().toISOString(),
        source:    'approved',
      };
      setBaseline(approvedEntry);
      // Re-hydrate results from sessionStorage approved plan key
      try {
        const raw = sessionStorage.getItem('psp_approved_plan_results');
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed) setBaseline(prev => ({ ...prev, results: parsed }));
        }
      } catch (_) { /* sessionStorage unavailable — results stay null */ }
    } else if (savedBase) {
      setBaseline(savedBase);
      // BUG-NEXT-012: psp_exercise_baseline is stored with results:null to avoid localStorage quota.
      // Re-hydrate from calculationService (sessionStorage) — same pattern as Tab 6 STORAGE-FIX-002.
      if (savedBase.source === 'tab5' && !savedBase.results) {
        try {
          const _stored = getCalculatedBaseline();
          if (_stored?.results) {
            setBaseline(prev => ({ ...prev, results: _stored.results }));
          }
        } catch (_) { /* sessionStorage unavailable — results stay null */ }
      }
    } else {
      // BUG-T60 fix: Tab7 was not reading Tab6 calculationService baseline.
      // When no psp_exercise_baseline exists, auto-load from psp_calculation_baseline
      // (same source Tab6 uses) so Tab7 does not show a false "No baseline loaded" warning.
      try {
        const calcBaseline = getCalculatedBaseline();
        if (calcBaseline?.scenario) {
          setBaseline({
            name:      calcBaseline.scenario?.operation_parameters?.operation_name ?? 'Baseline',
            snapshot:  calcBaseline.scenario,
            results:   calcBaseline.results ?? null,
            loaded_at: new Date().toISOString(),
            source:    'tab5'
          });
        }
      } catch (_e) { /* no baseline available — keep null */ }
    }

    // Improvement 1: Listen for psp:planApproved event (fired by Tab 6 on approval).
    // Reloads baseline live without requiring Tab 7 remount.
    const handlePlanApproved = () => {
      try {
        const ap = lsRead('psp_approved_plan');
        if (ap) {
          // BUG-S102-033 FIX: same wrapping as mount effect — psp_approved_plan is a flat
          // scenario, not a baseline object. Wrap so baseline.snapshot is populated.
          const entry = {
            name:      ap.operation_parameters?.operation_name ?? 'Approved CoA',
            snapshot:  ap,
            results:   null,
            loaded_at: new Date().toISOString(),
            source:    'approved',
          };
          setBaseline(entry);
          try {
            const raw = sessionStorage.getItem('psp_approved_plan_results');
            if (raw) {
              const parsed = JSON.parse(raw);
              if (parsed) setBaseline(prev => ({ ...prev, results: parsed }));
            }
          } catch (_) { /* non-fatal */ }
        }
      } catch (_) { /* non-fatal */ }
    };
    window.addEventListener('psp:planApproved', handlePlanApproved);

    const savedMedevacLoss  = lsRead(TAB7_STORAGE_KEYS.MEDEVAC_LOSS_INJECT);
    if (savedCiv)        setCivInject(savedCiv);
    if (savedMascal)     setMascalInject(savedMascal);
    const savedMtfDisplace  = lsRead(TAB7_STORAGE_KEYS.MTF_DISPLACE_INJECT);
    if (savedMedevacLoss)  setMedevacLossInject(savedMedevacLoss);
    const savedLocInterdict = lsRead(TAB7_STORAGE_KEYS.LOC_INTERDICT_INJECT);
    if (savedMtfDisplace)   setMtfDisplaceInject(savedMtfDisplace);
    if (savedLocInterdict)  setLocInterdictInject(savedLocInterdict);
    const savedR2eLoss = lsRead(TAB7_STORAGE_KEYS.R2E_LOSS_INJECT);
    if (savedR2eLoss) setR2eLossInject(savedR2eLoss);

    return () => window.removeEventListener('psp:planApproved', handlePlanApproved);
  }, []);

  // COA-BASELINE-001: When slot changes (including mount), push CoA data into baseline state
  useEffect(() => {
    if (coaBaselineSlot === 'LIVE') return;
    const key = coaBaselineSlot === 'COA_A' ? 'psp_cre_coa_1' : 'psp_cre_coa_2';
    const coa = lsRead(key);
    if (coa?.snapshot && coa?.results) {
      setBaseline({
        name:      coa.label || coa.name || coaBaselineSlot.replace('_', ' '),
        snapshot:  coa.snapshot,
        results:   coa.results,
        source:    'coa',
        loaded_at: new Date().toISOString(),
      });
    }
  }, [coaBaselineSlot]);

  // COA-BASELINE-001: Refresh available slots when Tab 5 saves a CoA mid-session
  useEffect(() => {
    const handler = () => setAvailableCoaSlots({
      COA_A: lsRead('psp_cre_coa_1') || null,
      COA_B: lsRead('psp_cre_coa_2') || null,
    });
    window.addEventListener('psp:coaSaved', handler);
    return () => window.removeEventListener('psp:coaSaved', handler);
  }, []);

  // BUG-NEXT-003: skip the first fire of the stale-clear effect so the
  // localStorage-restored result is not immediately wiped on mount.
  const _skipFirstClear = useRef(true);

  // BUG-84-022: Clear stale post-inject delta whenever any inject decision changes.
  // Previously, results from decision A persisted visibly after switching to decision C,
  // misleading the user into thinking the old results still applied.
  useEffect(() => {
    if (_skipFirstClear.current) { _skipFirstClear.current = false; return; }
    setInjectResult(null);
  }, [
    mroeInject.decision,
    civInject.decision,
    mascalInject.decision,
    medevacLossInject.decision,
    mtfDisplaceInject.decision,
    locInterdictInject.decision,
    r2eLossInject.decision,
    counterattackInject.decision,
    weatherInject.decision,
    convoyInject.decision,
  ]);

  // F4: Exercise clock tick — updates every second when running
  useEffect(() => {
    if (exerciseClock.running && exerciseClock.started_at) {
      clockTickRef.current = setInterval(() => {
        const elapsed_ms = Date.now() - new Date(exerciseClock.started_at).getTime();
        const seconds    = Math.floor(elapsed_ms / 1000);
        const h = String(Math.floor(seconds / 3600)).padStart(2, '0');
        const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
        const s = String(seconds % 60).padStart(2, '0');
        const op_day = Math.floor(elapsed_ms / (exerciseClock.compression_min * 60 * 1000)) + 1;
        setClockDisplay({ elapsed: `${h}:${m}:${s}`, op_day });
      }, 1000);
    } else {
      clearInterval(clockTickRef.current);
    }
    return () => clearInterval(clockTickRef.current);
  }, [exerciseClock.running, exerciseClock.started_at, exerciseClock.compression_min]);

  // Scheduling — keep ref current so interval closure reads latest script
  useEffect(() => { injectScriptRef.current = injectScript; }, [injectScript]);

  // Scheduling — auto-fire script entries at their wall-clock scheduled_time
  // Checks every 10 s; fires all due (unfire) entries simultaneously.
  // Only active for TRAINER role with scheduling toggled ON.
  useEffect(() => {
    if (!schedulingActive || userRole !== 'TRAINER') {
      setNextScheduled(null);
      return;
    }

    // Pure helper — format seconds as "1h 4m" / "4m 32s" / "45s"
    const fmtSecs = (secs) => {
      const h = Math.floor(secs / 3600);
      const m = Math.floor((secs % 3600) / 60);
      const s = secs % 60;
      if (h > 0) return `${h}h ${m}m`;
      if (m > 0) return `${m}m ${s}s`;
      return `${s}s`;
    };

    const tick = () => {
      const now     = new Date();
      const nowMins = now.getHours() * 60 + now.getMinutes();
      const script  = injectScriptRef.current;

      // Find all entries whose scheduled_time has arrived and not yet fired
      const due = script.filter(e => {
        if (e.fired || !e.scheduled_time) return false;
        const [h, m] = e.scheduled_time.split(':').map(Number);
        return (h * 60 + m) <= nowMins;
      });

      if (due.length > 0) {
        // Mark all due entries fired in one state update
        setInjectScript(prev => {
          const dueIds = new Set(due.map(e => e.id));
          const updated = prev.map(e =>
            dueIds.has(e.id)
              ? { ...e, fired: true, fired_at: new Date().toISOString(), auto_fired: true }
              : e
          );
          lsWrite(TAB7_STORAGE_KEYS.INJECT_SCRIPT, updated);
          return updated;
        });

        // Broadcast + expand accordion per entry
        const phaseMap = {
          MROE: 'mroeInject', CIV: 'civInject', MASCAL: 'mascalInject',
          MEDEVAC: 'medevacLossInject', MTF: 'mtfDisplaceInject',
          LOC: 'locInterdictInject', R2E: 'r2eLossInject',
          COUNTERATTACK: 'counterattackInject', WEATHER: 'weatherInject', CONVOY: 'convoyInject',
        };
        due.forEach(entry => {
          if (trainerSessionCode) {
            realtimeService.send('inject_fired', {
              inject_type: entry.inject_type,
              op_day:      entry.op_day,
              label:       entry.label,
            });
          }
          const phKey = phaseMap[entry.inject_type];
          if (phKey) setPhaseExpanded(p => ({ ...p, [phKey]: true }));
        });

        setSnackbar({ open: true, severity: 'warning',
          message: `⏰ Auto-fired: ${due.map(e => e.label).join(', ')}` });
      }

      // Compute next upcoming entry for countdown display
      const upcoming = script
        .filter(e => !e.fired && e.scheduled_time)
        .map(e => {
          const [h, m] = e.scheduled_time.split(':').map(Number);
          const target = new Date();
          target.setHours(h, m, 0, 0);
          if (target <= now) target.setDate(target.getDate() + 1); // wrap to tomorrow if past
          const secsRemaining = Math.floor((target - now) / 1000);
          return { label: e.label, inject_type: e.inject_type, scheduled_time: e.scheduled_time,
            secondsRemaining: secsRemaining, formatted: fmtSecs(secsRemaining) };
        })
        .filter(e => e.secondsRemaining > 0)
        .sort((a, b) => a.secondsRemaining - b.secondsRemaining)[0] || null;

      setNextScheduled(upcoming);
    };

    tick(); // run immediately on enable
    const timer = setInterval(tick, 10000);
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedulingActive, userRole, trainerSessionCode]);

  // F1: Shared handler — update structured decision meta fields on any inject
  const handleInjectMeta = (setFn, storageKey, fields) => {
    setFn(prev => {
      const updated = { ...prev, ...fields };
      lsWrite(storageKey, updated);
      return updated;
    });
  };

  // ── P2-S4: Realtime — student inject/clock receive handlers ─────────────
  const handleInjectReceived = React.useCallback((payload) => {
    const { inject_type, op_day, label } = payload || {};
    const phaseMap = {
      MROE: 'mroeInject', CIV: 'civInject', MASCAL: 'mascalInject',
      MEDEVAC: 'medevacLossInject', MTF: 'mtfDisplaceInject',
      LOC: 'locInterdictInject', R2E: 'r2eLossInject',
      COUNTERATTACK: 'counterattackInject', WEATHER: 'weatherInject', CONVOY: 'convoyInject',
    };
    // Activate the inject + set op_day, preserving student's own params
    const daySetters = {
      MROE:          () => setMroeInject(p => ({ ...p, active: true, day: op_day || p.day })),
      CIV:           () => setCivInject(p => ({ ...p, active: true, day: op_day || p.day })),
      MASCAL:        () => setMascalInject(p => ({ ...p, active: true, day: op_day || p.day })),
      MEDEVAC:       () => setMedevacLossInject(p => ({ ...p, active: true, day: op_day || p.day })),
      MTF:           () => setMtfDisplaceInject(p => ({ ...p, active: true, day: op_day || p.day })),
      LOC:           () => setLocInterdictInject(p => ({ ...p, active: true, day: op_day || p.day })),
      R2E:           () => setR2eLossInject(p => ({ ...p, active: true, day: op_day || p.day })),
      COUNTERATTACK: () => setCounterattackInject(p => ({ ...p, active: true, day: op_day || p.day })),
      WEATHER:       () => setWeatherInject(p => ({ ...p, active: true, day: op_day || p.day })),
      CONVOY:        () => setConvoyInject(p => ({ ...p, active: true, day: op_day || p.day })),
    };
    daySetters[inject_type]?.();
    setActiveStudentInjectType(inject_type); // SF-3: drive student simplified panel
    const phKey = phaseMap[inject_type];
    if (phKey) setPhaseExpanded(prev => ({ ...prev, [phKey]: true }));
    setTimeout(() => {
      const el = document.querySelector(`[data-inject-phase="${inject_type}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
    setSnackbar({ open: true, severity: 'warning',
      message: `⚡ Inject: ${label || inject_type} (D+${op_day}) — configure and run calculation` });
  }, []); // useState setters are stable refs — empty deps safe

  // SF-3: Student submits a decision from the simplified panel
  // Uses functional updater so no inject state deps needed (setters are stable).
  const handleStudentDecisionSubmit = React.useCallback((injectType, decisionKey) => {
    const setterMap = {
      MROE:          [setMroeInject,           TAB7_STORAGE_KEYS.MROE_INJECT],
      CIV:           [setCivInject,             TAB7_STORAGE_KEYS.CIV_INJECT],
      MASCAL:        [setMascalInject,          TAB7_STORAGE_KEYS.MASCAL_INJECT],
      MEDEVAC:       [setMedevacLossInject,     TAB7_STORAGE_KEYS.MEDEVAC_LOSS_INJECT],
      MTF:           [setMtfDisplaceInject,     TAB7_STORAGE_KEYS.MTF_DISPLACE_INJECT],
      LOC:           [setLocInterdictInject,    TAB7_STORAGE_KEYS.LOC_INTERDICT_INJECT],
      R2E:           [setR2eLossInject,         TAB7_STORAGE_KEYS.R2E_LOSS_INJECT],
      COUNTERATTACK: [setCounterattackInject,   TAB7_STORAGE_KEYS.COUNTERATTACK_INJECT],
      WEATHER:       [setWeatherInject,         TAB7_STORAGE_KEYS.WEATHER_INJECT],
      CONVOY:        [setConvoyInject,          TAB7_STORAGE_KEYS.CONVOY_INJECT],
    };
    const entry = setterMap[injectType];
    if (!entry) return;
    const [setter, storageKey] = entry;
    setter(prev => {
      const updated = { ...prev, decision: decisionKey, decided_at: new Date().toISOString() };
      lsWrite(storageKey, updated);
      return updated;
    });
  }, []); // setters and lsWrite are stable — empty deps safe

  const handleClockReceived = React.useCallback((payload) => {
    if (!payload) return;
    setExerciseClock(payload);
    writeExerciseClock( payload);
  }, []);

  const handleRoomClosed = React.useCallback(() => {
    setSnackbar({ open: true, severity: 'error',
      message: '⚠️ The trainer has closed this exercise room.' });
  }, []);

  // P3-IMP-001: Custom inject received from trainer
  const handleCustomInjectReceived = React.useCallback((payload) => {
    if (!payload?.id) return;
    setActiveCustomInject(payload);
    setCustomInjectDecision(null); // clear any previous decision on new inject
    setSnackbar({ open: true, severity: 'warning',
      message: `⚡ Custom inject: "${payload.title}" (D+${payload.op_day}) — respond below` });
    // Scroll to the custom inject panel
    setTimeout(() => {
      const el = document.querySelector('[data-custom-inject-panel]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
  }, []);

  // P3-IMP-001: Student chooses a response to a custom inject
  // Logs to session history, persists to Supabase, broadcasts to trainer Live Board
  const handleCustomInjectChoose = React.useCallback((inject, option) => {
    setCustomInjectDecision(option);

    // Build history entry
    const entry = {
      id:          crypto.randomUUID(),
      timestamp:   new Date().toISOString(),
      type:        'CUSTOM_INJECT',
      op_day:      inject.op_day,
      inject_title: inject.title,
      chosen_key:  option.key,
      chosen_label: option.label,
      delta: {
        total_dow:   option.delta_dow   || 0,
        blood_units: option.delta_blood || 0,
      },
    };

    setSessionHistory(prev => {
      const updated = [...prev, entry];
      lsWrite(TAB7_STORAGE_KEYS.SESSION_HISTORY, updated);

      // Persist to Supabase (fire-and-forget, uses closure over studentId/exerciseId)
      if (userRole === 'STUDENT' && studentId && exerciseId) {
        exerciseService.saveStudentSession(studentId, exerciseId, updated)
          .catch(() => {});
      }
      return updated;
    });

    // Broadcast to trainer Live Decision Board
    if (userRole === 'STUDENT' && sessionCode && studentId) {
      realtimeService.send('student_decision', {
        student_id:   studentId,
        student_name: userName,
        op_day:       inject.op_day,
        decisions:    [{ id: inject.id, label: inject.title, decision: option.key, confidence: null }],
        delta:        { total_dow: option.delta_dow || 0, blood_units: option.delta_blood || 0 },
        timestamp:    entry.timestamp,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole, studentId, exerciseId, sessionCode, userName]);

  // P2-S4: Trainer channel connection — fires when TrainerPanel selects an OPEN exercise
  useEffect(() => {
    if (userRole !== 'TRAINER' || !trainerSessionCode) return;
    realtimeService.connect(trainerSessionCode);
    return () => realtimeService.disconnect();
  }, [userRole, trainerSessionCode]);

  // P2-S4: Student channel subscription — fires on join
  useEffect(() => {
    if (userRole !== 'STUDENT' || !sessionCode) return;
    realtimeService.connect(sessionCode);
    const offInject       = realtimeService.on('inject_fired',    handleInjectReceived);
    const offClock        = realtimeService.on('clock_update',    handleClockReceived);
    const offClosed       = realtimeService.on('room_closed',     handleRoomClosed);
    const offCustomInject = realtimeService.on('custom_inject',   handleCustomInjectReceived); // P3-IMP-001
    return () => { offInject(); offClock(); offClosed(); offCustomInject(); realtimeService.disconnect(); };
  }, [userRole, sessionCode, handleInjectReceived, handleClockReceived, handleRoomClosed, handleCustomInjectReceived]);

  // F4: Start / stop exercise clock — stopping preserves current op_day for trainer review
  const handleClockToggle = () => {
    setExerciseClock(prev => {
      const next = prev.running
        ? { ...prev, running: false, started_at: null }
        : { ...prev, running: true,  started_at: new Date().toISOString() };
      writeExerciseClock( next);
      // Do NOT reset clockDisplay on stop — trainer keeps current day for review
      if (userRole === 'TRAINER' && trainerSessionCode) realtimeService.send('clock_update', next); // P2-S4
      return next;
    });
  };

  const handleCompressionChange = (_, val) => {
    if (!val) return;
    setExerciseClock(prev => {
      const next = { ...prev, compression_min: val };
      writeExerciseClock( next);
      if (userRole === 'TRAINER' && trainerSessionCode) realtimeService.send('clock_update', next); // P2-S4
      return next;
    });
  };

  // R2-09 (2026-10-22): the start date and Zulu time are ONE scenario setting shared with Tab 2. The trainer edits it here (before the clock is
  // started); the clock keeps a copy that is broadcast to the students.
  // R2-09: label of an operation day with its date and Zulu hour when the operation has a start date: 'D+3 (25 Oct 2026 06:00Z)'. One convention:
  // D+N is operation day N (it was D+N in some places and D+(N-1) in others).
  const dayLbl = (n) => {
    const op = scenario?.operation_parameters;
    try { return dayLabelZulu(op?.operation_start_date || '', op?.operation_start_time_z || DEFAULT_START_TIME_Z, Number(n)); } catch (_e) { return `D+${n}`; }
  };
  const _scenarioStartDate = scenario?.operation_parameters?.operation_start_date || '';
  const _scenarioStartTimeZ = scenario?.operation_parameters?.operation_start_time_z || DEFAULT_START_TIME_Z;
  const _scenarioDuration = scenario?.operation_parameters?.duration_days ?? 14;   // same default as opDays below
  const handleExerciseStartChange = (dateStr, timeStr) => {
    if (typeof onScenarioUpdate !== 'function') return;
    const op = scenario?.operation_parameters || {};
    const nextDate = dateStr !== undefined ? dateStr : _scenarioStartDate;
    const nextTime = timeStr !== undefined ? (timeStr || DEFAULT_START_TIME_Z) : _scenarioStartTimeZ;
    onScenarioUpdate({ operation_parameters: { ...op, operation_start_date: nextDate || null, operation_start_time_z: nextTime } });
  };
  useEffect(() => {
    const iso = _scenarioStartDate ? `${_scenarioStartDate}T${_scenarioStartTimeZ}:00Z` : null;
    if ((exerciseClock.exercise_start_date || null) === iso) return;
    const updated = { ...exerciseClock, exercise_start_date: iso };
    setExerciseClock(updated);
    writeExerciseClock( updated);
    if (userRole === 'TRAINER' && trainerSessionCode) realtimeService.send('clock_update', updated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_scenarioStartDate, _scenarioStartTimeZ]);

  // Session A — Trainer clock step controls (only active when clock is stopped)
  const handleClockStep = (delta) => {
    if (exerciseClock.running) return;
    const maxDay = (scenario?.operation_parameters?.duration_days ?? opDays) + 1;
    setClockDisplay(prev => ({
      ...prev,
      op_day: Math.max(1, Math.min(maxDay, prev.op_day + delta)),
    }));
  };

  const handleClockJump = (day) => {
    if (exerciseClock.running) return;
    const maxDay = (scenario?.operation_parameters?.duration_days ?? opDays) + 1;
    const target = Math.max(1, Math.min(maxDay, Number(day) || 1));
    setClockDisplay(prev => ({ ...prev, op_day: target }));
  };

  // Session A — Planning window utility (used in history tagging + projections grid)
  const CM_LEAD_TIME = {
    blood_request: 2, ambulance_request: 2, medevac_request: 3,
    frt_deploy: 2, axp_preposition: 1, mtf_reposition: 2,
  };

  const PLANNING_WINDOWS = ['ASSESS', 'ORIENT', 'REVIEW', 'CONFIRM', 'APPROVE', 'GUIDANCE'];
  const WINDOW_COLOR = {
    ASSESS: 'default', ORIENT: 'primary', REVIEW: 'info',
    CONFIRM: 'warning', APPROVE: 'error', GUIDANCE: 'secondary',
  };

  function getPlanningWindow(eventDay, currentOpDay) {
    const offset = (Number(eventDay) || 1) - (Number(currentOpDay) || 1);
    if (offset < 0)   return 'ASSESS';
    if (offset === 0) return 'ORIENT';
    if (offset === 1) return 'REVIEW';
    if (offset === 2) return 'CONFIRM';
    if (offset === 3) return 'APPROVE';
    return 'GUIDANCE';
  }

  // Session A — Countermeasure handler
  const handleCountermeasureChange = (type, fields) => {
    setCountermeasures(prev => {
      const updated = { ...prev, [type]: { ...prev[type], ...fields } };
      lsWrite(TAB7_STORAGE_KEYS.COUNTERMEASURES, updated);
      return updated;
    });
  };

  // Session C — Level selector handler
  const handleSelectLevel = (level) => {
    const preset = LEVEL_PRESETS[level];
    if (!preset) return;
    setExerciseLevel(level);
    lsWrite(TAB7_STORAGE_KEYS.EXERCISE_LEVEL, level);
    // Pre-set clock compression for the selected level
    const clockUpdate = { ...exerciseClock, compression_min: preset.compression_min };
    setExerciseClock(clockUpdate);
    writeExerciseClock( clockUpdate);
    // Pre-populate inject script with level preset entries (add UUIDs)
    const scriptEntries = preset.script.map(e => ({
      ...e,
      id:       crypto.randomUUID(),
      fired:    false,
      fired_at: null,
    }));
    setInjectScript(scriptEntries);
    lsWrite(TAB7_STORAGE_KEYS.INJECT_SCRIPT, scriptEntries);
  };

  // IMP-013: Clear Session — resets all exercise run data, keeps inject configuration
  const [showClearDialog, setShowClearDialog] = useState(false);

  const _decisionReset = { decision: null, decided_at: null, threat_class: null, rec_action: null, confidence: null, decision_notes: '' };

  const handleClearSession = () => {
    // Reset all inject decision fields — keep configuration (day, counts, scenario params)
    const _clearInject = (prev) => ({ ...prev, ..._decisionReset });
    setMroeInject(prev       => { const n = _clearInject(prev); lsWrite(TAB7_STORAGE_KEYS.MROE_INJECT,         n); return n; });
    setCivInject(prev        => { const n = _clearInject(prev); lsWrite(TAB7_STORAGE_KEYS.CIV_INJECT,          n); return n; });
    setMascalInject(prev     => { const n = _clearInject(prev); lsWrite(TAB7_STORAGE_KEYS.MASCAL_INJECT,       n); return n; });
    setMedevacLossInject(prev => { const n = _clearInject(prev); lsWrite(TAB7_STORAGE_KEYS.MEDEVAC_LOSS_INJECT, n); return n; });
    setMtfDisplaceInject(prev => { const n = _clearInject(prev); lsWrite(TAB7_STORAGE_KEYS.MTF_DISPLACE_INJECT, n); return n; });
    setLocInterdictInject(prev => { const n = _clearInject(prev); lsWrite(TAB7_STORAGE_KEYS.LOC_INTERDICT_INJECT, n); return n; });
    setR2eLossInject(prev    => { const n = { ..._clearInject(prev), checklist: [false,false,false,false,false] }; lsWrite(TAB7_STORAGE_KEYS.R2E_LOSS_INJECT, n); return n; });
    // Reset session history, post-inject result, exercise clock, and countermeasures
    setSessionHistory([]);
    lsWrite(TAB7_STORAGE_KEYS.SESSION_HISTORY, []);
    setInjectResult(null);
    const _clockReset = { started_at: null, compression_min: exerciseClock.compression_min, running: false, exercise_start_date: exerciseClock.exercise_start_date ?? null };
    setExerciseClock(_clockReset);
    writeExerciseClock( _clockReset);
    setClockDisplay({ elapsed: '00:00:00', op_day: 1 });
    setCountermeasures(defaultCountermeasures);
    lsWrite(TAB7_STORAGE_KEYS.COUNTERMEASURES, defaultCountermeasures);
    // Session B — also clear debrief snapshots
    lsWrite(TAB7_STORAGE_KEYS.DEBRIEF_SNAPSHOTS, []);
    // Reset inject script fired flags — keep entries but mark all Ready again
    const _scriptReset = injectScript.map(e => ({ ...e, fired: false, fired_at: null }));
    setInjectScript(_scriptReset);
    lsWrite(TAB7_STORAGE_KEYS.INJECT_SCRIPT, _scriptReset);
    setShowClearDialog(false);
  };

  // -- Load Tab 5 baseline directly in Tab 7 (avoids needing to visit Tab 6 first)
  const [baselineLoadError, setBaselineLoadError] = React.useState('');
  function handleLoadTab5Baseline() {
    setBaselineLoadError('');
    const stored = getCalculatedBaseline();
    if (!stored?.scenario) {
      setBaselineLoadError('No Tab 5 baseline found. Go to Tab 5, click Calculate, then return here.');
      return;
    }
    const entry = {
      name:      stored.scenario?.operation_parameters?.operation_name ?? 'Baseline',
      snapshot:  stored.scenario,
      results:   stored.results ?? null,
      loaded_at: new Date().toISOString(),
      source:    'tab5',
    };
    setBaseline(entry);
    try { lsWrite('psp_exercise_baseline', { ...entry, results: null }); } catch (_) {}
  }

  // COA-BASELINE-001: Switch exercise baseline between live calc and frozen CoA snapshots
  const handleCoaBaselineChange = React.useCallback((newSlot) => {
    if (!newSlot) return;
    setCoaBaselineSlot(newSlot);
    lsWrite(TAB7_STORAGE_KEYS.COA_BASELINE_SLOT, newSlot);
    if (newSlot === 'LIVE') {
      const stored = getCalculatedBaseline();
      if (stored?.scenario) {
        setBaseline({
          name:      stored.scenario?.operation_parameters?.operation_name ?? 'Baseline',
          snapshot:  stored.scenario,
          results:   stored.results ?? null,
          loaded_at: new Date().toISOString(),
          source:    'tab5',
        });
      }
      // If no live baseline, the "Load Tab 5 Baseline" button is already shown — no change needed
    }
    // CoA case is handled by the coaBaselineSlot useEffect above
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // -- Derived data ----------------------------------------------------------
  const opDays     = scenario?.operation_parameters?.duration_days ?? 14;
  const forceData  = scenario?.force ?? {};
  // F2-ARCH-010: forceBuilderResult.brigades[] now populated for all echelons.
  // Singular fallback retained for pre-F2 persisted data.
  const brigades   = forceData.brigades
    ?? forceData.forceBuilderResult?.brigades
    ?? (forceData.forceBuilderResult?.brigade ? [forceData.forceBuilderResult.brigade] : []);
  const bdeCount   = Math.max(1, brigades.length || 3);

  // Derive division count from brigades
  const divCount = Math.max(1, (() => {
    const divIds = new Set(brigades.map(b => b.parent_division_id ?? b.division_id ?? null).filter(Boolean));
    return divIds.size || Math.ceil(bdeCount / 3);
  })());

  // Planned daily values from Tab 6 baseline
  const planDaily = (() => {
    const r = baseline?.results;
    if (!r) return null;
    const days = baseline?.snapshot?.operation_parameters?.duration_days ?? opDays;
    const corps = r.by_corps ?? {};
    return {
      kia:     Math.round((corps.total_kia    ?? 0) / days),
      wia:     Math.round((corps.total_wia    ?? 0) / days),
      dnbi:    Math.round((corps.total_dnbi   ?? 0) / days),
    };
  })();

  const currentDayLog = dailyLog[selectedDay] ?? { kia: '', wia: '', dnbi: '', dow: '', dos: '', sorties: '' };

  // -- Handlers --------------------------------------------------------------
  const saveDayLog = () => {
    const updated = { ...dailyLog, [selectedDay]: { ...currentDayLog } };
    setDailyLog(updated);
    lsWrite(TAB7_STORAGE_KEYS.DAILY_LOG, updated);
  };

  const updateDayField = (field, val) => {
    setDailyLog(prev => ({
      ...prev,
      [selectedDay]: { ...(prev[selectedDay] ?? {}), [field]: val }
    }));
  };

  const addCasrep = () => {
    if (!newCasrep.event.trim()) return;
    const entry = { id: Date.now(), ...newCasrep, status: 'OPEN', created: new Date().toISOString() };
    const updated = [...casrepLog, entry];
    setCasrepLog(updated);
    lsWrite(TAB7_STORAGE_KEYS.CASREP_LOG, updated);
    setNewCasrep(prev => ({ ...prev, event: '' }));
  };

  const toggleCasrepStatus = (id) => {
    const updated = casrepLog.map(c => c.id === id ? { ...c, status: c.status === 'OPEN' ? 'CLOSED' : 'OPEN' } : c);
    setCasrepLog(updated);
    lsWrite(TAB7_STORAGE_KEYS.CASREP_LOG, updated);
  };

  const deleteCasrep = (id) => {
    const updated = casrepLog.filter(c => c.id !== id);
    setCasrepLog(updated);
    lsWrite(TAB7_STORAGE_KEYS.CASREP_LOG, updated);
  };

  const generateReport = (rpt) => {
    try {
      const reportData = {
        scenario:  baseline?.snapshot ?? scenario,
        results:   baseline?.results ?? null,
        day:       selectedDay,
        generated: new Date().toISOString(),
      };
      // BUG-T61 fix: pass scenario directly (not the wrapper object) so formatters
      // receive the correct shape; formatters now return strings not objects.
      const rawResult = rpt.formatter(reportData.scenario ?? reportData);
      const text = typeof rawResult === 'string' ? rawResult : JSON.stringify(rawResult, null, 2);
      const blob = new Blob([text ?? '[No data — load a Tab 5 baseline in Tab 6 first.]'],
        { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a   = document.createElement('a');
      a.href = url; a.download = rpt.filename;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch {
      // formatter may fail without results — still generate stub
      const blob = new Blob(['[Report unavailable — load a Tab 5 baseline in Tab 6.]'],
        { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a   = document.createElement('a');
      a.href = url; a.download = rpt.filename;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    }
  };

  // ── Escalation check ──────────────────────────────────────────────────────
  const escalationAlerts = (() => {
    if (!planDaily) return [];
    const log = dailyLog[selectedDay];
    if (!log) return [];
    const alerts = [];
    for (const [field, label] of [['kia', 'KIA'], ['wia', 'WIA'], ['dnbi', 'DNBI']]) {
      const actual  = Number(log[field]);
      const planned = planDaily[field];
      if (!isNaN(actual) && actual > 0 && planned > 0) {
        const ratio = actual / planned;
        if (ratio > 1 + ESCALATION_THRESHOLD) {
          alerts.push({ field, label, actual, planned, ratio });
        }
      }
    }
    return alerts;
  })();

  // ============================================================================
  // F1: Structured decision meta — shared helper rendered inside each inject panel
  // ============================================================================

  // IMP-014 — Build per-day SITREP data from baseline results + snapshot
  function buildSitrep(results, snapshot, day) {
    const dayIdx = Math.max(0, day - 1);

    // Brigade → division lookup from snapshot force structure
    const brigDivMap  = {};
    const divNameMap  = {};
    (snapshot?.force?.brigades  || []).forEach(b => { brigDivMap[b.id] = b.parent_division_id || 'unknown'; });
    (snapshot?.force?.divisions || []).forEach(d => { divNameMap[d.id] = d.name || d.division_name || d.id; });
    // Also try division_configs for names
    (snapshot?.operation_parameters?.division_configs || []).forEach(dc => {
      if (dc.division_id && dc.name) divNameMap[dc.division_id] = dc.name;
    });

    // Per-brigade row for the selected day
    const brigadeRows = (results.by_brigade || [])
      .filter(br => !(br.brigade_name || '').toLowerCase().includes('medical'))
      .map(br => {
        const d   = br.by_day?.[dayIdx] ?? {};
        const divId = brigDivMap[br.brigade_id] || 'unknown';
        return {
          id:          br.brigade_id,
          name:        br.brigade_name || br.brigade_id,
          division_id: divId,
          division_name: divNameMap[divId] || divId,
          kia:     d.battle?.kia ?? 0,
          wia:     d.battle?.wia ?? 0,
          dow:     d.battle?.dow ?? 0,
          dnbi:    d.dnbi?.total ?? 0,
          rtd:     (d.battle?.rtd ?? 0) + (d.dnbi?.rtd ?? 0),
          t1:      d.triage?.T1 ?? 0,
          t2:      d.triage?.T2 ?? 0,
          t3:      d.triage?.T3 ?? 0,
          blood_prbc:       d.resources?.blood_prbc ?? 0,
          blood_ffp:        d.resources?.blood_ffp  ?? 0,
          surgical_backlog: d.resources?.surgical_backlog ?? 0,
          gh_ok:     d.golden_hour?.cumulative_golden_hour_compliant ?? null,
          mascal:    d.facility_mascal?.level ?? 'NORMAL',
          evac_overwhelmed: d.casevac?.is_overwhelmed ?? false,
        };
      });

    // Group into divisions for CORPS view
    const divMap = {};
    brigadeRows.forEach(br => {
      if (!divMap[br.division_id]) {
        divMap[br.division_id] = {
          id: br.division_id, name: br.division_name,
          kia: 0, wia: 0, dow: 0, dnbi: 0, rtd: 0, t1: 0,
          blood_prbc: 0, blood_ffp: 0, mascal_any: false, brigades: [],
        };
      }
      const dv = divMap[br.division_id];
      dv.kia += br.kia; dv.wia += br.wia; dv.dow += br.dow;
      dv.dnbi += br.dnbi; dv.rtd += br.rtd; dv.t1 += br.t1;
      dv.blood_prbc += br.blood_prbc; dv.blood_ffp += br.blood_ffp;
      if (br.mascal === 'MASCAL' || br.mascal === 'CRITICAL') dv.mascal_any = true;
      dv.brigades.push(br);
    });
    const divisionRows = Object.values(divMap);

    // Day totals
    const dayTotals = results.by_day?.[dayIdx]?.totals ?? {};

    // R3 corps bed status
    const r3 = results.mascal_analysis?.r3_corps?.timeline?.[dayIdx] ?? null;

    // R2E per division
    const r2eDivs = (results.mascal_analysis?.r2e_divisions || []).map(d => ({
      division_id:   d.division_id,
      division_name: d.division_name || divNameMap[d.division_id] || d.division_id,
      beds:   d.r2e_beds ?? 0,
      occ:    d.timeline?.[dayIdx]?.occupancy_percent ?? 0,
      status: d.timeline?.[dayIdx]?.status ?? 'NORMAL',
      admissions: d.timeline?.[dayIdx]?.admissions ?? 0,
    }));

    // R2 per brigade
    const r2Bdes = (results.mascal_analysis?.brigades || []).map(b => ({
      brigade_id:   b.brigade_id,
      brigade_name: b.brigade_name,
      beds:   b.r2b_beds ?? 0,
      occ:    b.timeline?.[dayIdx]?.occupancy_percent ?? 0,
      status: b.timeline?.[dayIdx]?.status ?? 'NORMAL',
    }));

    // IMP-014 additions: cumulative blood PRBC consumed D1→day, surgical backlog, MEDEVAC sorties
    let _cumBloodPRBC = 0;
    let _surgBacklog  = 0;
    const _combatBdes = (results.by_brigade || []).filter(br => !(br.brigade_name || '').toLowerCase().includes('medical'));
    _combatBdes.forEach(br => {
      for (let di = 0; di <= dayIdx; di++) {
        _cumBloodPRBC += br.by_day?.[di]?.resources?.blood_prbc ?? 0;
      }
      _surgBacklog += br.by_day?.[dayIdx]?.resources?.surgical_backlog ?? 0;
    });
    const _initialPRBC = snapshot?.logistics?.medical_supplies?.initial_stock?.prbc_units || 0;
    const bloodPRBCRemaining = _initialPRBC > 0 ? Math.max(0, Math.round(_initialPRBC - _cumBloodPRBC)) : null;
    const cumulativeBloodPRBC = Math.round(_cumBloodPRBC);
    const surgBacklogTotal    = Math.round(_surgBacklog);
    const initialPRBC         = _initialPRBC;
    const medevacDailySorties = snapshot?.medical_configuration?.tactical_evac_config?.air_pool?.nominal_daily_sorties || 0;

    return { brigadeRows, divisionRows, dayTotals, r3, r2eDivs, r2Bdes, divNameMap,
             bloodPRBCRemaining, cumulativeBloodPRBC, surgBacklogTotal, initialPRBC, medevacDailySorties };
  }

  // IMP-014 — Status chip helper
  function sitrepStatusChip(status) {
    const cfg = {
      NORMAL:   { color: 'success', label: 'NORMAL'   },
      WARNING:  { color: 'warning', label: 'WARNING'  },
      CRITICAL: { color: 'error',   label: 'CRITICAL' },
      MASCAL:   { color: 'error',   label: 'MASCAL'   },
    };
    const c = cfg[status] || cfg.NORMAL;
    return <Chip label={c.label} color={c.color} size="small" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }} />;
  }

  function renderDecisionMeta(inject, setFn, storageKey) {
    if (!inject.decision) return null;
    return (
      <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 1 }}>
          {t('tab7.decision_analysis')}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>{t('tab7.threat_type')}</Typography>
            <ToggleButtonGroup size="small" exclusive
              value={inject.threat_class}
              onChange={(_, v) => v && handleInjectMeta(setFn, storageKey, { threat_class: v })}>
              <ToggleButton value="MISSION" sx={{ fontSize: '0.7rem', py: 0.25 }}>{t('tab7.threat_mission')}</ToggleButton>
              <ToggleButton value="LIFE"    sx={{ fontSize: '0.7rem', py: 0.25 }}>{t('tab7.threat_life')}</ToggleButton>
              <ToggleButton value="BOTH"    sx={{ fontSize: '0.7rem', py: 0.25 }}>{t('tab7.threat_both')}</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>{t('tab7.rec_action')}</Typography>
            <ToggleButtonGroup size="small" exclusive
              value={inject.rec_action}
              onChange={(_, v) => v && handleInjectMeta(setFn, storageKey, { rec_action: v })}>
              <ToggleButton value="HOLD"        sx={{ fontSize: '0.7rem', py: 0.25 }}>{t('tab7.action_hold')}</ToggleButton>
              <ToggleButton value="PREPOSITION" sx={{ fontSize: '0.7rem', py: 0.25 }}>{t('tab7.action_preposition')}</ToggleButton>
              <ToggleButton value="EVACUATE"    sx={{ fontSize: '0.7rem', py: 0.25 }}>{t('tab7.action_evacuate')}</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>{t('tab7.confidence')}</Typography>
            <ToggleButtonGroup size="small" exclusive
              value={inject.confidence}
              onChange={(_, v) => v && handleInjectMeta(setFn, storageKey, { confidence: v })}>
              <ToggleButton value="HIGH"   sx={{ fontSize: '0.7rem', py: 0.25 }}>{t('tab7.conf_high')}</ToggleButton>
              <ToggleButton value="MEDIUM" sx={{ fontSize: '0.7rem', py: 0.25 }}>{t('tab7.conf_medium')}</ToggleButton>
              <ToggleButton value="LOW"    sx={{ fontSize: '0.7rem', py: 0.25 }}>{t('tab7.conf_low')}</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
        <TextField fullWidth size="small"
          label={t('tab7.decision_notes_label')}
          value={inject.decision_notes || ''}
          onChange={e => handleInjectMeta(setFn, storageKey, { decision_notes: e.target.value })}
          multiline minRows={1} maxRows={3}
          sx={{ mt: 0.5 }}
        />
      </Box>
    );
  }

  // ============================================================================
  // RENDER: Tactical Schematic Map (V4-TAB7-001 — React-Konva, 2D drag, PNG export)
  // ============================================================================

  function renderSchematicMap() {
    const W = 800, H = 480;
    const FEBA_Y = 95;
    const R1_Y   = 145;
    const R2B_Y  = 240;
    const R2E_Y  = 340;
    const R3_Y   = 430;

    // --- Node position arrays (same data-driven logic as V3 SVG) ---
    // BUG-T59 fix: R3 label should use "R3 MTF" (NATO AJP-4.10C) not "R3 CSH" (US Army term)
    const r3Nodes = [{ x: W / 2, y: R3_Y, label: 'R3 MTF', name: 'Role 3 Medical Treatment Facility' }];

    const r2eSpacing = W / (divCount + 1);
    const r2eNodes = Array.from({ length: divCount }, (_, i) => ({
      x: r2eSpacing * (i + 1), y: R2E_Y,
      label: `R2E-D${i + 1}`, name: `Div ${i + 1} Field Hospital`,
    }));

    const r2bSpacing = W / (bdeCount + 1);
    const r2bNodes = Array.from({ length: bdeCount }, (_, i) => ({
      x: r2bSpacing * (i + 1), y: R2B_Y,
      label: `R2B-B${i + 1}`, name: brigades[i]?.name ?? `Brigade ${i + 1} Aid Station`,
    }));

    const r1Nodes = Array.from({ length: bdeCount }, (_, i) => ({
      x: r2bSpacing * (i + 1), y: R1_Y,
      label: `R1-B${i + 1}`, name: `Bde ${i + 1} Aid Stn`,
    }));

    // MSR routes — waypoints through R1 -> R2B -> R2E -> R3 (tension curve)
    const tab3Routes = scenario?.medical_configuration?.evacuation_routes ?? [];
    const msrPaths = r1Nodes.map((r1, i) => {
      const r2b = r2bNodes[i];
      const r2e = r2eNodes[Math.floor(i / Math.ceil(bdeCount / divCount))] ?? r2eNodes[0];
      const r3  = r3Nodes[0];
      const routeName = tab3Routes[i]?.name || `MSR ${i + 1}`;
      return {
        points: [r1.x, r1.y, r2b.x, r2b.y, r2e.x, r2e.y, r3.x, r3.y],
        label: routeName,
        labelX: (r1.x + r2b.x) / 2,
        labelY: (r1.y + r2b.y) / 2 - 10,
      };
    });

    // BUG-TAB7-TEMPLATE-001: configured check (same logic as V3)
    // BUG-T58 fix: facilities use f.type OR f.role_type OR f.facility_type — check all paths.
    const medFacilities = scenario?.medical_configuration?.facilities ?? [];
    const getFacType = (f) => f.facility_type || f.type || f.role_type || f.role || '';
    const getBeds7   = (f) => f.beds || f.bed_count || f.capacity || 0;
    const r2eConfigured = medFacilities.some(f => {
      const t = getFacType(f).toUpperCase().replace(/_/g, '');
      return (t === 'ROLE2ENHANCED' || t === 'ROLE2E') && getBeds7(f) > 0;
    });
    const r3Configured = medFacilities.some(f => {
      const t = getFacType(f).toUpperCase().replace(/_/g, '');
      return (t === 'ROLE3' || t === 'R3') && getBeds7(f) > 0;
    });

    // --- Drag helpers ---
    const getPos = (label, defX, defY) => {
      const ov = nodePositions[label];
      return ov ? [ov.x, ov.y] : [defX, defY];
    };
    const handleDragEnd = (label, e) => {
      setNodePositions(prev => ({ ...prev, [label]: { x: e.target.x(), y: e.target.y() } }));
    };

    // --- PNG export ---
    const handleExportPNG = () => {
      if (!stageRef.current) return;
      const uri = stageRef.current.toDataURL({ mimeType: 'image/png', pixelRatio: 2 });
      const a = document.createElement('a');
      a.download = 'tactical_schematic_map.png';
      a.href = uri;
      a.click();
    };

    // --- Konva MTF node renderer ---
    // BUG-84-021: aboveEchelon=true renders "corps asset" instead of "not configured"
    const renderKonvaMTF = (node, style, configured = true, aboveEchelon = false) => {
      const [px, py] = getPos(node.label, node.x, node.y);
      return (
        <Group
          key={node.label}
          x={px}
          y={py}
          draggable
          onDragEnd={(e) => handleDragEnd(node.label, e)}
        >
          {showCoverageArcs && (
            <Circle
              radius={style.r * 5}
              stroke={style.fill}
              strokeWidth={1}
              dash={[4, 4]}
              opacity={0.4}
              fillEnabled={false}
            />
          )}
          <Circle
            radius={style.r}
            fill={configured ? style.fill : '#f5f5f5'}
            stroke={configured ? 'white' : style.fill}
            strokeWidth={2}
            dash={configured ? [] : [4, 3]}
            opacity={configured ? 1 : 0.7}
          />
          {/* Medical cross — only for configured nodes */}
          {configured && (
            <Group>
              <Line
                points={[0, -style.r * 0.55, 0, style.r * 0.55]}
                stroke="white"
                strokeWidth={2.5}
              />
              <Line
                points={[-style.r * 0.55, 0, style.r * 0.55, 0]}
                stroke="white"
                strokeWidth={2.5}
              />
            </Group>
          )}
          {/* Node label */}
          <KonvaText
            y={style.r + 4}
            text={node.label}
            fontSize={10}
            fontStyle="bold"
            fill={configured ? '#333' : '#9e9e9e'}
            width={70}
            offsetX={35}
            align="center"
          />
          {!configured && !aboveEchelon && (
            <KonvaText
              y={style.r + 17}
              text="not configured"
              fontSize={8}
              fontStyle="italic"
              fill="#bdbdbd"
              width={80}
              offsetX={40}
              align="center"
            />
          )}
          {/* BUG-84-021: R3 at Division echelon is a corps asset, not missing config */}
          {aboveEchelon && (
            <KonvaText
              y={style.r + 17}
              text="corps asset"
              fontSize={8}
              fontStyle="italic"
              fill="#90a4ae"
              width={80}
              offsetX={40}
              align="center"
            />
          )}
        </Group>
      );
    };

    // Legend rows
    const legendItems = [
      { ...ROLE_STYLES.ROLE1,  label: 'Role 1 -- Aid Station',      cy: H - 58 },
      { ...ROLE_STYLES.ROLE2B, label: 'Role 2B -- Brigade Aid Stn', cy: H - 45 },
      { ...ROLE_STYLES.ROLE2E, label: 'Role 2E -- Field Hospital',  cy: H - 32 },
      { ...ROLE_STYLES.ROLE3,  label: 'Role 3 -- Corps Hospital',   cy: H - 19 },
    ];

    return (
      <Box>
        {/* Controls */}
        <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip
            label={showCoverageArcs ? 'Coverage Arcs: ON' : 'Coverage Arcs: OFF'}
            size="small"
            color={showCoverageArcs ? 'success' : 'default'}
            onClick={() => setShowCoverageArcs(p => !p)}
            variant={showCoverageArcs ? 'filled' : 'outlined'}
          />
          <Chip
            label={showMSRs ? 'MSR Routes: ON' : 'MSR Routes: OFF'}
            size="small"
            color={showMSRs ? 'info' : 'default'}
            onClick={() => setShowMSRs(p => !p)}
            variant={showMSRs ? 'filled' : 'outlined'}
          />
          <Button
            size="small"
            variant="outlined"
            startIcon={<GetAppIcon />}
            onClick={handleExportPNG}
            sx={{ ml: 'auto' }}
          >
            Export PNG
          </Button>
          {Object.keys(nodePositions).length > 0 && (
            <Button
              size="small"
              variant="text"
              color="inherit"
              onClick={() => setNodePositions({})}
            >
              Reset Layout
            </Button>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
            Schematic -- {bdeCount} bde / {divCount} div / 1 corps. Drag MTF nodes to reposition. MTF depth is doctrinal, not geographic.
          </Typography>
        </Box>

        {/* Konva canvas */}
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflowX: 'auto' }}>
          <Stage ref={stageRef} width={W} height={H}>
            <Layer>
              {/* Background zones */}
              <KonvaRect x={0} y={0} width={W} height={FEBA_Y - 10} fill="#ffebee" />
              <KonvaRect x={0} y={0} width={W} height={80} fill="#ffcdd2" />
              <KonvaText x={12} y={10} text="ENEMY TERRITORY" fontSize={11} fill="#c62828" fontStyle="bold" />
              <KonvaRect x={0} y={FEBA_Y - 10} width={W} height={H - FEBA_Y + 10} fill="#e8f5e9" />

              {/* Zone depth labels — right side */}
              <KonvaText x={W - 108} y={R1_Y - 6}  text="R1 (0-5 km)"    fontSize={9} fill="#888" align="right" width={100} />
              <KonvaText x={W - 108} y={R2B_Y - 6} text="R2B (5-15 km)"  fontSize={9} fill="#888" align="right" width={100} />
              <KonvaText x={W - 108} y={R2E_Y - 6} text="R2E (15-40 km)" fontSize={9} fill="#888" align="right" width={100} />
              <KonvaText x={W - 108} y={R3_Y - 6}  text="R3 (40-100 km)" fontSize={9} fill="#888" align="right" width={100} />

              {/* FEBA line */}
              <Line points={[0, FEBA_Y, W, FEBA_Y]} stroke="#c62828" strokeWidth={2.5} dash={[10, 6]} />
              <KonvaRect x={4} y={FEBA_Y - 9} width={44} height={14} cornerRadius={3} fill="#c62828" />
              <KonvaText x={8} y={FEBA_Y - 7} text="FEBA" fontSize={10} fill="white" fontStyle="bold" />

              {/* Patient flow arrow (left margin) */}
              <Line
                points={[22, R1_Y + 18, 22, R3_Y - 18]}
                stroke="#888"
                strokeWidth={1.5}
                dash={[3, 3]}
              />
              {/* Arrowhead at bottom */}
              <Line
                points={[17, R3_Y - 26, 22, R3_Y - 18, 27, R3_Y - 26]}
                stroke="#888"
                strokeWidth={1.5}
              />
              {/* Rotated label */}
              <KonvaText
                x={32}
                y={(R1_Y + R3_Y) / 2 + 28}
                text="Patient flow"
                fontSize={9}
                fill="#888"
                rotation={-90}
              />

              {/* MSR routes */}
              {showMSRs && msrPaths.map((msr, i) => (
                <Group key={i}>
                  <Line
                    points={msr.points}
                    stroke="#90a4ae"
                    strokeWidth={2}
                    dash={[5, 3]}
                    opacity={0.6}
                    tension={0.3}
                  />
                  <KonvaText
                    x={msr.labelX - 30}
                    y={msr.labelY}
                    text={msr.label}
                    fontSize={8}
                    fill="#78909c"
                    fontStyle="italic"
                    align="center"
                    width={60}
                  />
                </Group>
              ))}

              {/* MTF nodes — render in depth order (back to front) */}
              {/* BUG-84-021: at DIVISION echelon R3 is a corps asset — mark aboveEchelon */}
              {r3Nodes.map(n => renderKonvaMTF(
                n, ROLE_STYLES.ROLE3, r3Configured,
                !r3Configured && scenario?.force?.echelon === 'DIVISION'
              ))}
              {r2eNodes.map(n => renderKonvaMTF(n, ROLE_STYLES.ROLE2E, r2eConfigured))}
              {r2bNodes.map(n => renderKonvaMTF(n, ROLE_STYLES.ROLE2B, true))}
              {r1Nodes.map(n => renderKonvaMTF(n, ROLE_STYLES.ROLE1, true))}

              {/* Legend */}
              <KonvaRect x={12} y={H - 72} width={188} height={64} cornerRadius={4} fill="white" opacity={0.9} stroke="#ccc" strokeWidth={1} />
              {legendItems.map(({ fill, r: lr, label, cy }) => (
                <Group key={label}>
                  <Circle x={24} y={cy} radius={lr * 0.6} fill={fill} />
                  <KonvaText x={36} y={cy - 5} text={label} fontSize={9} fill="#333" />
                </Group>
              ))}
            </Layer>
          </Stage>
        </Box>
      </Box>
    );
  }

  // ============================================================================
  // RENDER: Daily Operational Reporting
  // ============================================================================

  function renderDailyReporting() {
    const numDays = opDays;
    return (
      <Box>
        {/* Day selector */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 60 }}>
            Day {selectedDay}
          </Typography>
          <Slider
            value={selectedDay}
            min={1}
            max={numDays}
            step={1}
            marks
            onChange={(_, v) => setSelectedDay(v)}
            sx={{ flex: 1, maxWidth: 400 }}
            size="small"
          />
          <Typography variant="caption" color="text.secondary">of {numDays}</Typography>
        </Box>

        {escalationAlerts.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }} icon={<ErrorIcon />}>
            <AlertTitle>⚠ ESCALATION THRESHOLD EXCEEDED — Day {selectedDay}</AlertTitle>
            {escalationAlerts.map(({ label, actual, planned, ratio }) => (
              <Box key={label}>
                <strong>{label}:</strong> Actual {actual} vs planned {planned} ({((ratio - 1) * 100).toFixed(0)}% above plan).
              </Box>
            ))}
            <Box sx={{ mt: 1 }}>
              Return to <strong>Tab 6 → Phase 1 (Assess)</strong> and file a MEDASSESSREP. Create a revised variant and regenerate Annex Q.
              <Button
                size="small"
                variant="outlined"
                color="error"
                sx={{ ml: 2 }}
                onClick={() => onNavigate && onNavigate(5)}
                startIcon={<ArrowUpwardIcon />}
              >
                Go to Tab 6
              </Button>
            </Box>
          </Alert>
        )}

        {!planDaily && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No Tab 5 baseline loaded in Tab 6. Load a baseline there to enable actual vs planned delta comparison.
          </Alert>
        )}

        {/* Input form */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
            Day {selectedDay} — Actual Figures
          </Typography>
          <Grid container spacing={2}>
            {[
              { field: 'kia',     label: 'KIA (actual)',              plan: planDaily?.kia },
              { field: 'wia',     label: 'WIA (actual)',              plan: planDaily?.wia },
              { field: 'dnbi',    label: 'DNBI (actual)',             plan: planDaily?.dnbi },
              { field: 'dow',     label: 'DoW (died of wounds)',      plan: null },
              { field: 'dos',     label: 'Blood DOS remaining (days)', plan: null },
              { field: 'sorties', label: 'MEDEVAC sorties flown',     plan: null },
            ].map(({ field, label, plan }) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={field}>
                <TextField
                  fullWidth
                  size="small"
                  label={label}
                  type="number"
                  value={currentDayLog[field] ?? ''}
                  onChange={e => updateDayField(field, e.target.value)}
                  helperText={plan != null ? `Planned: ~${plan}/day` : undefined}
                  inputProps={{ min: 0 }}
                />
              </Grid>
            ))}
          </Grid>
          <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={saveDayLog}
            >
              Save Day {selectedDay}
            </Button>
          </Box>
        </Paper>

        {/* Delta summary table */}
        {planDaily && Object.keys(dailyLog).length > 0 && (
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell>Day</TableCell>
                  <TableCell align="right">KIA actual</TableCell>
                  <TableCell align="right">KIA plan</TableCell>
                  <TableCell align="right">WIA actual</TableCell>
                  <TableCell align="right">WIA plan</TableCell>
                  <TableCell align="right">DNBI actual</TableCell>
                  <TableCell align="right">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(dailyLog)
                  .sort(([a], [b]) => Number(a) - Number(b))
                  .map(([day, log]) => {
                    const hasData = log.kia !== '' || log.wia !== '';
                    if (!hasData) return null;
                    const kiaRatio = planDaily.kia > 0 ? Number(log.kia) / planDaily.kia : 0;
                    const wiaRatio = planDaily.wia > 0 ? Number(log.wia) / planDaily.wia : 0;
                    const exceedKIA = kiaRatio > 1 + ESCALATION_THRESHOLD;
                    const exceedWIA = wiaRatio > 1 + ESCALATION_THRESHOLD;
                    const rowColor = (exceedKIA || exceedWIA) ? 'error.50' : 'inherit';
                    return (
                      <TableRow key={day} sx={{ bgcolor: rowColor }}>
                        <TableCell>{dayLbl(day)}</TableCell>
                        <TableCell align="right" sx={{ color: exceedKIA ? 'error.main' : 'inherit' }}>
                          {log.kia || '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'text.secondary' }}>{planDaily.kia}</TableCell>
                        <TableCell align="right" sx={{ color: exceedWIA ? 'error.main' : 'inherit' }}>
                          {log.wia || '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'text.secondary' }}>{planDaily.wia}</TableCell>
                        <TableCell align="right">{log.dnbi || '—'}</TableCell>
                        <TableCell align="right">
                          {(exceedKIA || exceedWIA)
                            ? <Chip label="ESCALATE" size="small" color="error" />
                            : <Chip label="OK" size="small" color="success" icon={<CheckCircleIcon />} />
                          }
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* CASREP Log */}
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>{t('tab7.casrep_log')}</Typography>
        <Paper variant="outlined" sx={{ p: 1.5, mb: 1 }}>
          <Grid container spacing={1} alignItems="flex-end">
            <Grid size={{ xs: 12, sm: 2 }}>
              <TextField
                fullWidth
                size="small"
                label={t('tab7.casrep_day_label')}
                type="number"
                value={newCasrep.day}
                onChange={e => setNewCasrep(p => ({ ...p, day: Number(e.target.value) }))}
                inputProps={{ min: 1, max: opDays }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 7 }}>
              <TextField
                fullWidth
                size="small"
                label={t('tab7.casrep_event_label')}
                value={newCasrep.event}
                onChange={e => setNewCasrep(p => ({ ...p, event: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && addCasrep()}
              />
            </Grid>
            <Grid size={{ xs: 8, sm: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('tab7.casrep_severity_label')}</InputLabel>
                <Select
                  value={newCasrep.severity}
                  label={t('tab7.casrep_severity_label')}
                  onChange={e => setNewCasrep(p => ({ ...p, severity: e.target.value }))}
                >
                  {CASREP_SEVERITIES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 4, sm: 1 }}>
              <Button fullWidth variant="contained" size="small" onClick={addCasrep}>
                <AddCircleOutlineIcon />
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {casrepLog.length > 0 && (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell>{t('tab7.casrep_col_day')}</TableCell>
                  <TableCell>{t('tab7.casrep_col_event')}</TableCell>
                  <TableCell>{t('tab7.casrep_col_severity')}</TableCell>
                  <TableCell>{t('tab7.casrep_col_status')}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {casrepLog.map(c => (
                  <TableRow key={c.id} sx={{ opacity: c.status === 'CLOSED' ? 0.55 : 1 }}>
                    <TableCell>{dayLbl(c.day)}</TableCell>
                    <TableCell>{c.event}</TableCell>
                    <TableCell>
                      <Chip
                        label={c.severity}
                        size="small"
                        color={c.severity === 'CRITICAL' ? 'error' : c.severity === 'WARNING' ? 'warning' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={c.status}
                        size="small"
                        color={c.status === 'OPEN' ? 'warning' : 'success'}
                        onClick={() => toggleCasrepStatus(c.id)}
                        sx={{ cursor: 'pointer' }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => deleteCasrep(c.id)} color="error">
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  }

  // ============================================================================
  // RENDER: Real-Time Battlefield Reports (TAB7-REPORTS-MOVE)
  // ============================================================================

  function renderBattlefieldReports() {
    return (
      <Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          These reports are filed during operations — not pre-mission planning products.
          They require scenario context from Tab 6. If no Tab 6 baseline is loaded,
          the exported file will contain a stub with guidance text.
        </Alert>
        <Grid container spacing={2}>
          {BATTLEFIELD_REPORTS.map(rpt => (
            <Grid size={{ xs: 12, sm: 6 }} key={rpt.id}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  {rpt.icon}
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1 }}>{rpt.name}</Typography>
                  <Chip label={rpt.stanag} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {rpt.description}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<GetAppIcon />}
                  onClick={() => generateReport(rpt)}
                  fullWidth
                >
                  Generate {rpt.name} (.txt)
                </Button>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // ============================================================================
  // RENDER: Enemy Counterattack Inject (TAB7-CA)
  // ============================================================================

  function renderCounterattackInject() {
    const surgeCas   = Number(counterattackInject.surge_casualties) || 35;
    const hitBde     = brigades[counterattackInject.brigade_index ?? 0];
    const bdeLabel   = hitBde?.name || hitBde?.unit_name || `Brigade ${(counterattackInject.brigade_index ?? 0) + 1}`;
    const bloodNeeded = Math.round(surgeCas * 0.35 * 6);   // ~35% T1, 6 PRBC each
    const surgHrs     = Math.round(surgeCas * 0.35 * 2);   // ~2h per T1 surgical case
    const noBaseline  = !baseline;

    const handleCAField = (field, value) => {
      const updated = { ...counterattackInject, [field]: value };
      setCounterattackInject(updated);
      lsWrite(TAB7_STORAGE_KEYS.COUNTERATTACK_INJECT, updated);
    };
    const handleCADecision = (opt) => {
      const updated = { ...counterattackInject, decision: opt, decided_at: new Date().toISOString() };
      setCounterattackInject(updated);
      lsWrite(TAB7_STORAGE_KEYS.COUNTERATTACK_INJECT, updated);
    };

    return (
      <Box data-inject-phase="COUNTERATTACK">
        {noBaseline && (
          <Alert severity="warning" sx={{ mb: 2 }}>Load a Tab 5 baseline to enable inject calculation.</Alert>
        )}

        {/* Config */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>{t('tab7.inject_config')}</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField fullWidth size="small" label={t('tab7.inject_on_day')} type="number"
                value={counterattackInject.day}
                onChange={e => handleCAField('day', Number(e.target.value))}
                inputProps={{ min: 1, max: opDays }}
                helperText={`D+1 to D+${opDays}`} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('tab7.attack_type')}</InputLabel>
                <Select value={counterattackInject.attack_type} label={t('tab7.attack_type')}
                  onChange={e => handleCAField('attack_type', e.target.value)}>
                  <MenuItem value="GROUND_ASSAULT">Ground Assault</MenuItem>
                  <MenuItem value="ARMOURED_THRUST">Armoured Thrust</MenuItem>
                  <MenuItem value="INFILTRATION">Infiltration</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Surge casualties: {surgeCas}
              </Typography>
              <Slider value={surgeCas} min={10} max={120} step={5}
                onChange={(_, v) => handleCAField('surge_casualties', v)}
                marks={[{ value: 10, label: '10' }, { value: 120, label: '120' }]}
                size="small" />
            </Grid>
            {brigades.length > 1 && (
              <Grid size={{ xs: 12, sm: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('tab7.affected_brigade')}</InputLabel>
                  <Select value={counterattackInject.brigade_index ?? 0} label={t('tab7.affected_brigade')}
                    onChange={e => handleCAField('brigade_index', Number(e.target.value))}>
                    {brigades.map((b, i) => (
                      <MenuItem key={i} value={i}>{b.name || b.unit_name || `Brigade ${i + 1}`}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Situation */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'error.main', borderWidth: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Situation — Day {dayLbl(counterattackInject.day)}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Corps G2 reports a {counterattackInject.attack_type.replace(/_/g, ' ').toLowerCase()} by an
            estimated battalion-plus force against <strong>{bdeLabel}</strong>. Brigade reports{' '}
            <strong>{surgeCas} WIA</strong> in a 90-minute contact period — surge demand is
            immediate. Triage estimate: {Math.round(surgeCas * 0.35)} T1 / {Math.round(surgeCas * 0.40)} T2 /
            {' '}{Math.round(surgeCas * 0.25)} T3.
          </Typography>
          <Grid container spacing={1}>
            <Grid size={{ xs: 4 }}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'error.50', borderRadius: 1 }}>
                <Typography variant="h6" fontWeight="bold" color="error.main">{Math.round(surgeCas * 0.35)}</Typography>
                <Typography variant="caption" display="block">{t('tab7.civilian_inject.t1_immediate')}</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'warning.50', borderRadius: 1 }}>
                <Typography variant="h6" fontWeight="bold" color="warning.dark">{Math.round(surgeCas * 0.40)}</Typography>
                <Typography variant="caption" display="block">{t('tab7.civilian_inject.t2_delayed')}</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="h6" fontWeight="bold">{Math.round(surgeCas * 0.25)}</Typography>
                <Typography variant="caption" display="block">{t('tab7.civilian_inject.t3_minor')}</Typography>
              </Box>
            </Grid>
          </Grid>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Estimated blood demand: ~{bloodNeeded} PRBC · Surgical hours: ~{surgHrs}h · R2B must receive T1 within 1h.
          </Typography>
        </Paper>

        <Divider sx={{ my: 2 }}><Chip label="Command Decision" size="small" /></Divider>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            {
              opt: 'A', color: 'success',
              title: 'Pre-position FRT + escalate MEDEVAC',
              body: 'Deploy Forward Resuscitation Team to brigade R1 immediately. Place MEDEVAC on 5-minute standby. Accept consumption of 2 additional surgical team-hours. (+1 FRT team; +2 MEDEVAC sorties modelled)',
              risk: 'Consumes surgical reserve. If attack is feint, resource was burned unnecessarily.',
              doc: 'AJMedP-1 ss 3.15',
            },
            {
              opt: 'B', color: 'warning',
              title: 'Absorb within current R2B capacity',
              body: 'Trust current MTF configuration to absorb the surge. Hold FRT in reserve. Accept longer R1 dwell time for T2 patients.',
              risk: 'R2B census spike — if T1 count exceeds surgical capacity, DOW increases.',
              doc: 'AJMedP-1 triage protocol',
            },
            {
              opt: 'C', color: 'info',
              title: 'Emergency blood request + Blue Light',
              body: 'Submit URGENT blood request (+40 PRBC from corps reserve). Activate Blue Light evacuation protocol for all T1.',
              risk: 'Blood arrives in 24h — does not address immediate T1 surgical queue.',
              doc: 'ATP-4.02 blood resupply',
            },
            {
              opt: 'D', color: 'secondary',
              title: 'STRATEVAC uplift + mutual R2B support',
              body: 'Request immediate STRATEVAC platform for critical overflow. Activate neighbouring R2B mutual support agreement. (+3 sorties; +20 blood units modelled)',
              risk: 'STRATEVAC tasking from corps — competing priorities may delay approval.',
              doc: 'AJMedP-2 STRATEVAC',
            },
          ].map(({ opt, color, title, body, risk, doc }) => (
            <Grid size={{ xs: 12, md: 6 }} key={opt}>
              <Paper variant="outlined" sx={{
                p: 2, height: '100%', cursor: 'pointer',
                borderColor: counterattackInject.decision === opt ? `${color}.main` : 'divider',
                borderWidth: counterattackInject.decision === opt ? 2 : 1,
                bgcolor: counterattackInject.decision === opt ? `${color}.50` : 'inherit',
              }} onClick={() => handleCADecision(opt)}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Chip label={`Option ${opt}`} size="small" color={color} />
                  <Typography variant="subtitle2" fontWeight="bold">{title}</Typography>
                </Box>
                <Typography variant="body2" sx={{ mb: 1 }}>{body}</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  <strong>Risk:</strong> {risk}
                </Typography>
                <Chip label={doc} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
              </Paper>
            </Grid>
          ))}
        </Grid>
        {renderDecisionMeta(counterattackInject, setCounterattackInject, TAB7_STORAGE_KEYS.COUNTERATTACK_INJECT)}
      </Box>
    );
  }

  // ============================================================================
  // RENDER: Weather Change Inject (TAB7-WX)
  // ============================================================================

  function renderWeatherInject() {
    const durDays    = Number(weatherInject.duration_days) || 2;
    const noBaseline = !baseline;
    const wxLabels   = { IMC: 'IMC / Low cloud ceiling', BLIZZARD: 'Blizzard / Snow', EXTREME_HEAT: 'Extreme heat / Dust' };
    const degradePct = { A: 80, B: 80, C: 60, D: 40 };

    const handleWXField = (field, value) => {
      const updated = { ...weatherInject, [field]: value };
      setWeatherInject(updated);
      lsWrite(TAB7_STORAGE_KEYS.WEATHER_INJECT, updated);
    };
    const handleWXDecision = (opt) => {
      const updated = { ...weatherInject, decision: opt, decided_at: new Date().toISOString() };
      setWeatherInject(updated);
      lsWrite(TAB7_STORAGE_KEYS.WEATHER_INJECT, updated);
    };

    return (
      <Box data-inject-phase="WEATHER">
        {noBaseline && (
          <Alert severity="warning" sx={{ mb: 2 }}>Load a Tab 5 baseline to enable inject calculation.</Alert>
        )}

        {/* Config */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>{t('tab7.inject_config')}</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField fullWidth size="small" label={t('tab7.inject_on_day')} type="number"
                value={weatherInject.day}
                onChange={e => handleWXField('day', Number(e.target.value))}
                inputProps={{ min: 1, max: opDays }}
                helperText={`D+1 to D+${opDays}`} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('tab7.weather_event')}</InputLabel>
                <Select value={weatherInject.weather_type} label={t('tab7.weather_event')}
                  onChange={e => handleWXField('weather_type', e.target.value)}>
                  <MenuItem value="IMC">IMC / Low cloud ceiling</MenuItem>
                  <MenuItem value="BLIZZARD">Blizzard / Snow</MenuItem>
                  <MenuItem value="EXTREME_HEAT">Extreme heat / Dust</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Duration: {durDays} day{durDays > 1 ? 's' : ''}
              </Typography>
              <Slider value={durDays} min={1} max={5} step={1}
                onChange={(_, v) => handleWXField('duration_days', v)}
                marks={[{ value: 1, label: '1d' }, { value: 5, label: '5d' }]}
                size="small" />
            </Grid>
          </Grid>
        </Paper>

        {/* Situation */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'info.main', borderWidth: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Situation — Day {dayLbl(weatherInject.day)}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            METOCC reports <strong>{wxLabels[weatherInject.weather_type]}</strong> conditions forecast
            for the next <strong>{durDays} day{durDays > 1 ? 's' : ''}</strong>. Aviation is
            AMBER/RED — MEDEVAC helicopter operations are degraded or suspended. DNBI rates are
            expected to spike ({weatherInject.weather_type === 'EXTREME_HEAT' ? '+15% heat injury' :
              weatherInject.weather_type === 'BLIZZARD' ? '+20% cold injury / frostbite' : '+10% respiratory'}).
            Ground CASEVAC becomes the primary evacuation modality.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip size="small" color="warning" label="MEDEVAC: DEGRADED" sx={{ fontSize: '0.7rem' }} />
            <Chip size="small" color="info"    label={`${durDays}d duration`} sx={{ fontSize: '0.7rem' }} />
            <Chip size="small" color="default" label="Ground CASEVAC: PRIMARY" sx={{ fontSize: '0.7rem' }} />
          </Box>
        </Paper>

        <Divider sx={{ my: 2 }}><Chip label="Command Decision" size="small" /></Divider>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            {
              opt: 'A', color: 'warning',
              title: 'Ground CASEVAC only — full MEDEVAC grounding',
              body: `Ground all MEDEVAC. Activate full ground CASEVAC network (+2 ambulances). Accept extended evacuation time and increased DOW risk. (MEDEVAC −${degradePct.A}%; +2 ground vehicles)`,
              risk: 'T1 golden hour breach likely if FEBA distance > 15 km. DOW increases.',
              doc: 'AJMedP-2 Ch.6',
            },
            {
              opt: 'B', color: 'success',
              title: 'Ground CASEVAC + DNBI shelter activation',
              body: `Same as A plus activate pre-planned warm/cool shelter protocol to reduce DNBI surge. (MEDEVAC −${degradePct.B}%; +2 ground vehicles; DNBI mitigation)`,
              risk: 'Shelter activation requires pre-stocked logistics. If not prepared, DNBI impact unchanged.',
              doc: 'AJMedP-1 ss 2.4',
            },
            {
              opt: 'C', color: 'info',
              title: 'Selective MEDEVAC — critical only',
              body: `Permit MEDEVAC for T1-only with risk waiver, suspend routine T2 lift. (MEDEVAC −${degradePct.C}%)`,
              risk: 'Crew risk. Command responsibility for authorising flight in IMC conditions.',
              doc: 'AJMedP-2 MEDEVAC waiver',
            },
            {
              opt: 'D', color: 'secondary',
              title: 'Weather waiver — continue selective operations',
              body: `Request METOCC-approved weather waiver. Continue limited MEDEVAC at reduced rate. Accept risk. (MEDEVAC −${degradePct.D}%)`,
              risk: 'Aircraft and crew at significant risk. Waiver may be refused by aviation authority.',
              doc: 'ATP-2.4.1',
            },
          ].map(({ opt, color, title, body, risk, doc }) => (
            <Grid size={{ xs: 12, md: 6 }} key={opt}>
              <Paper variant="outlined" sx={{
                p: 2, height: '100%', cursor: 'pointer',
                borderColor: weatherInject.decision === opt ? `${color}.main` : 'divider',
                borderWidth: weatherInject.decision === opt ? 2 : 1,
                bgcolor: weatherInject.decision === opt ? `${color}.50` : 'inherit',
              }} onClick={() => handleWXDecision(opt)}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Chip label={`Option ${opt}`} size="small" color={color} />
                  <Typography variant="subtitle2" fontWeight="bold">{title}</Typography>
                </Box>
                <Typography variant="body2" sx={{ mb: 1 }}>{body}</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  <strong>Risk:</strong> {risk}
                </Typography>
                <Chip label={doc} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
              </Paper>
            </Grid>
          ))}
        </Grid>
        {renderDecisionMeta(weatherInject, setWeatherInject, TAB7_STORAGE_KEYS.WEATHER_INJECT)}
      </Box>
    );
  }

  // ============================================================================
  // RENDER: Supply Convoy Ambush Inject (TAB7-CV)
  // ============================================================================

  function renderConvoyInject() {
    const bloodLost  = Number(convoyInject.blood_units_lost) || 50;
    const dosLost    = Number(convoyInject.class_viii_dos_lost) || 3;
    const noBaseline = !baseline;
    const baseBloodStock = baseline?.snapshot?.medical_configuration?.blood_stock_units ?? null;

    const handleCVField = (field, value) => {
      const updated = { ...convoyInject, [field]: value };
      setConvoyInject(updated);
      lsWrite(TAB7_STORAGE_KEYS.CONVOY_INJECT, updated);
    };
    const handleCVDecision = (opt) => {
      const updated = { ...convoyInject, decision: opt, decided_at: new Date().toISOString() };
      setConvoyInject(updated);
      lsWrite(TAB7_STORAGE_KEYS.CONVOY_INJECT, updated);
    };

    return (
      <Box data-inject-phase="CONVOY">
        {noBaseline && (
          <Alert severity="warning" sx={{ mb: 2 }}>Load a Tab 5 baseline to enable inject calculation.</Alert>
        )}

        {/* Config */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>{t('tab7.inject_config')}</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField fullWidth size="small" label={t('tab7.inject_on_day')} type="number"
                value={convoyInject.day}
                onChange={e => handleCVField('day', Number(e.target.value))}
                inputProps={{ min: 1, max: opDays }}
                helperText={`D+1 to D+${opDays}`} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Blood units lost (PRBC): {bloodLost}
              </Typography>
              <Slider value={bloodLost} min={10} max={150} step={5}
                onChange={(_, v) => handleCVField('blood_units_lost', v)}
                marks={[{ value: 10, label: '10' }, { value: 150, label: '150' }]}
                size="small" />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Class VIII DOS lost: {dosLost}
              </Typography>
              <Slider value={dosLost} min={1} max={10} step={1}
                onChange={(_, v) => handleCVField('class_viii_dos_lost', v)}
                marks={[{ value: 1, label: '1d' }, { value: 10, label: '10d' }]}
                size="small" />
            </Grid>
          </Grid>
        </Paper>

        {/* Situation */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'error.main', borderWidth: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Situation — Day {dayLbl(convoyInject.day)}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            G4 reports: a 3-vehicle medical resupply convoy on MSR White was ambushed at grid
            {' '}[CLASSIFIED]. All vehicles disabled — crew casualties under treatment. Cargo destroyed:{' '}
            <strong>{bloodLost} units PRBC</strong> (blood temperature chain broken) and{' '}
            <strong>{dosLost} DOS Class VIII</strong> supplies. Blood DOS clock is now running.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip size="small" color="error"   label={`−${bloodLost} PRBC`} sx={{ fontSize: '0.7rem' }} />
            <Chip size="small" color="warning" label={`−${dosLost} DOS Class VIII`} sx={{ fontSize: '0.7rem' }} />
            {baseBloodStock && (
              <Chip size="small" color="default"
                label={`Remaining stock: ~${Math.max(0, baseBloodStock - bloodLost)} units`}
                sx={{ fontSize: '0.7rem' }} />
            )}
          </Box>
        </Paper>

        <Divider sx={{ my: 2 }}><Chip label="Command Decision" size="small" /></Divider>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            {
              opt: 'A', color: 'success',
              title: 'Emergency air resupply (24h)',
              body: `Submit URGENT blood request via STRATEVAC channel. Air resupply recovers approx. 70% of loss within 24h (+${Math.round(bloodLost * 0.7)} PRBC modelled).`,
              risk: 'Ties up STRATEVAC platform for 24h. Delivery not guaranteed if airspace denied.',
              doc: 'ATP-4.02 emergency resupply',
            },
            {
              opt: 'B', color: 'warning',
              title: 'Blood rationing protocol',
              body: 'Implement STANAG 2068 blood rationing. Defer elective surgery. Extend existing stock by reducing elective consumption. No immediate stock recovery — shown as consequence in debrief.',
              risk: 'Elective backlog accumulates. If combat tempo continues, blood will reach critical threshold.',
              doc: 'STANAG 2068 blood support',
            },
            {
              opt: 'C', color: 'info',
              title: 'Request HNS blood support',
              body: `Activate HNS blood support agreement (STANAG 2068). ~50% recovery in 12h (+${Math.round(bloodLost * 0.5)} PRBC modelled).`,
              risk: 'HNS stock may be incompatible blood type mix. Requires pre-arranged agreement.',
              doc: 'STANAG 2068',
            },
            {
              opt: 'D', color: 'error',
              title: 'Accept degraded stock — continue as planned',
              body: `Accept the full stock loss (−${bloodLost} PRBC). Continue operations within reduced capacity. No active recovery — full calculator impact applied.`,
              risk: 'Blood DOS likely to reach critical threshold before next scheduled resupply.',
              doc: 'ATP-4.02 degraded ops',
            },
          ].map(({ opt, color, title, body, risk, doc }) => (
            <Grid size={{ xs: 12, md: 6 }} key={opt}>
              <Paper variant="outlined" sx={{
                p: 2, height: '100%', cursor: 'pointer',
                borderColor: convoyInject.decision === opt ? `${color}.main` : 'divider',
                borderWidth: convoyInject.decision === opt ? 2 : 1,
                bgcolor: convoyInject.decision === opt ? `${color}.50` : 'inherit',
              }} onClick={() => handleCVDecision(opt)}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Chip label={`Option ${opt}`} size="small" color={color} />
                  <Typography variant="subtitle2" fontWeight="bold">{title}</Typography>
                </Box>
                <Typography variant="body2" sx={{ mb: 1 }}>{body}</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  <strong>Risk:</strong> {risk}
                </Typography>
                <Chip label={doc} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
              </Paper>
            </Grid>
          ))}
        </Grid>
        {renderDecisionMeta(convoyInject, setConvoyInject, TAB7_STORAGE_KEYS.CONVOY_INJECT)}
      </Box>
    );
  }

  // ============================================================================
  // RENDER: Planned Injection Types (V4 preview)
  // ============================================================================

  function renderInjectTypes() {
    // Remaining scaffold items — the ones now implemented (MASCAL, MTF, LOC, MEDEVAC, Enemy
    // Counterattack, Weather, Supply Convoy) have been removed from this list.
    // Civilian MASCAL (mass refugee scenario) is covered by the Civilian Encounter inject (Phase 5).
    const types = [
      { label: 'NBC / CBRN Contamination', desc: 'Site contamination — R2B access denied, decontamination queue.', teaches: 'Mass casualty + decon capacity; Route protection' },
      { label: 'Mass Burn Incident',        desc: 'Armour fire — surge of burn casualties exceeding R2B burns capacity.', teaches: 'Burn specialist constraint; transfer chain' },
    ];
    return (
      <Grid container spacing={2}>
        {types.map(({ label, desc, teaches }) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={label}>
            <Paper variant="outlined" sx={{ p: 1.5, height: '100%', opacity: 0.8 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>{label}</Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {desc}
              </Typography>
              <Chip
                label={teaches}
                size="small"
                variant="outlined"
                color="primary"
                sx={{ fontSize: '0.65rem', height: 'auto', py: 0.25,
                  '& .MuiChip-label': { whiteSpace: 'normal' } }}
              />
            </Paper>
          </Grid>
        ))}
      </Grid>
    );
  }

  // ============================================================================
  // RENDER: MRoE Dilemma Inject (V4-TAB7-002)
  // ============================================================================

  function renderMRoEInject() {
    // -- Data paths from baseline ------------------------------------------------
    const mascal     = baseline?.results?.mascal_analysis ?? {};
    const r2bBeds    = mascal.total_r2b_beds   ?? 0;
    const peakPct    = mascal.peak_r2b_pct     ?? 0;
    const mroeLoad   = mascal.mroe_load        ?? {};
    const strat      = baseline?.results?.stratevac_schedule_analysis ?? {};
    const stratWeekly  = strat.weekly_capacity ?? baseline?.results?.stratevac_weekly ?? 0;
    const stratDaily   = Math.round(stratWeekly / 7);

    // MRoE config from scenario
    const scenMroe   = scenario?.operation_parameters?.mroe_config ?? {};
    const mroeState  = scenMroe.state ?? null;
    const stateInfo  = mroeState ? (MROE_STATES[mroeState] ?? null) : null;

    // Computed from inject settings
    const epwCount         = Number(mroeInject.epw_count) || 0;
    const occupiedBeds     = r2bBeds > 0 ? Math.round((peakPct / 100) * r2bBeds) : 0;
    const freeBeds         = r2bBeds > 0 ? Math.max(0, r2bBeds - occupiedBeds) : 0;
    const epwFitsInFree    = freeBeds >= epwCount;
    const sortiesNeeded    = Math.ceil(epwCount / 6); // 6 PAX per C-130/helicopter load
    const stratFeasible    = stratDaily > 0 && stratDaily >= sortiesNeeded;

    // Option B risk level
    const optBRisk = epwCount < 10 ? 'LOW' : epwCount < 30 ? 'MEDIUM' : 'HIGH';
    const optBRiskColor = optBRisk === 'LOW' ? 'success' : optBRisk === 'MEDIUM' ? 'warning' : 'error';

    // Option A delay estimate: each EPW processed takes ~0.25h of R2B triage time
    const coalitionDelayHr = (epwCount * 0.25).toFixed(1);

    const noBaseline = !baseline;

    // -- Save inject decision ----------------------------------------------------
    const handleDecision = (option) => {
      const updated = {
        ...mroeInject,
        decision: option,
        decided_at: new Date().toISOString(),
      };
      setMroeInject(updated);
      lsWrite(TAB7_STORAGE_KEYS.MROE_INJECT, updated);
    };

    const handleReset = () => {
      const updated = { ...mroeInject, decision: null, decided_at: null };
      setMroeInject(updated);
      lsWrite(TAB7_STORAGE_KEYS.MROE_INJECT, updated);
    };

    const handleInjectField = (field, val) => {
      const updated = { ...mroeInject, [field]: val };
      setMroeInject(updated);
      // no lsWrite here — flushed on tab change
    };

    return (
      <Box>
        {/* ── Doctrine header ─────────────────────────────────────────── */}
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>{t('tab7.mroe_inject.gc3_art12')}</AlertTitle>
          Enemy wounded in action (EWIA) and prisoners of war (EPW) must be treated
          without any adverse distinction. This obligation cannot be set aside
          by operational conditions, resource pressure, or commander discretion.
          GC III Art. 19 authorises evacuation to the rear as a doctrinal alternative.
        </Alert>

        {noBaseline && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No Tab 6 baseline loaded. Consequence calculations will be estimated only.
            Load a Tab 5 baseline in Tab 6 to enable data-driven consequence analysis.
          </Alert>
        )}

        {/* ── EXCON configuration ─────────────────────────────────────── */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
            EXCON — Inject Parameters
          </Typography>
          {/* IMP-004: Preset quick-load */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mr: 0.5 }}>
              Preset:
            </Typography>
            {INJECT_PRESETS.MROE.map(p => (
              <Chip key={p.key} label={p.label} size="small" variant="outlined" color="secondary"
                onClick={() => setMroeInject(prev => ({ ...prev, ...p.fields }))}
                sx={{ fontSize: '0.7rem', height: 22, cursor: 'pointer' }} />
            ))}
          </Box>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                label={t('tab7.inject_on_day')}
                type="number"
                value={mroeInject.day}
                onChange={e => handleInjectField('day', Number(e.target.value))}
                inputProps={{ min: 1, max: opDays }}
                helperText={`Operation day 1–${opDays}`}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                label={t('tab7.epw_ewia_count')}
                type="number"
                value={mroeInject.epw_count}
                onChange={e => handleInjectField('epw_count', Number(e.target.value))}
                inputProps={{ min: 1, max: 500 }}
                helperText="Number of EPW/EWIA arriving at R2B"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Scenario MRoE state:
                </Typography>
                {stateInfo ? (
                  <Chip
                    label={`MRoE ${mroeState}`}
                    size="small"
                    color={stateInfo.color === 'success' ? 'success' : stateInfo.color === 'warning' ? 'warning' : 'error'}
                    sx={{ ml: 1 }}
                  />
                ) : (
                  <Chip label="NOT CONFIGURED" size="small" color="default" sx={{ ml: 1 }} />
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* ── Situation card ──────────────────────────────────────────── */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'warning.main', borderWidth: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Situation — Day {dayLbl(mroeInject.day)}
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: peakPct > 80 ? 'error.50' : 'grey.50', borderRadius: 1 }}>
                <Typography variant="h5" fontWeight="bold" color={peakPct > 80 ? 'error.main' : 'text.primary'}>
                  {r2bBeds > 0 ? `${peakPct.toFixed(0)}%` : 'N/A'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  R2B peak occupancy
                </Typography>
                {r2bBeds > 0 && (
                  <Typography variant="caption" display="block" color="text.secondary">
                    {occupiedBeds} / {r2bBeds} beds · {freeBeds} free
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'warning.50', borderRadius: 1 }}>
                <Typography variant="h5" fontWeight="bold" color="warning.dark">
                  {epwCount}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  EPW/EWIA demand
                </Typography>
                <Typography variant="caption" display="block" color={epwFitsInFree ? 'success.main' : 'error.main'}>
                  {r2bBeds > 0
                    ? epwFitsInFree
                      ? 'Fits in free beds'
                      : `Exceeds free beds by ${epwCount - freeBeds}`
                    : 'No baseline data'}
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'error.50', borderRadius: 1 }}>
                <Typography variant="h5" fontWeight="bold" color="error.main">
                  LOCKED
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  GC III Art. 12 status
                </Typography>
                <Typography variant="caption" display="block" color="error.main">
                  Cannot refuse / delay treatment
                </Typography>
              </Box>
            </Grid>
          </Grid>
          {mroeLoad.epw_beds_required > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Scenario EPW load (from Tab 2 MRoE config): {mroeLoad.epw_beds_required} beds planned
                {mroeLoad.epw_daily_patients > 0 && ` · ~${Math.round(mroeLoad.epw_daily_patients * 10) / 10}/day inflow`}
              </Typography>
            </Box>
          )}
        </Paper>

        <Divider sx={{ my: 2 }}>
          <Chip label={t('tab7.inject_decision')} size="small" />
        </Divider>

        {/* ── Decision options ────────────────────────────────────────── */}
        <Grid container spacing={2} sx={{ mb: 2 }}>

          {/* Option A: Full GC III compliance */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2, height: '100%',
                borderColor: mroeInject.decision === 'A' ? 'success.main' : 'divider',
                borderWidth: mroeInject.decision === 'A' ? 2 : 1,
                bgcolor: mroeInject.decision === 'A' ? 'success.50' : 'inherit',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CheckCircleIcon color="success" />
                <Typography variant="subtitle2" fontWeight="bold">
                  {t('tab7.mroe_inject.opt_a')}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Admit all {epwCount} EPW/EWIA into R2B treatment chain immediately.
                Treat without adverse distinction per GC III Art. 12.
              </Typography>
              <Box sx={{ bgcolor: 'grey.50', p: 1, borderRadius: 1, mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  <strong>Coalition impact:</strong> ~{coalitionDelayHr}h average intake delay for coalition WIA (triage queue).
                </Typography>
                {r2bBeds > 0 && !epwFitsInFree && (
                  <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                    Capacity shortfall: {epwCount - freeBeds} beds. MASCAL protocol activates.
                  </Typography>
                )}
                <Typography variant="caption" color="success.main" display="block" sx={{ mt: 0.5 }}>
                  Legal status: COMPLIANT. No LEGAD action required.
                </Typography>
              </Box>
              <Chip label="GC III Art. 12 — COMPLIANT" size="small" color="success" />
              <Box sx={{ mt: 1.5 }}>
                <Button
                  variant={mroeInject.decision === 'A' ? 'contained' : 'outlined'}
                  color="success"
                  size="small"
                  fullWidth
                  onClick={() => handleDecision('A')}
                  disabled={mroeInject.decision === 'A'}
                >
                  {mroeInject.decision === 'A' ? t('tab7.selected') : t('tab7.select_option', { opt: 'A' })}
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Option B: 2h intake hold */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2, height: '100%',
                borderColor: mroeInject.decision === 'B' ? 'warning.main' : 'divider',
                borderWidth: mroeInject.decision === 'B' ? 2 : 1,
                bgcolor: mroeInject.decision === 'B' ? 'warning.50' : 'inherit',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <WarningAmberIcon color="warning" />
                <Typography variant="subtitle2" fontWeight="bold">
                  {t('tab7.mroe_inject.opt_b')}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Hold EPW/EWIA in a holding area for 2 hours pending LEGAD clearance
                and resource assessment. Triage only; no definitive care.
              </Typography>
              <Box sx={{ bgcolor: 'grey.50', p: 1, borderRadius: 1, mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  <strong>Legal risk:</strong> If any EPW is LLE, a 2h delay may constitute violation of GC III Art. 12.
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  Risk level for {epwCount} EPW:{' '}
                  <Chip label={optBRisk} size="small" color={optBRiskColor} sx={{ fontSize: '0.6rem', height: 16 }} />
                </Typography>
                <Typography variant="caption" color="warning.dark" display="block" sx={{ mt: 0.5 }}>
                  Requires LEGAD sign-off within hold window.
                </Typography>
              </Box>
              <Chip label="GC III Art. 12 — LEGAL RISK" size="small" color="warning" />
              <Box sx={{ mt: 1.5 }}>
                <Tooltip title={t('tab7.mroe_inject.opt_b_confirm')}>
                  <span style={{ display: 'block' }}>
                    <Button
                      variant={mroeInject.decision === 'B' ? 'contained' : 'outlined'}
                      color="warning"
                      size="small"
                      fullWidth
                      onClick={() => handleDecision('B')}
                      disabled={mroeInject.decision === 'B'}
                    >
                      {mroeInject.decision === 'B' ? t('tab7.selected') : t('tab7.select_option', { opt: 'B' })}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Paper>
          </Grid>

          {/* Option C: STRATEVAC EPW */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2, height: '100%',
                borderColor: mroeInject.decision === 'C' ? 'info.main' : 'divider',
                borderWidth: mroeInject.decision === 'C' ? 2 : 1,
                bgcolor: mroeInject.decision === 'C' ? 'info.50' : 'inherit',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AirplanemodeActiveIcon color="info" />
                <Typography variant="subtitle2" fontWeight="bold">
                  {t('tab7.mroe_inject.opt_c')}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Evacuate EPW/EWIA to rear area detention facility per GC III Art. 19.
                Stabilise only at R2B; hand off to STRATEVAC chain.
              </Typography>
              <Box sx={{ bgcolor: 'grey.50', p: 1, borderRadius: 1, mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  <strong>Sorties required:</strong> {sortiesNeeded} lifts
                  ({epwCount} PAX @ 6 per sortie).
                </Typography>
                {stratDaily > 0 ? (
                  <Typography
                    variant="caption"
                    color={stratFeasible ? 'success.main' : 'error.main'}
                    display="block"
                    sx={{ mt: 0.5 }}
                  >
                    STRATEVAC capacity: ~{stratDaily} sorties/day —{' '}
                    {stratFeasible ? 'FEASIBLE' : 'INSUFFICIENT (coalition share at risk)'}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    STRATEVAC capacity: no baseline data.
                  </Typography>
                )}
                <Typography variant="caption" color="info.main" display="block" sx={{ mt: 0.5 }}>
                  Legal status: COMPLIANT per GC III Art. 19 (evacuation authorised).
                </Typography>
              </Box>
              <Chip label="GC III Art. 19 — COMPLIANT" size="small" color="info" />
              <Box sx={{ mt: 1.5 }}>
                <Button
                  variant={mroeInject.decision === 'C' ? 'contained' : 'outlined'}
                  color="info"
                  size="small"
                  fullWidth
                  onClick={() => handleDecision('C')}
                  disabled={mroeInject.decision === 'C'}
                >
                  {mroeInject.decision === 'C' ? t('tab7.selected') : t('tab7.select_option', { opt: 'C' })}
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* ── Consequence display (shown after decision) ───────────────── */}
        {mroeInject.decision && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'primary.main' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <GavelIcon color="primary" />
              <Typography variant="subtitle2" fontWeight="bold">
                {t('tab7.consequence_analysis', { opt: mroeInject.decision })}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Button size="small" variant="outlined" color="inherit" onClick={handleReset}>
                {t('tab7.reset_decision')}
              </Button>
            </Box>

            {mroeInject.decision === 'A' && (
              <Box>
                <Alert severity="success" sx={{ mb: 1.5 }}>
                  <strong>GC III Art. 12 obligation met.</strong> All EPW/EWIA receive treatment
                  without adverse distinction. No legal exposure.
                </Alert>
                <Typography variant="body2">
                  <strong>Coalition impact:</strong> Estimated {coalitionDelayHr}h average intake delay
                  for coalition WIA due to triage queue expansion.
                  {r2bBeds > 0 && !epwFitsInFree && (
                    <> MASCAL threshold exceeded — surge protocol activates.
                      Blue Light Protocol should be generated (see Phase 3).</>
                  )}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Recording:</strong> Log EPW admission in CASREP as INFO event.
                  MEDASSESSREP update required if capacity threshold crossed.
                </Typography>
              </Box>
            )}

            {mroeInject.decision === 'B' && (
              <Box>
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                  <strong>Legal risk accepted.</strong> 2h hold in force. LEGAD must be contacted
                  immediately. If any EPW deteriorates during hold, unit bears legal responsibility
                  under GC III Art. 12.
                </Alert>
                <Typography variant="body2">
                  <strong>Risk quantification:</strong> {epwCount} EPW held {epwCount > 0 && `for 2h`}.
                  Statistical probability of 1+ LLE case in {epwCount} EWIA: approximately{' '}
                  {epwCount < 10 ? '10–20%' : epwCount < 30 ? '30–50%' : '60–80%'}.
                  Risk level: <strong>{optBRisk}</strong>.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Required actions:</strong> LEGAD notification (immediate),
                  commander endorsement on hold order, triage nurse assigned to holding area,
                  2h countdown started. If LEGAD unavailable within 1h — revert to Option A.
                </Typography>
              </Box>
            )}

            {mroeInject.decision === 'C' && (
              <Box>
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  <strong>GC III Art. 19 evacuation authorised.</strong> STRATEVAC chain
                  activated for EPW. Stabilise at R2B; do not provide definitive care unless
                  LLE and evacuation cannot proceed within 6h.
                </Alert>
                <Typography variant="body2">
                  <strong>Logistics:</strong> {sortiesNeeded} STRATEVAC sorties requested.
                  {stratDaily > 0
                    ? stratFeasible
                      ? ` Daily capacity (${stratDaily} sorties/day) is sufficient.`
                      : ` Daily capacity (${stratDaily} sorties/day) insufficient — coalition lifts may be displaced.`
                    : ' Load Tab 6 baseline for capacity verification.'}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Recording:</strong> Log STRATEVAC EPW request in CASREP.
                  Update MEDASSESSREP with adjusted blood stocks (stabilisation only — no surgical load).
                </Typography>
              </Box>
            )}

            {mroeInject.decided_at && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                Decision recorded: {new Date(mroeInject.decided_at).toLocaleString()}
              </Typography>
            )}
            {/* F1: Structured decision fields */}
            {renderDecisionMeta(mroeInject, setMroeInject, TAB7_STORAGE_KEYS.MROE_INJECT)}
          </Paper>
        )}

        {/* ── Debrief card ────────────────────────────────────────────── */}
        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <SchoolIcon color="secondary" />
            <Typography variant="subtitle2" fontWeight="bold">{t('tab7.debrief_points')}</Typography>
          </Box>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>GC III Art. 12</strong> — EPW and EWIA shall be treated humanely and without
            any adverse distinction. The obligation is absolute and non-derogable. Resource pressure,
            tactical necessity, or MRoE state cannot override it.
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>GC III Art. 19</strong> — Evacuation to the rear is the doctrinal release valve.
            Option C is always available provided STRATEVAC capacity exists. When capacity is
            constrained, this creates a genuine command dilemma: keep EPW in R2B chain or compete
            with coalition STRATEVAC demand.
          </Typography>
          <Typography variant="body2">
            <strong>Planning implication</strong> — EPW volume must be planned separately from
            coalition patient flow. Tab 2 MRoE config drives the EPW capacity estimate;
            the inject tests whether that planning assumption holds under surge conditions.
          </Typography>
        </Paper>

        {/* ── Inject catalog footnote ─────────────────────────────────── */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" sx={{ mb: 1 }}>
            Planned for future release (scaffold — not yet functional):
          </Typography>
          {renderInjectTypes()}
        </Box>
      </Box>
    );
  }

  // ============================================================================
  // RENDER: Civilian Encounter Inject (V4-TAB7-003)
  // ============================================================================

  function renderCivilianInject() {
    // -- Data paths from baseline ------------------------------------------------
    const mascal   = baseline?.results?.mascal_analysis ?? {};
    const r2bBeds  = mascal.total_r2b_beds ?? 0;
    const peakPct  = mascal.peak_r2b_pct   ?? 0;

    // Scenario config
    const civDensity = scenario?.operation_parameters?.mroe_config?.civilian_density ?? 'NONE';
    const hnsAvail   = !!(scenario?.operation_parameters?.hns_config?.enabled);

    const noBaseline = !baseline;

    // -- Computed values --------------------------------------------------------
    const scenType = civInject.scenario_type || 'INDIVIDUAL';
    const civCount = Number(civInject.civ_count) || 200;
    const t1Count  = Math.round(civCount * 0.10);
    const t2Count  = Math.round(civCount * 0.25);
    const t3Count  = civCount - t1Count - t2Count;
    const civBlood   = t1Count * 6;  // 6 PRBC per T1 (V3.1-BLOOD-STREAM2 rate)
    const civFfp     = t1Count * 5;  // 5 FFP per T1
    const civSurgHrs = t1Count * 2;  // 2h per T1 surgical case

    const occupiedBeds = r2bBeds > 0 ? Math.round((peakPct / 100) * r2bBeds) : 0;
    const freeBeds     = r2bBeds > 0 ? Math.max(0, r2bBeds - occupiedBeds) : 0;
    const t1FitsInFree = freeBeds >= t1Count;

    // -- Handlers ---------------------------------------------------------------
    const handleCivDecision = (option) => {
      const updated = { ...civInject, decision: option, decided_at: new Date().toISOString() };
      setCivInject(updated);
      lsWrite(TAB7_STORAGE_KEYS.CIV_INJECT, updated);
    };

    const handleCivReset = () => {
      const updated = { ...civInject, decision: null, decided_at: null };
      setCivInject(updated);
      lsWrite(TAB7_STORAGE_KEYS.CIV_INJECT, updated);
    };

    const handleCivField = (field, val) => {
      const updated = { ...civInject, [field]: val };
      setCivInject(updated);
      // no lsWrite here — flushed on tab change
    };

    // -- INDIVIDUAL option consequence data ------------------------------------
    const indivOptions = {
      A: { blood: '3-6 PRBC', ffp: '2-5 FFP', surgHrs: '2-4 h', legal: 'COMPLIANT — GC IV Art. 16 met' },
      B: { blood: '1-2 PRBC', ffp: '1-2 FFP', surgHrs: 'varies', legal: 'LEGAL RISK — delay may violate GC IV if child deteriorates' },
      C: { blood: '0',        ffp: '0',        surgHrs: '0',      legal: 'GC IV Art. 16 VIOLATION — children with LLE injuries are non-optional' },
      D: {
        blood: '1-2 PRBC', ffp: '1 FFP', surgHrs: '0.5 h',
        legal: hnsAvail ? 'COMPLIANT — HNS confirmed in scenario config' : 'RISK — HNS not confirmed in scenario config',
      },
    };

    return (
      <Box>
        {/* -- Probability context ----------------------------------------- */}
        {civDensity === 'NONE' ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Low probability encounter — civilian density is set to NONE in Tab 2 scenario config.
            Inject remains available for doctrine teaching regardless.
          </Alert>
        ) : ['MODERATE', 'HIGH', 'URBAN_COMBAT'].includes(civDensity) ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            High probability encounter — civilian density is set to {civDensity} in Tab 2.
            This inject should be expected during exercise play.
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            Civilian density: {civDensity}. Encounter probability moderate.
          </Alert>
        )}

        {/* -- Doctrine header --------------------------------------------- */}
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>{t('tab7.civilian_inject.gc4_art16')}</AlertTitle>
          Wounded civilians, and especially children, must be given special respect and protection.
          Treatment of immediately life-threatening (T1-equivalent) injuries is non-optional.
          {scenType === 'INDIVIDUAL'
            ? ' Unlike EPW/GC III dilemmas, Option C (refuse treatment) is a GC IV Art. 16 doctrine violation — not a legitimate command choice. Option D (stabilise + HNS transfer) is the compliant alternative.'
            : ' In the mass scenario, Option D (refuse all — MTF at capacity) is a GC IV Art. 16 doctrine violation for T1 casualties. Option C (redirect to HNS) carries legal risk but may be compliant when HNS is confirmed.'}
          {' '}Capacity limitations do not override GC IV Art. 16 obligations for T1 casualties. Teaching moments are provided when a violation is selected.
        </Alert>

        {noBaseline && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No Tab 6 baseline loaded. Consequence calculations will be estimated only.
          </Alert>
        )}

        {/* -- Scenario type selector -------------------------------------- */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
            EXCON — Scenario Selection
          </Typography>
          {/* IMP-004: Preset quick-load */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mr: 0.5 }}>
              Preset:
            </Typography>
            {INJECT_PRESETS.CIV.map(p => (
              <Chip key={p.key} label={p.label} size="small" variant="outlined" color="secondary"
                onClick={() => setCivInject(prev => ({ ...prev, ...p.fields }))}
                sx={{ fontSize: '0.7rem', height: 22, cursor: 'pointer' }} />
            ))}
          </Box>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 5 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('tab7.scenario_type')}</InputLabel>
                <Select
                  value={scenType}
                  label={t('tab7.scenario_type')}
                  onChange={e => handleCivField('scenario_type', e.target.value)}
                >
                  <MenuItem value="INDIVIDUAL">Individual — Child at the Gate</MenuItem>
                  <MenuItem value="MASS">Mass — Refugee Column</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                label={t('tab7.inject_on_day')}
                type="number"
                value={civInject.day}
                onChange={e => handleCivField('day', Number(e.target.value))}
                inputProps={{ min: 1, max: opDays }}
                helperText={`Operation day 1-${opDays}`}
              />
            </Grid>
            {scenType === 'MASS' && (
              <Grid size={{ xs: 12, sm: 3 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  Civilian count: {civCount}
                </Typography>
                <Slider
                  value={civCount}
                  min={50}
                  max={500}
                  step={10}
                  onChange={(_, v) => handleCivField('civ_count', v)}
                  marks={[{ value: 50, label: '50' }, { value: 500, label: '500' }]}
                  size="small"
                />
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* -- Situation card ---------------------------------------------- */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'error.main', borderWidth: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Situation — Day {dayLbl(civInject.day)}
          </Typography>
          {scenType === 'INDIVIDUAL' ? (
            <Typography variant="body2">
              A civilian presents at the R2B gate with a child (age &lt;12) with a penetrating
              chest wound — T1-equivalent, immediately life-threatening. The MTF duty nurse requests
              command guidance on admission. Current R2B occupancy:{' '}
              {r2bBeds > 0
                ? `${peakPct.toFixed(0)}% (${occupiedBeds}/${r2bBeds} beds)`
                : 'unknown (no baseline)'}. HNS availability:{' '}
              {hnsAvail ? 'confirmed in scenario' : 'not confirmed in scenario'}.
            </Typography>
          ) : (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                A refugee column of approximately {civCount} civilians approaches R2E. Initial
                triage estimate: <strong>{t1Count} T1</strong> (10%),{' '}
                <strong>{t2Count} T2</strong> (25%), <strong>{t3Count} T3</strong> (65%).
                R2B current occupancy:{' '}
                {r2bBeds > 0 ? `${peakPct.toFixed(0)}% (${freeBeds} beds free)` : 'unknown'}.
              </Typography>
              <Grid container spacing={1}>
                <Grid size={{ xs: 4 }}>
                  <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'error.50', borderRadius: 1 }}>
                    <Typography variant="h6" fontWeight="bold" color="error.main">{t1Count}</Typography>
                    <Typography variant="caption" display="block">{t('tab7.civilian_inject.t1_immediate')}</Typography>
                    <Typography variant="caption" display="block" color="error.main">{t('tab7.civilian_inject.t1_note')}</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'warning.50', borderRadius: 1 }}>
                    <Typography variant="h6" fontWeight="bold" color="warning.dark">{t2Count}</Typography>
                    <Typography variant="caption" display="block">{t('tab7.civilian_inject.t2_delayed')}</Typography>
                    <Typography variant="caption" display="block" color="warning.dark">{t('tab7.civilian_inject.t2_note')}</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                    <Typography variant="h6" fontWeight="bold">{t3Count}</Typography>
                    <Typography variant="caption" display="block">{t('tab7.civilian_inject.t3_minor')}</Typography>
                    <Typography variant="caption" display="block" color="text.secondary">{t('tab7.civilian_inject.t3_note')}</Typography>
                  </Box>
                </Grid>
              </Grid>
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Blood demand if T1 treated: ~{civBlood} PRBC + ~{civFfp} FFP.
                  Surgical hours: ~{civSurgHrs}h.
                  {!t1FitsInFree && r2bBeds > 0 && (
                    <> T1 count ({t1Count}) exceeds free beds ({freeBeds}) — MASCAL surge protocol likely.</>
                  )}
                </Typography>
              </Box>
            </Box>
          )}
        </Paper>

        <Divider sx={{ my: 2 }}>
          <Chip label={t('tab7.inject_decision')} size="small" />
        </Divider>

        {/* -- Decision options: INDIVIDUAL scenario ----------------------- */}
        {scenType === 'INDIVIDUAL' ? (
          <Grid container spacing={2} sx={{ mb: 2 }}>

            {/* Option A — Treat Immediately */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2, height: '100%',
                  borderColor: civInject.decision === 'A' ? 'success.main' : 'divider',
                  borderWidth: civInject.decision === 'A' ? 2 : 1,
                  bgcolor: civInject.decision === 'A' ? 'success.50' : 'inherit',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CheckCircleIcon color="success" />
                  <Typography variant="subtitle2" fontWeight="bold">{t('tab7.civilian_inject.opt_a')}</Typography>
                </Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Admit the child as T1. Treat without adverse distinction.
                  No command consultation required — obligation is immediate.
                </Typography>
                <Box sx={{ bgcolor: 'grey.50', p: 1, borderRadius: 1, mb: 1.5 }}>
                  <Typography variant="caption" display="block">
                    <strong>Blood:</strong> {indivOptions.A.blood} + {indivOptions.A.ffp}
                  </Typography>
                  <Typography variant="caption" display="block">
                    <strong>Surgical time:</strong> {indivOptions.A.surgHrs}
                  </Typography>
                  <Typography variant="caption" color="success.main" display="block" sx={{ mt: 0.5 }}>
                    {indivOptions.A.legal}
                  </Typography>
                </Box>
                <Chip label="GC IV Art. 16 — COMPLIANT" size="small" color="success" />
                <Box sx={{ mt: 1.5 }}>
                  <Button
                    variant={civInject.decision === 'A' ? 'contained' : 'outlined'}
                    color="success"
                    size="small"
                    fullWidth
                    onClick={() => handleCivDecision('A')}
                    disabled={civInject.decision === 'A'}
                  >
                    {civInject.decision === 'A' ? t('tab7.selected') : t('tab7.select_option', { opt: 'A' })}
                  </Button>
                </Box>
              </Paper>
            </Grid>

            {/* Option B — Triage and Queue */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2, height: '100%',
                  borderColor: civInject.decision === 'B' ? 'warning.main' : 'divider',
                  borderWidth: civInject.decision === 'B' ? 2 : 1,
                  bgcolor: civInject.decision === 'B' ? 'warning.50' : 'inherit',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <WarningAmberIcon color="warning" />
                  <Typography variant="subtitle2" fontWeight="bold">{t('tab7.civilian_inject.opt_b')}</Typography>
                </Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Accept the child into triage queue behind military WIA with equivalent triage score.
                  No immediate prioritisation over coalition casualties.
                </Typography>
                <Box sx={{ bgcolor: 'grey.50', p: 1, borderRadius: 1, mb: 1.5 }}>
                  <Typography variant="caption" display="block">
                    <strong>Blood:</strong> {indivOptions.B.blood} + {indivOptions.B.ffp}
                  </Typography>
                  <Typography variant="caption" display="block">
                    <strong>Surgical time:</strong> {indivOptions.B.surgHrs}
                  </Typography>
                  <Typography variant="caption" color="warning.dark" display="block" sx={{ mt: 0.5 }}>
                    {indivOptions.B.legal}
                  </Typography>
                </Box>
                <Chip label="GC IV Art. 16 — LEGAL RISK" size="small" color="warning" />
                <Box sx={{ mt: 1.5 }}>
                  <Tooltip title={t('tab7.civilian_inject.opt_b_status')}>
                    <span style={{ display: 'block' }}>
                      <Button
                        variant={civInject.decision === 'B' ? 'contained' : 'outlined'}
                        color="warning"
                        size="small"
                        fullWidth
                        onClick={() => handleCivDecision('B')}
                        disabled={civInject.decision === 'B'}
                      >
                        {civInject.decision === 'B' ? t('tab7.selected') : t('tab7.select_option', { opt: 'B' })}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </Paper>
            </Grid>

            {/* Option C — Refuse (GC IV VIOLATION — teaching moment) */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2, height: '100%',
                  borderColor: civInject.decision === 'C' ? 'error.main' : 'error.light',
                  borderWidth: civInject.decision === 'C' ? 2 : 1,
                  bgcolor: civInject.decision === 'C' ? 'error.50' : 'inherit',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <ErrorIcon color="error" />
                  <Typography variant="subtitle2" fontWeight="bold">{t('tab7.civilian_inject.opt_c')}</Typography>
                  <Chip label={t('tab7.civilian_inject.opt_c_status')} size="small" color="error" sx={{ ml: 'auto' }} />
                </Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Refuse admission — civilian not covered by unit MRoE as interpreted by duty officer.
                  Child turned away at gate without treatment.
                </Typography>
                <Box sx={{ bgcolor: 'error.50', p: 1, borderRadius: 1, mb: 1.5, border: '1px solid', borderColor: 'error.light' }}>
                  <Typography variant="caption" display="block">
                    <strong>Blood consumed:</strong> {indivOptions.C.blood}
                  </Typography>
                  <Typography variant="caption" display="block">
                    <strong>Surgical time:</strong> {indivOptions.C.surgHrs}
                  </Typography>
                  <Typography variant="caption" color="error.main" display="block" sx={{ mt: 0.5 }}>
                    <strong>{indivOptions.C.legal}</strong>
                  </Typography>
                </Box>
                {civInject.decision === 'C' && (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    Teaching moment: GC IV Art. 16 explicitly requires special protection for
                    wounded children. This refusal constitutes a reportable violation.
                    LEGAD must be notified immediately.
                  </Alert>
                )}
                <Box sx={{ mt: 1.5 }}>
                  <Button
                    variant={civInject.decision === 'C' ? 'contained' : 'outlined'}
                    color="error"
                    size="small"
                    fullWidth
                    onClick={() => handleCivDecision('C')}
                    disabled={civInject.decision === 'C'}
                  >
                    {civInject.decision === 'C' ? t('tab7.selected_violation') : t('tab7.select_option', { opt: 'C' })}
                  </Button>
                </Box>
              </Paper>
            </Grid>

            {/* Option D — Stabilize + HNS Transfer */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2, height: '100%',
                  borderColor: civInject.decision === 'D'
                    ? (hnsAvail ? 'success.main' : 'warning.main')
                    : 'divider',
                  borderWidth: civInject.decision === 'D' ? 2 : 1,
                  bgcolor: civInject.decision === 'D'
                    ? (hnsAvail ? 'success.50' : 'warning.50')
                    : 'inherit',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CheckCircleIcon color={hnsAvail ? 'success' : 'warning'} />
                  <Typography variant="subtitle2" fontWeight="bold">{t('tab7.civilian_inject.opt_d')}</Typography>
                </Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Provide immediate stabilisation (haemorrhage control, airway management),
                  then transfer to Host Nation Support medical facility as soon as possible.
                </Typography>
                <Box sx={{ bgcolor: 'grey.50', p: 1, borderRadius: 1, mb: 1.5 }}>
                  <Typography variant="caption" display="block">
                    <strong>Blood:</strong> {indivOptions.D.blood} + {indivOptions.D.ffp}
                  </Typography>
                  <Typography variant="caption" display="block">
                    <strong>Surgical time:</strong> {indivOptions.D.surgHrs}
                  </Typography>
                  <Typography
                    variant="caption"
                    color={hnsAvail ? 'success.main' : 'warning.dark'}
                    display="block"
                    sx={{ mt: 0.5 }}
                  >
                    {indivOptions.D.legal}
                  </Typography>
                </Box>
                <Chip
                  label={hnsAvail
                    ? 'GC IV Art. 16 — COMPLIANT (HNS confirmed)'
                    : 'GC IV Art. 16 — RISK (HNS not confirmed)'}
                  size="small"
                  color={hnsAvail ? 'success' : 'warning'}
                />
                <Box sx={{ mt: 1.5 }}>
                  <Button
                    variant={civInject.decision === 'D' ? 'contained' : 'outlined'}
                    color={hnsAvail ? 'success' : 'warning'}
                    size="small"
                    fullWidth
                    onClick={() => handleCivDecision('D')}
                    disabled={civInject.decision === 'D'}
                  >
                    {civInject.decision === 'D' ? t('tab7.selected') : t('tab7.select_option', { opt: 'D' })}
                  </Button>
                </Box>
              </Paper>
            </Grid>

          </Grid>
        ) : (
          /* -- Decision options: MASS scenario --------------------------- */
          <Grid container spacing={2} sx={{ mb: 2 }}>

            {/* Option A — Full Triage + Treat T1 */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2, height: '100%',
                  borderColor: civInject.decision === 'A' ? 'success.main' : 'divider',
                  borderWidth: civInject.decision === 'A' ? 2 : 1,
                  bgcolor: civInject.decision === 'A' ? 'success.50' : 'inherit',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CheckCircleIcon color="success" />
                  <Typography variant="subtitle2" fontWeight="bold">
                    {t('tab7.civilian_inject.mass_opt_a')}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Screen all {civCount} civilians. Treat {t1Count} T1 immediately.
                  Redirect {t2Count} T2 and {t3Count} T3 to HNS facilities.
                </Typography>
                <Box sx={{ bgcolor: 'grey.50', p: 1, borderRadius: 1, mb: 1.5 }}>
                  <Typography variant="caption" display="block">
                    <strong>Blood demand:</strong> ~{civBlood} PRBC + ~{civFfp} FFP
                  </Typography>
                  <Typography variant="caption" display="block">
                    <strong>Surgical hours:</strong> ~{civSurgHrs}h
                  </Typography>
                  <Typography variant="caption" color="success.main" display="block" sx={{ mt: 0.5 }}>
                    COMPLIANT — T1 obligation met. T2/T3 conditional on HNS{' '}
                    {hnsAvail ? '(confirmed)' : '(unconfirmed in scenario)'}.
                  </Typography>
                </Box>
                <Chip label="GC IV Art. 16 — COMPLIANT" size="small" color="success" />
                <Box sx={{ mt: 1.5 }}>
                  <Button
                    variant={civInject.decision === 'A' ? 'contained' : 'outlined'}
                    color="success"
                    size="small"
                    fullWidth
                    onClick={() => handleCivDecision('A')}
                    disabled={civInject.decision === 'A'}
                  >
                    {civInject.decision === 'A' ? t('tab7.selected') : t('tab7.select_option', { opt: 'A' })}
                  </Button>
                </Box>
              </Paper>
            </Grid>

            {/* Option B — Stabilize All + STRATEVAC */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2, height: '100%',
                  borderColor: civInject.decision === 'B' ? 'info.main' : 'divider',
                  borderWidth: civInject.decision === 'B' ? 2 : 1,
                  bgcolor: civInject.decision === 'B' ? 'info.50' : 'inherit',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CheckCircleIcon color="info" />
                  <Typography variant="subtitle2" fontWeight="bold">
                    {t('tab7.civilian_inject.mass_opt_b')}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Stabilise all {civCount} civilians at R2E level, then evacuate all via STRATEVAC
                  to rear area civilian medical facilities.
                </Typography>
                <Box sx={{ bgcolor: 'grey.50', p: 1, borderRadius: 1, mb: 1.5 }}>
                  <Typography variant="caption" display="block">
                    <strong>Blood demand:</strong> stabilisation only — ~{Math.round(civBlood * 0.4)} PRBC
                  </Typography>
                  <Typography variant="caption" display="block">
                    <strong>STRATEVAC sorties:</strong> ~{Math.ceil(civCount / 6)} lifts required
                  </Typography>
                  <Typography variant="caption" color="info.main" display="block" sx={{ mt: 0.5 }}>
                    COMPLIANT if STRATEVAC capacity available. High logistics burden.
                  </Typography>
                </Box>
                <Chip label="GC IV Art. 16 — COMPLIANT if capacity" size="small" color="info" />
                <Box sx={{ mt: 1.5 }}>
                  <Button
                    variant={civInject.decision === 'B' ? 'contained' : 'outlined'}
                    color="info"
                    size="small"
                    fullWidth
                    onClick={() => handleCivDecision('B')}
                    disabled={civInject.decision === 'B'}
                  >
                    {civInject.decision === 'B' ? t('tab7.selected') : t('tab7.select_option', { opt: 'B' })}
                  </Button>
                </Box>
              </Paper>
            </Grid>

            {/* Option C — Redirect All to HNS */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2, height: '100%',
                  borderColor: civInject.decision === 'C'
                    ? (hnsAvail ? 'warning.main' : 'error.main')
                    : 'divider',
                  borderWidth: civInject.decision === 'C' ? 2 : 1,
                  bgcolor: civInject.decision === 'C'
                    ? (hnsAvail ? 'warning.50' : 'error.50')
                    : 'inherit',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <WarningAmberIcon color={hnsAvail ? 'warning' : 'error'} />
                  <Typography variant="subtitle2" fontWeight="bold">{t('tab7.civilian_inject.mass_opt_c')}</Typography>
                </Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Redirect entire column to Host Nation Support facilities without MTF engagement.
                  No resources committed at R2E.
                </Typography>
                <Box sx={{ bgcolor: 'grey.50', p: 1, borderRadius: 1, mb: 1.5 }}>
                  <Typography variant="caption" display="block">
                    <strong>Blood demand:</strong> 0 (no treatment at MTF)
                  </Typography>
                  <Typography
                    variant="caption"
                    color={hnsAvail ? 'warning.dark' : 'error.main'}
                    display="block"
                    sx={{ mt: 0.5 }}
                  >
                    {hnsAvail
                      ? 'RISK — HNS confirmed but T1 patients may not survive transfer time.'
                      : 'HIGH RISK — HNS not confirmed. T1 redirect without treatment = GC IV Art. 16 exposure.'}
                  </Typography>
                </Box>
                <Chip
                  label={hnsAvail
                    ? 'GC IV Art. 16 — RISK (transfer delay)'
                    : 'GC IV Art. 16 — HIGH RISK'}
                  size="small"
                  color={hnsAvail ? 'warning' : 'error'}
                />
                <Box sx={{ mt: 1.5 }}>
                  <Button
                    variant={civInject.decision === 'C' ? 'contained' : 'outlined'}
                    color={hnsAvail ? 'warning' : 'error'}
                    size="small"
                    fullWidth
                    onClick={() => handleCivDecision('C')}
                    disabled={civInject.decision === 'C'}
                  >
                    {civInject.decision === 'C' ? t('tab7.selected') : t('tab7.select_option', { opt: 'C' })}
                  </Button>
                </Box>
              </Paper>
            </Grid>

            {/* Option D — Refuse (GC IV VIOLATION for T1 — teaching moment) */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2, height: '100%',
                  borderColor: civInject.decision === 'D' ? 'error.main' : 'error.light',
                  borderWidth: civInject.decision === 'D' ? 2 : 1,
                  bgcolor: civInject.decision === 'D' ? 'error.50' : 'inherit',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <ErrorIcon color="error" />
                  <Typography variant="subtitle2" fontWeight="bold">
                    {t('tab7.civilian_inject.mass_opt_d')}
                  </Typography>
                  <Chip label="GC IV VIOLATION (T1)" size="small" color="error" sx={{ ml: 'auto' }} />
                </Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Refuse all civilians citing MTF at operational capacity. No triage, no treatment,
                  column redirected without assessment.
                </Typography>
                <Box sx={{ bgcolor: 'error.50', p: 1, borderRadius: 1, mb: 1.5, border: '1px solid', borderColor: 'error.light' }}>
                  <Typography variant="caption" display="block">
                    <strong>Blood demand:</strong> 0
                  </Typography>
                  <Typography variant="caption" color="error.main" display="block" sx={{ mt: 0.5 }}>
                    <strong>GC IV Art. 16 VIOLATION for {t1Count} T1 casualties.</strong> MTF capacity
                    is not a valid justification for refusing life-threatening civilian casualties.
                    LEGAD notification mandatory. OPORD authorisation required.
                  </Typography>
                </Box>
                {civInject.decision === 'D' && (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    Teaching moment: Capacity limitation does not override GC IV Art. 16 for T1
                    casualties. MASCAL triage protocol applies — T1 must be accepted. T2/T3 may
                    be redirected to HNS.
                  </Alert>
                )}
                <Box sx={{ mt: 1.5 }}>
                  <Button
                    variant={civInject.decision === 'D' ? 'contained' : 'outlined'}
                    color="error"
                    size="small"
                    fullWidth
                    onClick={() => handleCivDecision('D')}
                    disabled={civInject.decision === 'D'}
                  >
                    {civInject.decision === 'D' ? t('tab7.selected_violation') : t('tab7.select_option', { opt: 'D' })}
                  </Button>
                </Box>
              </Paper>
            </Grid>

          </Grid>
        )}

        {/* -- Consequence display (shown after decision) ------------------ */}
        {civInject.decision && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'primary.main' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <GavelIcon color="primary" />
              <Typography variant="subtitle2" fontWeight="bold">
                {t('tab7.consequence_analysis', { opt: civInject.decision })}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Button size="small" variant="outlined" color="inherit" onClick={handleCivReset}>
                {t('tab7.reset_decision')}
              </Button>
            </Box>

            {scenType === 'INDIVIDUAL' && civInject.decision === 'A' && (
              <Box>
                <Alert severity="success" sx={{ mb: 1.5 }}>
                  <strong>GC IV Art. 16 obligation met.</strong> Child treated as T1 without adverse
                  distinction. No legal exposure. Treatment recorded in MEDASSESSREP as Stream 2
                  civilian admission.
                </Alert>
                <Typography variant="body2">
                  <strong>Resource impact:</strong> 3-6 PRBC + 2-5 FFP. Surgical team 2-4h.
                  Minimal impact on coalition throughput if performed during off-peak window.
                </Typography>
              </Box>
            )}

            {scenType === 'INDIVIDUAL' && civInject.decision === 'B' && (
              <Box>
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                  <strong>Legal risk accepted.</strong> Child queued behind military WIA.
                  If condition deteriorates in queue, unit bears legal exposure under GC IV Art. 16.
                  LEGAD must be informed of queue position.
                </Alert>
                <Typography variant="body2">
                  <strong>Monitoring required:</strong> Triage nurse must reassess child every 15 min.
                  If deterioration detected, immediately upgrade to front of queue
                  (effectively reverts to Option A).
                </Typography>
              </Box>
            )}

            {scenType === 'INDIVIDUAL' && civInject.decision === 'C' && (
              <Box>
                <Alert severity="error" sx={{ mb: 1.5 }}>
                  <strong>GC IV Art. 16 VIOLATION.</strong> Refusal of treatment for a wounded child
                  with a life-threatening injury is not permitted under any operational justification.
                  This constitutes a reportable IHL violation. LEGAD notification required immediately.
                </Alert>
                <Typography variant="body2">
                  <strong>Required actions:</strong> Notify LEGAD. Commander endorsement of incident
                  report. Documentation in CASREP as CRITICAL. Potential ICRC notification if death occurs.
                </Typography>
              </Box>
            )}

            {scenType === 'INDIVIDUAL' && civInject.decision === 'D' && (
              <Box>
                <Alert severity={hnsAvail ? 'success' : 'warning'} sx={{ mb: 1.5 }}>
                  {hnsAvail ? (
                    <><strong>GC IV Art. 16 met via HNS transfer.</strong> Stabilisation provided.
                    HNS transfer initiated. Monitor transfer completion and outcome.</>
                  ) : (
                    <><strong>Risk — HNS availability unconfirmed.</strong> If HNS cannot accept within
                    transfer time, obligation reverts to treating unit. Verify HNS capacity before
                    initiating transfer.</>
                  )}
                </Alert>
                <Typography variant="body2">
                  <strong>Resource impact:</strong> 1-2 PRBC + 1 FFP (stabilisation). 0.5h surgical time.
                  Transfer documentation required — hand-off receipt from HNS facility.
                </Typography>
              </Box>
            )}

            {scenType === 'MASS' && civInject.decision === 'A' && (
              <Box>
                <Alert severity="success" sx={{ mb: 1.5 }}>
                  <strong>GC IV Art. 16 obligation met.</strong> All {t1Count} T1 civilians receive
                  treatment. T2/T3 redirected to HNS — compliant provided HNS is available.
                </Alert>
                <Typography variant="body2">
                  <strong>Resource impact:</strong> ~{civBlood} PRBC + ~{civFfp} FFP over ~{civSurgHrs}h.
                  {!t1FitsInFree && r2bBeds > 0 && (
                    <> T1 surge ({t1Count}) exceeds free beds ({freeBeds}) — MASCAL surge protocol activates.</>
                  )}
                  {' '}Log as Stream 2 civilian admission in MEDASSESSREP.
                </Typography>
              </Box>
            )}

            {scenType === 'MASS' && civInject.decision === 'B' && (
              <Box>
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  <strong>Stabilise and evacuate — compliant if capacity available.</strong> All{' '}
                  {civCount} civilians stabilised and handed to STRATEVAC chain. High logistics burden.
                </Alert>
                <Typography variant="body2">
                  <strong>STRATEVAC demand:</strong> ~{Math.ceil(civCount / 6)} sorties.
                  Blood: ~{Math.round(civBlood * 0.4)} PRBC (stabilisation only, no definitive surgery).
                  Verify STRATEVAC capacity against coalition lift plan before committing.
                </Typography>
              </Box>
            )}

            {scenType === 'MASS' && civInject.decision === 'C' && (
              <Box>
                <Alert severity={hnsAvail ? 'warning' : 'error'} sx={{ mb: 1.5 }}>
                  {hnsAvail ? (
                    <><strong>HNS redirect — compliant only if T1 patients survive transfer.</strong> Risk
                    of GC IV violation if T1 casualties deteriorate en route.</>
                  ) : (
                    <><strong>HIGH RISK.</strong> Redirecting {t1Count} T1 casualties without confirmed HNS
                    availability creates significant GC IV Art. 16 exposure.</>
                  )}
                </Alert>
                <Typography variant="body2">
                  <strong>Required actions:</strong> Confirm HNS facility capacity and transfer time before
                  redirecting T1 casualties. If HNS cannot guarantee T1 care within 1h — revert to Option A.
                </Typography>
              </Box>
            )}

            {scenType === 'MASS' && civInject.decision === 'D' && (
              <Box>
                <Alert severity="error" sx={{ mb: 1.5 }}>
                  <strong>GC IV Art. 16 VIOLATION — {t1Count} T1 casualties refused.</strong> MTF
                  capacity is not a valid justification under IHL. MASCAL triage applies: T1 must be
                  accepted, T2/T3 may be deferred. LEGAD notification mandatory.
                </Alert>
                <Typography variant="body2">
                  <strong>Correct action:</strong> Activate MASCAL surge protocol. Accept T1.
                  Redirect T2/T3 to HNS. Request additional STRATEVAC capacity via OPORD amendment.
                </Typography>
              </Box>
            )}

            {civInject.decided_at && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                Decision recorded: {new Date(civInject.decided_at).toLocaleString()}
              </Typography>
            )}
            {/* F1: Structured decision fields */}
            {renderDecisionMeta(civInject, setCivInject, TAB7_STORAGE_KEYS.CIV_INJECT)}
          </Paper>
        )}

        {/* -- Debrief card ------------------------------------------------ */}
        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <SchoolIcon color="secondary" />
            <Typography variant="subtitle2" fontWeight="bold">{t('tab7.debrief_points')}</Typography>
          </Box>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>GC IV Art. 16</strong> — Wounded civilians, especially children, must be given
            special respect and protection. For immediately life-threatening injuries, treatment is
            non-optional. Unlike the GC III EPW dilemma, there is no legitimate refuse option.
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>MASCAL triage principle</strong> — Capacity limitation activates MASCAL triage
            protocol, not exclusion of civilian casualties. T1 must be treated regardless of origin.
            T2/T3 may be redirected to HNS when capacity is genuinely exceeded.
          </Typography>
          <Typography variant="body2">
            <strong>Planning implication</strong> — Civilian casualty density (Tab 2 MRoE config)
            drives the probability of this encounter. High civilian density scenarios require explicit
            Stream 2 blood and surgical capacity planning. The inject tests whether that planning
            assumption holds under surge conditions.
          </Typography>
        </Paper>

      </Box>
    );
  }

  // ============================================================================
  // RENDER: MASCAL Event Inject (V4-TAB7-004)
  // ============================================================================

  function renderMASCALInject() {
    // -- Data paths from baseline ------------------------------------------------
    const mascal       = baseline?.results?.mascal_analysis ?? {};
    const r2bBeds      = mascal.total_r2b_beds ?? 0;
    const peakPct      = mascal.peak_r2b_pct   ?? 0;
    const maSys        = mascal.system_summary  ?? {};
    const firstCritDay = maSys.first_critical_day ?? null;
    const stratData    = baseline?.results?.stratevac_schedule_analysis ?? {};
    const stratWeekly  = stratData.weekly_capacity ?? baseline?.results?.stratevac_weekly ?? 0;
    const stratDaily   = Math.round(stratWeekly / 7);

    // Blood DOS from user-entered daily log for inject day
    const bloodDosLog = Number(dailyLog[mascalInject.day]?.dos) || 0;

    const noBaseline = !baseline;

    // -- Computed values --------------------------------------------------------
    const casualtyCount   = Number(mascalInject.casualty_count) || 0;
    const arrivalHrs      = Number(mascalInject.arrival_window_hours) || 2;
    const t1Count         = Math.round(casualtyCount * 0.25);
    const t2Count         = Math.round(casualtyCount * 0.45);
    const t3Count         = casualtyCount - t1Count - t2Count;
    const r2bPatients     = Math.round(casualtyCount * 0.65);
    const occupiedBeds    = r2bBeds > 0 ? Math.round((peakPct / 100) * r2bBeds) : 0;
    const surgeOccupied   = occupiedBeds + r2bPatients;
    const surgePct        = r2bBeds > 0 ? Math.round((surgeOccupied / r2bBeds) * 100) : null;
    const t1Blood         = t1Count * 6;   // 6 PRBC per T1 (V3.1-BLOOD-STREAM2 rate)
    const t2Blood         = t2Count * 2;   // 2 PRBC per T2
    const totalBlood      = t1Blood + t2Blood;
    const totalFfp        = Math.round(totalBlood * 0.8);
    const surgHrs         = t1Count * 2;   // 2h per T1 surgical case
    const stratSorties    = Math.ceil((t2Count + t3Count) / 6);
    const stratFeasible   = stratDaily > 0 && stratDaily >= stratSorties;

    // Surge status chip
    const surgeStatus = surgePct == null
      ? { label: 'No baseline', color: 'default' }
      : surgePct > 80
        ? { label: `MASCAL TRIGGERED (${surgePct}%)`, color: 'error' }
        : surgePct > 70
          ? { label: `BORDERLINE (${surgePct}%)`, color: 'warning' }
          : { label: `BELOW THRESHOLD (${surgePct}%)`, color: 'success' };

    // -- Handlers ---------------------------------------------------------------
    const handleMascalDecision = (option) => {
      const updated = { ...mascalInject, decision: option, decided_at: new Date().toISOString() };
      setMascalInject(updated);
      lsWrite(TAB7_STORAGE_KEYS.MASCAL_INJECT, updated);
    };

    const handleMascalReset = () => {
      const updated = { ...mascalInject, decision: null, decided_at: null };
      setMascalInject(updated);
      lsWrite(TAB7_STORAGE_KEYS.MASCAL_INJECT, updated);
    };

    const handleMascalField = (field, val) => {
      const updated = { ...mascalInject, [field]: val };
      setMascalInject(updated);
      // no lsWrite here — flushed on tab change
    };

    return (
      <Box>
        {/* -- Doctrine header ------------------------------------------- */}
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>{t('tab7.mascal_inject.ajmedp_ref')}</AlertTitle>
          MASCAL is declared when T1 arrivals exceed surgical capacity OR R2B occupancy exceeds 80%.
          Protocol activation is a doctrine trigger, not a command discretion — it is mandatory when
          criteria are met. Option A is the only fully compliant response. Options B-D illustrate
          consequences of delayed or incomplete protocol activation.
        </Alert>

        {noBaseline && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No Tab 6 baseline loaded. Surge occupancy and STRATEVAC capacity calculations will
            be unavailable. Load a Tab 5 baseline in Tab 6 to enable data-driven analysis.
          </Alert>
        )}

        {/* -- EXCON config ---------------------------------------------- */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
            EXCON — Inject Parameters
          </Typography>
          {/* IMP-004: Preset quick-load */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mr: 0.5 }}>
              Preset:
            </Typography>
            {INJECT_PRESETS.MASCAL.map(p => (
              <Chip key={p.key} label={p.label} size="small" variant="outlined" color="secondary"
                onClick={() => setMascalInject(prev => ({ ...prev, ...p.fields }))}
                sx={{ fontSize: '0.7rem', height: 22, cursor: 'pointer' }} />
            ))}
          </Box>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('tab7.event_type')}</InputLabel>
                <Select
                  value={mascalInject.event_type}
                  label={t('tab7.event_type')}
                  onChange={e => handleMascalField('event_type', e.target.value)}
                >
                  <MenuItem value="ARTILLERY">Artillery Strike</MenuItem>
                  <MenuItem value="IED">IED / Mine Strike</MenuItem>
                  <MenuItem value="AIRSTRIKE">Airstrike</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                fullWidth
                size="small"
                label={t('tab7.inject_on_day')}
                type="number"
                value={mascalInject.day}
                onChange={e => handleMascalField('day', Number(e.target.value))}
                inputProps={{ min: 1, max: opDays }}
                helperText={`Operation day 1-${opDays}`}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                fullWidth
                size="small"
                label={t('tab7.casualty_count')}
                type="number"
                value={mascalInject.casualty_count}
                onChange={e => handleMascalField('casualty_count', Number(e.target.value))}
                inputProps={{ min: 1, max: 500 }}
                helperText={planDaily?.wia ? `Planned WIA: ~${planDaily.wia}/day` : 'Casualties arriving at R2B'}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('tab7.arrival_window')}</InputLabel>
                <Select
                  value={mascalInject.arrival_window_hours}
                  label={t('tab7.arrival_window')}
                  onChange={e => handleMascalField('arrival_window_hours', Number(e.target.value))}
                >
                  <MenuItem value={1}>1 hour</MenuItem>
                  <MenuItem value={2}>2 hours</MenuItem>
                  <MenuItem value={4}>4 hours</MenuItem>
                  <MenuItem value={6}>6 hours</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* -- Situation card -------------------------------------------- */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'error.main', borderWidth: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Situation — Day {dayLbl(mascalInject.day)} — {mascalInject.event_type} event
          </Typography>
          <Grid container spacing={1} sx={{ mb: 1.5 }}>
            <Grid size={{ xs: 3 }}>
              <Box sx={{
                textAlign: 'center', p: 1, borderRadius: 1,
                bgcolor: surgePct != null && surgePct > 80 ? 'error.50' : 'warning.50'
              }}>
                <Typography variant="h6" fontWeight="bold"
                  color={surgePct != null && surgePct > 80 ? 'error.main' : 'warning.dark'}>
                  {surgePct != null ? `${surgePct}%` : 'N/A'}
                </Typography>
                <Typography variant="caption" display="block">{t('tab7.mascal_inject.r2b_projected')}</Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                  {r2bBeds > 0 ? `${surgeOccupied}/${r2bBeds} beds` : 'no baseline'}
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 3 }}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'error.50', borderRadius: 1 }}>
                <Typography variant="h6" fontWeight="bold" color="error.main">{t1Count}</Typography>
                <Typography variant="caption" display="block">{t('tab7.civilian_inject.t1_immediate')}</Typography>
                <Typography variant="caption" display="block" color="error.main">{t('tab7.mascal_inject.protocol_mandatory')}</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 3 }}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'warning.50', borderRadius: 1 }}>
                <Typography variant="h6" fontWeight="bold" color="warning.dark">{t2Count}</Typography>
                <Typography variant="caption" display="block">{t('tab7.civilian_inject.t2_delayed')}</Typography>
                <Typography variant="caption" display="block" color="warning.dark">{t('tab7.mascal_inject.time_sensitive')}</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 3 }}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="h6" fontWeight="bold">{t3Count}</Typography>
                <Typography variant="caption" display="block">{t('tab7.civilian_inject.t3_minor')}</Typography>
                <Typography variant="caption" display="block" color="text.secondary">{t('tab7.mascal_inject.walk_wait')}</Typography>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip label={surgeStatus.label} size="small" color={surgeStatus.color} />
            <Chip label={`~${totalBlood} PRBC demand`} size="small" color="warning" variant="outlined" />
            <Chip label={`~${surgHrs}h surgical`} size="small" color="default" variant="outlined" />
          </Box>
          {bloodDosLog > 0 && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Blood DOS logged {dayLbl(mascalInject.day)}: {bloodDosLog} days remaining.
              Surge consumes ~{(totalBlood / 30).toFixed(1)} additional days at 30 PRBC/day baseline rate.
            </Typography>
          )}
          {firstCritDay != null && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Planned first MASCAL-threshold day: {dayLbl(firstCritDay)} (from Tab 6 baseline).
            </Typography>
          )}
        </Paper>

        <Divider sx={{ my: 2 }}>
          <Chip label={t('tab7.inject_decision')} size="small" />
        </Divider>

        {/* -- Decision options ------------------------------------------ */}
        <Grid container spacing={2} sx={{ mb: 2 }}>

          {/* Option A — Activate MASCAL Protocol [CORRECT] */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2, height: '100%',
                borderColor: mascalInject.decision === 'A' ? 'success.main' : 'divider',
                borderWidth: mascalInject.decision === 'A' ? 2 : 1,
                bgcolor: mascalInject.decision === 'A' ? 'success.50' : 'inherit',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CheckCircleIcon color="success" />
                <Typography variant="subtitle2" fontWeight="bold">
                  {t('tab7.mascal_inject.opt_a')}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Declare MASCAL immediately. Implement AJMedP-1 ss 3.19 triage protocol.
                T1 surgical teams assigned exclusively. T2 deferred with timeline. T3 holding area.
                Alert blood chain and request mutual aid assessment.
              </Typography>
              <Box sx={{ bgcolor: 'grey.50', p: 1, borderRadius: 1, mb: 1.5 }}>
                <Typography variant="caption" display="block">
                  <strong>Blood demand:</strong> {totalBlood} PRBC + {totalFfp} FFP
                </Typography>
                <Typography variant="caption" display="block">
                  <strong>Surgical commitment:</strong> ~{surgHrs}h for {t1Count} T1 cases
                </Typography>
                <Typography variant="caption" color="success.main" display="block" sx={{ mt: 0.5 }}>
                  AJMedP-1 ss 3.19 — PROTOCOL MANDATORY when criteria met. Fully compliant.
                </Typography>
              </Box>
              <Chip label="AJMedP-1 ss 3.19 — COMPLIANT" size="small" color="success" />
              <Box sx={{ mt: 1.5 }}>
                <Button
                  variant={mascalInject.decision === 'A' ? 'contained' : 'outlined'}
                  color="success"
                  size="small"
                  fullWidth
                  onClick={() => handleMascalDecision('A')}
                  disabled={mascalInject.decision === 'A'}
                >
                  {mascalInject.decision === 'A' ? t('tab7.selected') : t('tab7.select_option', { opt: 'A' })}
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Option B — Absorb Without Declaring MASCAL [RISK] */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2, height: '100%',
                borderColor: mascalInject.decision === 'B' ? 'warning.main' : 'divider',
                borderWidth: mascalInject.decision === 'B' ? 2 : 1,
                bgcolor: mascalInject.decision === 'B' ? 'warning.50' : 'inherit',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <WarningAmberIcon color="warning" />
                <Typography variant="subtitle2" fontWeight="bold">
                  {t('tab7.mascal_inject.opt_b')}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Continue routine triage without protocol change. Surgical teams take all-comers
                in arrival order. No blood chain alert. No mutual aid request.
              </Typography>
              <Box sx={{ bgcolor: 'grey.50', p: 1, borderRadius: 1, mb: 1.5 }}>
                <Typography variant="caption" display="block">
                  <strong>Surgical queue saturation:</strong> ~{Math.round(arrivalHrs * 1.5)}h from first arrival
                </Typography>
                <Typography variant="caption" display="block">
                  <strong>T2 wait risk:</strong> {t2Count} T2 cases — LLE risk if wait exceeds 6h
                </Typography>
                <Typography variant="caption" color="warning.dark" display="block" sx={{ mt: 0.5 }}>
                  Blood DOS drains untracked — resupply trigger may not fire in time.
                </Typography>
              </Box>
              <Chip label="AJMedP-1 — NON-COMPLIANT if threshold exceeded" size="small" color="warning" />
              <Box sx={{ mt: 1.5 }}>
                <Tooltip title={`Risk: surgical team overwhelmed within ~${Math.round(arrivalHrs * 1.5)}h of first arrival`}>
                  <span style={{ display: 'block' }}>
                    <Button
                      variant={mascalInject.decision === 'B' ? 'contained' : 'outlined'}
                      color="warning"
                      size="small"
                      fullWidth
                      onClick={() => handleMascalDecision('B')}
                      disabled={mascalInject.decision === 'B'}
                    >
                      {mascalInject.decision === 'B' ? t('tab7.selected') : t('tab7.select_option', { opt: 'B' })}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Paper>
          </Grid>

          {/* Option C — MASCAL Protocol + STRATEVAC T2/T3 [CORRECT] */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2, height: '100%',
                borderColor: mascalInject.decision === 'C' ? 'info.main' : 'divider',
                borderWidth: mascalInject.decision === 'C' ? 2 : 1,
                bgcolor: mascalInject.decision === 'C' ? 'info.50' : 'inherit',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AirplanemodeActiveIcon color="info" />
                <Typography variant="subtitle2" fontWeight="bold">
                  {t('tab7.mascal_inject.opt_c')}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Declare MASCAL. Treat {t1Count} T1 at R2B. Activate STRATEVAC for {t2Count} T2
                and {t3Count} T3 to rear area. Clears R2B fastest — highest logistics demand.
              </Typography>
              <Box sx={{ bgcolor: 'grey.50', p: 1, borderRadius: 1, mb: 1.5 }}>
                <Typography variant="caption" display="block">
                  <strong>STRATEVAC sorties:</strong> ~{stratSorties} lifts ({t2Count + t3Count} PAX @ 6/sortie)
                </Typography>
                {stratDaily > 0 ? (
                  <Typography
                    variant="caption"
                    color={stratFeasible ? 'success.main' : 'error.main'}
                    display="block"
                    sx={{ mt: 0.5 }}
                  >
                    STRATEVAC capacity: ~{stratDaily} sorties/day —{' '}
                    {stratFeasible ? 'FEASIBLE' : 'INSUFFICIENT — coalition lifts at risk'}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    STRATEVAC capacity: no baseline data.
                  </Typography>
                )}
                <Typography variant="caption" color="info.main" display="block" sx={{ mt: 0.5 }}>
                  T1 blood demand unchanged at R2B: {t1Blood} PRBC + {Math.round(t1Blood * 0.8)} FFP.
                </Typography>
              </Box>
              <Chip label="AJMedP-1 ss 3.19 + STRATEVAC — COMPLIANT" size="small" color="info" />
              <Box sx={{ mt: 1.5 }}>
                <Button
                  variant={mascalInject.decision === 'C' ? 'contained' : 'outlined'}
                  color="info"
                  size="small"
                  fullWidth
                  onClick={() => handleMascalDecision('C')}
                  disabled={mascalInject.decision === 'C'}
                >
                  {mascalInject.decision === 'C' ? t('tab7.selected') : t('tab7.select_option', { opt: 'C' })}
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Option D — Adjacent MTF Mutual Support [CORRECT if available] */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2, height: '100%',
                borderColor: mascalInject.decision === 'D' ? 'success.main' : 'divider',
                borderWidth: mascalInject.decision === 'D' ? 2 : 1,
                bgcolor: mascalInject.decision === 'D' ? 'success.50' : 'inherit',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CheckCircleIcon color="success" />
                <Typography variant="subtitle2" fontWeight="bold">
                  {t('tab7.mascal_inject.opt_d')}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Declare MASCAL. Treat T1 at R2B. Transfer {t2Count} T2 and {t3Count} T3 to
                adjacent brigade R2B or division R2E via ground transport.
              </Typography>
              <Box sx={{ bgcolor: 'grey.50', p: 1, borderRadius: 1, mb: 1.5 }}>
                <Typography variant="caption" display="block">
                  <strong>T1 at own R2B:</strong> {t1Blood} PRBC + {Math.round(t1Blood * 0.8)} FFP
                </Typography>
                <Typography variant="caption" display="block">
                  <strong>Ground transfer:</strong> T2 acceptable, T3 acceptable.
                  T1 transfer only if EVT available and delay below 45 min.
                </Typography>
                <Typography variant="caption" color="success.main" display="block" sx={{ mt: 0.5 }}>
                  Compliant if adjacent MTF capacity confirmed. Verify before transferring T2.
                </Typography>
              </Box>
              <Chip label="AJMedP-1 ss 3.19 — COMPLIANT if capacity confirmed" size="small" color="success" />
              <Box sx={{ mt: 1.5 }}>
                <Button
                  variant={mascalInject.decision === 'D' ? 'contained' : 'outlined'}
                  color="success"
                  size="small"
                  fullWidth
                  onClick={() => handleMascalDecision('D')}
                  disabled={mascalInject.decision === 'D'}
                >
                  {mascalInject.decision === 'D' ? t('tab7.selected') : t('tab7.select_option', { opt: 'D' })}
                </Button>
              </Box>
            </Paper>
          </Grid>

        </Grid>

        {/* -- Consequence display (shown after decision) ---------------- */}
        {mascalInject.decision && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'primary.main' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <GavelIcon color="primary" />
              <Typography variant="subtitle2" fontWeight="bold">
                {t('tab7.consequence_analysis', { opt: mascalInject.decision })}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Button size="small" variant="outlined" color="inherit" onClick={handleMascalReset}>
                {t('tab7.reset_decision')}
              </Button>
            </Box>

            {mascalInject.decision === 'A' && (
              <Box>
                <Alert severity="success" sx={{ mb: 1.5 }}>
                  <strong>MASCAL declared. AJMedP-1 ss 3.19 protocol active.</strong> Triage teams
                  assigned. Blood chain notified. Mutual aid assessment initiated.
                </Alert>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Required actions (next 30 min):</strong>
                </Typography>
                <Typography variant="body2" component="div">
                  <Box component="ol" sx={{ pl: 2, m: 0 }}>
                    <Box component="li">Declare MASCAL — notify higher HQ, adjacent MTF, STRATEVAC chain.</Box>
                    <Box component="li">Triage sort: {t1Count} T1 (immediate) / {t2Count} T2 (delayed, timeline set) / {t3Count} T3 (holding area).</Box>
                    <Box component="li">Assign all surgical teams to T1 only.</Box>
                    <Box component="li">Request emergency blood resupply: {totalBlood} PRBC + {totalFfp} FFP.</Box>
                    <Box component="li">File CASREP CRITICAL for Day {mascalInject.day} event.</Box>
                    <Box component="li">Update MEDASSESSREP — escalate to Tab 6 if actual exceeds plan by 30%.</Box>
                  </Box>
                </Typography>
              </Box>
            )}

            {mascalInject.decision === 'B' && (
              <Box>
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                  <strong>No protocol declared. Routine triage continuing.</strong> Surgical queue
                  will saturate within ~{Math.round(arrivalHrs * 1.5)}h of first arrival.
                </Alert>
                <Typography variant="body2">
                  <strong>Risk quantification:</strong> {t2Count} T2 cases waiting with no dedicated
                  surgical slot. If any T2 waits over 6h, LLE threshold crossed — unit bears
                  preventable mortality exposure. Blood DOS depleting without resupply trigger.
                  When queue collapse becomes visible, reverting to Option A is harder under
                  active surge than declaring it immediately.
                </Typography>
              </Box>
            )}

            {mascalInject.decision === 'C' && (
              <Box>
                <Alert severity={stratFeasible || stratDaily === 0 ? 'success' : 'warning'} sx={{ mb: 1.5 }}>
                  {stratFeasible || stratDaily === 0 ? (
                    <><strong>MASCAL declared. STRATEVAC activated for T2/T3.</strong>{' '}
                      {stratDaily > 0
                        ? `Daily capacity (${stratDaily} sorties) is sufficient for ${stratSorties} required lifts.`
                        : 'Load Tab 6 baseline to verify STRATEVAC feasibility.'}</>
                  ) : (
                    <><strong>STRATEVAC capacity insufficient.</strong> {stratDaily} sorties/day available,
                      {stratSorties} needed. Coalition lift allocation at risk — coordinate with J4.</>
                  )}
                </Alert>
                <Typography variant="body2">
                  <strong>T1 management:</strong> {t1Count} T1 retained at R2B. Blood: {t1Blood} PRBC
                  + {Math.round(t1Blood * 0.8)} FFP. Surgical: ~{t1Count * 2}h.
                  {' '}<strong>STRATEVAC manifest:</strong> {t2Count + t3Count} PAX,
                  {' '}{stratSorties} sorties. Log in CASREP and update MEDASSESSREP.
                </Typography>
              </Box>
            )}

            {mascalInject.decision === 'D' && (
              <Box>
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  <strong>MASCAL declared. Mutual aid request sent to adjacent MTF.</strong>{' '}
                  Confirm capacity before transferring T2 patients. T1 remains at own R2B.
                </Alert>
                <Typography variant="body2">
                  <strong>Ground transport protocol:</strong> T2 (stable, delayed) — acceptable
                  for ground transfer if ETT available. T3 — acceptable. T1 — only if EVT
                  crew available and transit under 45 min. Document hand-off in CASREP.
                  If adjacent MTF cannot confirm capacity within 30 min — revert to Option C
                  (STRATEVAC) for T2/T3.
                </Typography>
              </Box>
            )}

            {mascalInject.decided_at && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                Decision recorded: {new Date(mascalInject.decided_at).toLocaleString()}
              </Typography>
            )}
            {/* F1: Structured decision fields */}
            {renderDecisionMeta(mascalInject, setMascalInject, TAB7_STORAGE_KEYS.MASCAL_INJECT)}
          </Paper>
        )}

        {/* -- Debrief card ---------------------------------------------- */}
        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <SchoolIcon color="secondary" />
            <Typography variant="subtitle2" fontWeight="bold">{t('tab7.debrief_points')}</Typography>
          </Box>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>AJMedP-1 ss 3.19 MASCAL criteria</strong> — declared when R2B occupancy
            exceeds 80% OR T1 arrival rate exceeds one surgical team capacity (~8-10 cases/day).
            Either criterion is sufficient. Both criteria may apply simultaneously in a surge.
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Blood demand spike is plannable.</strong> {t1Count} T1 x 6 PRBC = {t1Blood} PRBC
            in {arrivalHrs}h. This should be derived from the planned scenario (Tab 2 threat level
            + operation duration) and pre-positioned in blood stock before the exercise day.
          </Typography>
          <Typography variant="body2">
            <strong>MASCAL declaration is a cascade trigger.</strong> It activates simultaneously:
            blood chain alert, STRATEVAC pre-notification, adjacent MTF notification, higher HQ
            SITREP. All actions must occur within 30 min of declaration. Delay in any one element
            compounds the others. The inject tests whether the student initiates the full cascade —
            not just the triage sort.
          </Typography>
        </Paper>

      </Box>
    );
  }

  // ============================================================================
  // RENDER: MEDEVAC Asset Loss Inject (V4-TAB7-005)
  // ============================================================================

  function renderMEDEVACLossInject() {
    // -- Doctrine header -------------------------------------------------------
    const doctrineHeader = (
      <Alert severity="info" sx={{ mb: 2 }}>
        <AlertTitle>{t('tab7.medevac_inject.ajmedp_ref')}</AlertTitle>
        Loss of MEDEVAC platforms does not suspend the ss45 min surgical access requirement
        (R1 to R2B). It forces adaptation: sortie redistribution, ground MEDEVAC surge,
        mutual aid request, or CASEVAC substitution (T2/T3 only). All options except
        Option B maintain golden hour for T1 casualties.
      </Alert>
    );

    // -- No-baseline guard -----------------------------------------------------
    const noBaseline = !baseline?.results;

    // -- Baseline data reads ---------------------------------------------------
    const tecConfig        = scenario?.medical_configuration?.tactical_evac_config ?? {};
    const airPool          = tecConfig.air_pool ?? {};
    const nominalSorties   = Number(airPool.nominal_daily_sorties) || 0;
    const helicopterCount  = Number(airPool.helicopter_count ?? airPool.allocated_aircraft ?? 0);
    const goldenHourPct    = baseline?.results?.medical_system_analysis?.overall_golden_hour_percent ?? null;
    const baselineCorps    = baseline?.results?.by_corps ?? {};
    const planWiaTotal     = Object.values(baselineCorps).reduce((s, c) => s + (Number(c?.total_wia) || 0), 0);
    const planWia          = opDays > 0 ? planWiaTotal / opDays : 0;

    // -- Derived ---------------------------------------------------------------
    const platformsLost      = Number(medevacLossInject.platforms_lost) || 1;
    const remainingAircraft  = Math.max(0, helicopterCount - platformsLost);
    const sortiesPerAircraft = helicopterCount > 0 ? Math.round(nominalSorties / helicopterCount) : 0;
    const remainingSorties   = remainingAircraft * sortiesPerAircraft;
    const sortieDeficit      = nominalSorties - remainingSorties;
    const coverageDropPct    = nominalSorties > 0
      ? Math.round((sortieDeficit / nominalSorties) * 100) : null;
    const uncoveredPaxPerDay = sortieDeficit * 2;  // ~2 PAX per sortie planning figure
    const groundBreachRisk   = sortieDeficit > 0 && remainingSorties < nominalSorties * 0.5;
    const dowRisk            = planWia > 0 ? Math.round(planWia * 0.02) : null;

    // -- Helpers ---------------------------------------------------------------
    function handleMedevacLossField(field, val) {
      setMedevacLossInject(prev => {
        const next = { ...prev, [field]: val };
        lsWrite(TAB7_STORAGE_KEYS.MEDEVAC_LOSS_INJECT, next);
        return next;
      });
    }
    function handleMedevacLossDecision(opt) {
      setMedevacLossInject(prev => {
        const next = { ...prev, decision: opt, decided_at: new Date().toISOString() };
        lsWrite(TAB7_STORAGE_KEYS.MEDEVAC_LOSS_INJECT, next);
        return next;
      });
    }
    function handleMedevacLossReset() {
      setMedevacLossInject(prev => {
        const next = { ...prev, decision: null, decided_at: null };
        lsWrite(TAB7_STORAGE_KEYS.MEDEVAC_LOSS_INJECT, next);
        return next;
      });
    }

    const selected = medevacLossInject.decision;

    const optionStyle = (opt) => ({
      p: 2, cursor: 'pointer',
      borderWidth: 2, borderStyle: 'solid',
      borderColor: selected === opt
        ? (opt === 'B' ? 'warning.main' : 'success.main')
        : 'divider',
      bgcolor: selected === opt
        ? (opt === 'B' ? 'warning.50' : 'success.50')
        : 'background.paper',
      transition: 'border-color 0.15s, background-color 0.15s',
    });

    // -- Options ---------------------------------------------------------------
    const optA = (
      <Paper variant="outlined" sx={optionStyle('A')} onClick={() => handleMedevacLossDecision('A')}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CheckCircleIcon color="success" />
          <Typography fontWeight="bold">{t('tab7.medevac_inject.opt_a')}</Typography>
          <Chip label={t('tab7.medevac_inject.opt_a_status')} size="small" color="success" sx={{ ml: 'auto' }} />
        </Box>
        <Typography variant="body2" color="text.secondary">
          Remaining {remainingSorties} sorties reallocated to T1 priority only. Ground ambulances
          absorb T2/T3. Golden hour maintained for T1 if ground transport time stays below 45 min.
        </Typography>
      </Paper>
    );

    const optB = (
      <Tooltip title={`Risk: T1 patients with delayed R1 to R2B transit exceed the 45 min golden hour threshold`} placement="top">
        <Paper variant="outlined" sx={optionStyle('B')} onClick={() => handleMedevacLossDecision('B')}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <WarningAmberIcon color="warning" />
            <Typography fontWeight="bold">{t('tab7.medevac_inject.opt_b')}</Typography>
            <Chip label={t('tab7.medevac_inject.opt_b_status')} size="small" color="warning" sx={{ ml: 'auto' }} />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {sortieDeficit} sorties/day deficit. ~{uncoveredPaxPerDay} PAX/day uncovered.
            Golden hour compliance drops. DOW risk increases for delayed T1 casualties.
          </Typography>
        </Paper>
      </Tooltip>
    );

    const optC = (
      <Paper variant="outlined" sx={optionStyle('C')} onClick={() => handleMedevacLossDecision('C')}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CheckCircleIcon color="info" />
          <Typography fontWeight="bold">{t('tab7.medevac_inject.opt_c')}</Typography>
          <Chip label={t('tab7.medevac_inject.opt_c_status')} size="small" color="info" sx={{ ml: 'auto' }} />
        </Box>
        <Typography variant="body2" color="text.secondary">
          Submit MEDEVAC mutual aid request. Specify sortie requirement ({sortieDeficit}/day).
          Confirm adjacent unit capacity before relying on it.
          If not confirmed within 30 min, revert to Option A.
        </Typography>
      </Paper>
    );

    const optD = (
      <Paper variant="outlined" sx={optionStyle('D')} onClick={() => handleMedevacLossDecision('D')}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <AirplanemodeActiveIcon color="success" />
          <Typography fontWeight="bold">{t('tab7.medevac_inject.opt_d')}</Typography>
          <Chip label={t('tab7.medevac_inject.opt_d_status')} size="small" color="success" sx={{ ml: 'auto' }} />
        </Box>
        <Typography variant="body2" color="text.secondary">
          CASEVAC authorised for T2/T3 only. T1 still requires MEDEVAC if any air asset available.
          Commander authorisation required. LEGAD note on patient marking (no red cross on CASEVAC aircraft).
        </Typography>
      </Paper>
    );

    // -- Post-decision consequence ---------------------------------------------
    const consequence = selected && (() => {
      if (selected === 'A') return (
        <Box sx={{ mt: 2 }}>
          <Alert severity="success" sx={{ mb: 1.5 }}>
            <AlertTitle>
              <GavelIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
              Asset redistribution executed — golden hour maintained for T1.
            </AlertTitle>
          </Alert>
          <Typography variant="body2" gutterBottom>
            Action checklist:
          </Typography>
          <ol style={{ marginTop: 4, paddingLeft: 20 }}>
            <Typography component="li" variant="body2">Reallocate {remainingSorties} remaining sorties — T1 priority only; T2/T3 ground-only until further notice.</Typography>
            <Typography component="li" variant="body2">Task ground ambulances for T2/T3 surge. Confirm ground route time to R2B (must stay below 45 min).</Typography>
            <Typography component="li" variant="body2">File CASREP SIGNIFICANT: platform count reduced from {helicopterCount} to {remainingAircraft}, cause: {medevacLossInject.loss_cause}.</Typography>
            <Typography component="li" variant="body2">Request mutual aid as contingency regardless — submit even while executing Option A.</Typography>
            <Typography component="li" variant="body2">Update MEDASSESSREP: sortie capacity degraded, golden hour maintained for T1 only.</Typography>
          </ol>
        </Box>
      );
      if (selected === 'B') return (
        <Box sx={{ mt: 2 }}>
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            <AlertTitle>
              <GavelIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
              Risk accepted — no active mitigation. Golden hour compliance degraded.
            </AlertTitle>
          </Alert>
          <Typography variant="body2">
            Sortie deficit: {sortieDeficit}/day. Estimated {uncoveredPaxPerDay} PAX/day without air MEDEVAC.
            {dowRisk != null && (
              <> Estimated additional DOW: ~{dowRisk} per day (2% WIA conversion rate per delayed T1 hour).</>
            )}
            {' '}CASREP required when any preventable death is attributable to delayed evacuation.
            LEGAD: no doctrine violation per se, but preventable DOW is reportable and reviewable.
          </Typography>
        </Box>
      );
      if (selected === 'C') return (
        <Box sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 1.5 }}>
            <AlertTitle>
              <GavelIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
              Mutual aid requested — await confirmation before tasking.
            </AlertTitle>
          </Alert>
          <Typography variant="body2">
            Mutual aid request: specify {Math.ceil(sortieDeficit / Math.max(sortiesPerAircraft, 1))} aircraft
            for {sortieDeficit} sorties/day shortfall, duration TBD, grid [R2B location].
            Await confirmation — do not re-task inbound casualties to adjacent MTF until capacity confirmed.
            30-minute timeout: if no confirmation, execute Option A.
            Document in CASREP SIGNIFICANT.
          </Typography>
        </Box>
      );
      if (selected === 'D') return (
        <Box sx={{ mt: 2 }}>
          <Alert severity="success" sx={{ mb: 1.5 }}>
            <AlertTitle>
              <GavelIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
              CASEVAC authorised — T2/T3 only. T1 priority for remaining MEDEVAC sorties.
            </AlertTitle>
          </Alert>
          <Typography variant="body2">
            CASEVAC rules: non-medical aircraft, no medical crew, no red cross marking.
            Acceptable for T2/T3 if air MEDEVAC fully committed to T1.
            LEGAD note: CASEVAC patients retain full GC I protection if clearly marked as wounded.
            Document patient transfers in CASREP. File MEDASSESSREP noting CASEVAC in use.
          </Typography>
        </Box>
      );
      return null;
    })();

    // -- Debrief ---------------------------------------------------------------
    const debrief = (
      <Paper variant="outlined" sx={{ p: 2, mt: 2, borderColor: 'info.light' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <SchoolIcon color="info" />
          <Typography variant="subtitle2" fontWeight="bold">{t('tab7.medevac_inject.debrief_title')}</Typography>
        </Box>
        <Typography variant="body2" paragraph>
          <strong>AJMedP-2 threshold:</strong> ss45 min R1 to R2B surgical access. Asset loss does not suspend this
          obligation — it requires adaptation. The metric must still be tracked and reported even when degraded.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>Sortie deficit quantification:</strong> {platformsLost} aircraft lost
          x {sortiesPerAircraft} sorties/aircraft = {sortieDeficit} sorties/day
          = ~{uncoveredPaxPerDay} PAX/day at risk.{' '}
          This is the number that must appear in the CASREP and the mutual aid request.
        </Typography>
        <Typography variant="body2">
          <strong>CASEVAC is a bridge, not a replacement.</strong> T1 casualties should always receive
          MEDEVAC-standard care (medical crew, equipment) where any air asset is available.
          CASEVAC is authorised only when MEDEVAC assets are fully committed to higher-priority cases.
        </Typography>
      </Paper>
    );

    return (
      <Box>
        {doctrineHeader}

        {noBaseline && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No baseline loaded. Sortie deficit and golden hour impact cannot be computed.
            Load a baseline in Tab 6 to enable quantitative consequence analysis.
            EXCON config and decision options remain available.
          </Alert>
        )}

        {/* EXCON config */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>{t('tab7.excon_config')}</Typography>
          {/* IMP-004: Preset quick-load */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mr: 0.5 }}>
              Preset:
            </Typography>
            {INJECT_PRESETS.MEDEVAC.map(p => (
              <Chip key={p.key} label={p.label} size="small" variant="outlined" color="secondary"
                onClick={() => setMedevacLossInject(prev => ({ ...prev, ...p.fields }))}
                sx={{ fontSize: '0.7rem', height: 22, cursor: 'pointer' }} />
            ))}
          </Box>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('tab7.loss_cause')}</InputLabel>
                <Select
                  value={medevacLossInject.loss_cause}
                  label={t('tab7.loss_cause')}
                  onChange={e => handleMedevacLossField('loss_cause', e.target.value)}
                >
                  <MenuItem value="IED">IED strike</MenuItem>
                  <MenuItem value="AD_ENGAGEMENT">Air defence engagement</MenuItem>
                  <MenuItem value="MECHANICAL">Mechanical failure</MenuItem>
                  <MenuItem value="WEATHER_GROUNDING">Weather grounding</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('tab7.platforms_lost')}</InputLabel>
                <Select
                  value={medevacLossInject.platforms_lost}
                  label={t('tab7.platforms_lost')}
                  onChange={e => handleMedevacLossField('platforms_lost', Number(e.target.value))}
                >
                  <MenuItem value={1}>1 aircraft</MenuItem>
                  <MenuItem value={2}>2 aircraft</MenuItem>
                  <MenuItem value={3}>3 aircraft</MenuItem>
                </Select>
                {helicopterCount > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    Fleet: {helicopterCount} aircraft configured
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth size="small"
                label={t('tab7.inject_on_day')}
                type="number"
                value={medevacLossInject.day}
                onChange={e => handleMedevacLossField('day', Number(e.target.value))}
                inputProps={{ min: 1, max: opDays }}
                helperText={`Operation day 1-${opDays}`}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Situation card */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'warning.main', borderWidth: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Situation — Day {dayLbl(medevacLossInject.day)} — {medevacLossInject.loss_cause.replace(/_/g, ' ')} event
          </Typography>
          <Grid container spacing={1} sx={{ mb: 1.5 }}>
            <Grid size={{ xs: 4 }}>
              <Box sx={{ textAlign: 'center', p: 1, borderRadius: 1,
                bgcolor: remainingSorties === 0 ? 'error.50' : sortieDeficit > 0 ? 'warning.50' : 'success.50' }}>
                <Typography variant="h6" fontWeight="bold"
                  color={remainingSorties === 0 ? 'error.main' : sortieDeficit > 0 ? 'warning.dark' : 'success.main'}>
                  {nominalSorties > 0 ? `${remainingSorties}/${nominalSorties}` : 'N/A'}
                </Typography>
                <Typography variant="caption" display="block">{t('tab7.sorties_remaining')}</Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                  {nominalSorties > 0 ? `${sortieDeficit} deficit` : 'no baseline'}
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Box sx={{ textAlign: 'center', p: 1, borderRadius: 1,
                bgcolor: coverageDropPct != null && coverageDropPct > 50 ? 'error.50' : 'warning.50' }}>
                <Typography variant="h6" fontWeight="bold"
                  color={coverageDropPct != null && coverageDropPct > 50 ? 'error.main' : 'warning.dark'}>
                  {coverageDropPct != null ? `${coverageDropPct}%` : 'N/A'}
                </Typography>
                <Typography variant="caption" display="block">{t('tab7.coverage_drop')}</Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                  ~{uncoveredPaxPerDay} PAX/day at risk
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Box sx={{ textAlign: 'center', p: 1, borderRadius: 1,
                bgcolor: groundBreachRisk ? 'error.50' : 'warning.50' }}>
                <Typography variant="h6" fontWeight="bold"
                  color={groundBreachRisk ? 'error.main' : 'warning.dark'}>
                  {goldenHourPct != null ? `${goldenHourPct}%` : 'N/A'}
                </Typography>
                <Typography variant="caption" display="block">{t('tab7.baseline_golden')}</Typography>
                <Typography variant="caption" display="block"
                  color={groundBreachRisk ? 'error.main' : 'warning.dark'}>
                  {groundBreachRisk ? 'AT RISK' : 'DEGRADED'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {nominalSorties === 0 && (
              <Chip label="No MEDEVAC fleet configured in Tab 3" size="small" color="default" />
            )}
            {coverageDropPct != null && coverageDropPct >= 100 && (
              <Chip label="All aircraft lost — full ground MEDEVAC only" size="small" color="error" />
            )}
            {coverageDropPct != null && coverageDropPct > 50 && coverageDropPct < 100 && (
              <Chip label={`${coverageDropPct}% sortie capacity lost`} size="small" color="error" />
            )}
            {groundBreachRisk && (
              <Chip label="Golden hour at risk for T1 without intervention" size="small" color="warning" />
            )}
          </Box>
        </Paper>

        {/* 4 Options */}
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
          Command Decision — Day {dayLbl(medevacLossInject.day)}
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, sm: 6 }}>{optA}</Grid>
          <Grid size={{ xs: 12, sm: 6 }}>{optB}</Grid>
          <Grid size={{ xs: 12, sm: 6 }}>{optC}</Grid>
          <Grid size={{ xs: 12, sm: 6 }}>{optD}</Grid>
        </Grid>

        {/* Post-decision consequence */}
        {consequence}

        {/* F1: Structured decision fields */}
        {renderDecisionMeta(medevacLossInject, setMedevacLossInject, TAB7_STORAGE_KEYS.MEDEVAC_LOSS_INJECT)}

        {/* Debrief */}
        {debrief}

        {/* Reset */}
        {selected && (
          <Box sx={{ mt: 2 }}>
            <Button size="small" variant="outlined" color="secondary" onClick={handleMedevacLossReset}>
              {t('tab7.reset_decision')}
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  // ============================================================================
  // RENDER: MTF Displacement Inject (V4-TAB7-006)
  // ============================================================================

  function renderMTFDisplaceInject() {
    const doctrineHeader = (
      <Alert severity="warning" sx={{ mb: 2 }}>
        <AlertTitle>{t('tab7.mtf_inject.ajmedp_ref')}</AlertTitle>
        Displacing an MTF requires 24-hour pre-notification to higher HQ except under direct threat
        (GC I Art.19 emergency clause). The coverage gap window must be quantified, bridged by
        STRATEVAC and adjacent MTF coordination, and documented in a CASREP SIGNIFICANT.
        Option D (move without notification) is non-compliant.
      </Alert>
    );

    const noBaseline = !baseline?.results;

    // -- Baseline data reads ---------------------------------------------------
    const r2bBeds       = baseline?.results?.mascal_analysis?.total_r2b_beds ?? 0;
    const peakPct       = baseline?.results?.mascal_analysis?.peak_r2b_pct   ?? 0;
    const stratWeekly   = baseline?.results?.stratevac_schedule_analysis?.weekly_capacity
                            ?? baseline?.results?.stratevac_weekly ?? 0;
    const stratDaily    = Math.round(stratWeekly / 7);
    const occupiedBeds  = r2bBeds > 0 ? Math.round((peakPct / 100) * r2bBeds) : 0;
    const baselineCorps = baseline?.results?.by_corps ?? {};
    const planWiaTotal  = Object.values(baselineCorps).reduce((s, c) => s + (Number(c?.total_wia) || 0), 0);
    const planWia       = opDays > 0 ? planWiaTotal / opDays : 0;

    // -- Derived ---------------------------------------------------------------
    const gapHrs           = Number(mtfDisplaceInject.estimated_gap_hrs) || 4;
    const inboundDuringGap = Math.round((planWia / 24) * gapHrs);
    const patientsAtRisk   = occupiedBeds + inboundDuringGap;
    const bloodChainRisk   = gapHrs > 2;
    const surgicalGapRisk  = gapHrs > 4;
    const stratSorties     = occupiedBeds > 0 ? Math.ceil(occupiedBeds / 6) : 0;
    const stratFeasible    = stratDaily > 0 && stratSorties > 0 && stratDaily >= stratSorties;

    // -- Helpers ---------------------------------------------------------------
    function handleMtfField(field, val) {
      setMtfDisplaceInject(prev => {
        const next = { ...prev, [field]: val };
        lsWrite(TAB7_STORAGE_KEYS.MTF_DISPLACE_INJECT, next);
        return next;
      });
    }
    function handleMtfDecision(opt) {
      setMtfDisplaceInject(prev => {
        const next = { ...prev, decision: opt, decided_at: new Date().toISOString() };
        lsWrite(TAB7_STORAGE_KEYS.MTF_DISPLACE_INJECT, next);
        return next;
      });
    }
    function handleMtfReset() {
      setMtfDisplaceInject(prev => {
        const next = { ...prev, decision: null, decided_at: null };
        lsWrite(TAB7_STORAGE_KEYS.MTF_DISPLACE_INJECT, next);
        return next;
      });
    }

    const selected = mtfDisplaceInject.decision;

    const optionStyle = (opt) => {
      const isViolation = opt === 'D';
      const isRisk      = opt === 'C';
      const isSelected  = selected === opt;
      return {
        p: 2, cursor: 'pointer', borderWidth: 2, borderStyle: 'solid',
        borderColor: isSelected
          ? (isViolation ? 'error.main' : isRisk ? 'warning.main' : 'success.main')
          : 'divider',
        bgcolor: isSelected
          ? (isViolation ? 'error.50' : isRisk ? 'warning.50' : 'success.50')
          : 'background.paper',
        transition: 'border-color 0.15s, background-color 0.15s',
      };
    };

    // -- Options ---------------------------------------------------------------
    const optA = (
      <Paper variant="outlined" sx={optionStyle('A')} onClick={() => handleMtfDecision('A')}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CheckCircleIcon color="success" />
          <Typography fontWeight="bold">{t('tab7.mtf_inject.opt_a')}</Typography>
          <Chip label={t('tab7.mtf_inject.opt_a_status')} size="small" color="success" sx={{ ml: 'auto' }} />
        </Box>
        <Typography variant="body2" color="text.secondary">
          File CASREP SIGNIFICANT 24h prior. STRATEVAC inpatients ({occupiedBeds > 0 ? occupiedBeds : 'N/A'}).
          Advance party to new grid. Move MTF. Declare operational at new grid.
          Full pre-notification sequence — minimum coverage gap.
        </Typography>
      </Paper>
    );

    const optB = (
      <Paper variant="outlined" sx={optionStyle('B')} onClick={() => handleMtfDecision('B')}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CheckCircleIcon color="info" />
          <Typography fontWeight="bold">{t('tab7.mtf_inject.opt_b')}</Typography>
          <Chip label={t('tab7.mtf_inject.opt_b_status')} size="small" color="info" sx={{ ml: 'auto' }} />
        </Box>
        <Typography variant="body2" color="text.secondary">
          Coordinate diversion protocol with adjacent MTF. Confirm capacity before diverting.
          Casualty tracking documentation follows patients. CASREP gap duration to higher HQ.
        </Typography>
      </Paper>
    );

    const optC = (
      <Tooltip title={t('tab7.mtf_inject.opt_c_risk')} placement="top">
        <Paper variant="outlined" sx={optionStyle('C')} onClick={() => handleMtfDecision('C')}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <WarningAmberIcon color="warning" />
            <Typography fontWeight="bold">{t('tab7.mtf_inject.opt_c')}</Typography>
            <Chip label={t('tab7.mtf_inject.opt_c_status')} size="small" color="warning" sx={{ ml: 'auto' }} />
          </Box>
          <Typography variant="body2" color="text.secondary">
            Tactically sound only if threat timeline allows. Under direct threat, GC I Art.19 permits
            emergency move without prior clearance. Delay = higher risk to MTF staff and inpatients.
          </Typography>
        </Paper>
      </Tooltip>
    );

    const optD = (
      <Paper variant="outlined" sx={optionStyle('D')} onClick={() => handleMtfDecision('D')}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <ErrorIcon color="error" />
          <Typography fontWeight="bold">{t('tab7.mtf_inject.opt_d')}</Typography>
          <Chip label={t('tab7.mtf_inject.opt_d_status')} size="small" color="error" sx={{ ml: 'auto' }} />
        </Box>
        <Typography variant="body2" color="text.secondary">
          Higher HQ loses situational awareness. Inbound casualties routed to offline MTF.
          CASREP filed after-action. Preventable deaths if patients arrive at empty grid.
          Teaching moment: this option is selectable to illustrate the consequence.
        </Typography>
      </Paper>
    );

    // -- Post-decision consequences --------------------------------------------
    const consequence = selected && (() => {
      if (selected === 'A') return (
        <Box sx={{ mt: 2 }}>
          <Alert severity="success" sx={{ mb: 1.5 }}>
            <AlertTitle>
              <GavelIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
              Pre-notification sequence initiated. Staged displacement in progress.
            </AlertTitle>
          </Alert>
          <Typography variant="body2" gutterBottom>Pre-notification checklist:</Typography>
          <ol style={{ marginTop: 4, paddingLeft: 20 }}>
            <Typography component="li" variant="body2">
              File CASREP SIGNIFICANT: include new grid, gap duration estimate ({gapHrs}h), patient count ({occupiedBeds} inpatients).
            </Typography>
            <Typography component="li" variant="body2">
              STRATEVAC: {stratSorties > 0 ? `${stratSorties} sorties required for ${occupiedBeds} inpatients` : 'no inpatients to evacuate'}.
              {stratSorties > 0 && !stratFeasible && ' WARNING: exceeds daily baseline capacity — multi-day evacuation.'}
              {stratSorties > 0 && stratFeasible && ' Baseline capacity sufficient.'}
            </Typography>
            <Typography component="li" variant="body2">
              Cold chain: PLT transfer must complete within 4h. PRBC acceptable up to 42 days refrigerated.
            </Typography>
            <Typography component="li" variant="body2">
              Advance party departs first — establish comms and power at new grid before main body moves.
            </Typography>
            <Typography component="li" variant="body2">
              Declare MTF operational at new grid before releasing outgoing grid from medical system.
            </Typography>
          </ol>
        </Box>
      );
      if (selected === 'B') return (
        <Box sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 1.5 }}>
            <AlertTitle>
              <GavelIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
              Diversion coordinated with adjacent MTF. Notification sent to higher HQ.
            </AlertTitle>
          </Alert>
          <Typography variant="body2">
            Diversion notification required to: (a) Brigade medical officer; (b) adjacent MTF;
            (c) MEDEVAC dispatch. Patient tracking: all {occupiedBeds > 0 ? occupiedBeds : 'current'} inpatients
            and ~{inboundDuringGap} inbound during gap must retain documentation.
            Confirm adjacent MTF capacity before diverting —
            {occupiedBeds + inboundDuringGap > 0 ? ` total ${patientsAtRisk} patients to coordinate.` : ' total patient count unknown without baseline.'}
          </Typography>
        </Box>
      );
      if (selected === 'C') return (
        <Box sx={{ mt: 2 }}>
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            <AlertTitle>
              <GavelIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
              Delay accepted — threat timeline must be confirmed before execution.
            </AlertTitle>
          </Alert>
          <Typography variant="body2">
            Delay is only appropriate if threat timeline exceeds displacement time plus patient clearance time.
            Estimated gap: {gapHrs}h. If threat materialises during delay: execute Option A under GC I Art.19
            emergency clause (immediate move permitted, notify HQ as soon as possible after).
            {bloodChainRisk && ' Blood cold chain: PLT exposure clock is running — transfer within 4h of removal from refrigeration.'}
          </Typography>
        </Box>
      );
      if (selected === 'D') return (
        <Box sx={{ mt: 2 }}>
          <Alert severity="error" sx={{ mb: 1.5 }}>
            <AlertTitle>
              <GavelIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
              AJMedP-1 ss 4.8 non-compliant. Higher HQ situational awareness lost.
            </AlertTitle>
          </Alert>
          <Typography variant="body2">
            All inbound casualties during the {gapHrs}h gap period ({inboundDuringGap > 0 ? `~${inboundDuringGap} WIA` : 'unknown count'})
            arrive at an offline MTF. Corrective action: immediately notify new grid to MEDEVAC dispatch
            and brigade medical officer. CASREP after-action required. LEGAD review if preventable DOW is documented.
          </Typography>
        </Box>
      );
      return null;
    })();

    // -- Debrief ---------------------------------------------------------------
    const debrief = (
      <Paper variant="outlined" sx={{ p: 2, mt: 2, borderColor: 'info.light' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <SchoolIcon color="info" />
          <Typography variant="subtitle2" fontWeight="bold">{t('tab7.mtf_inject.debrief_title')}</Typography>
        </Box>
        <Typography variant="body2" paragraph>
          <strong>Pre-notification rule:</strong> AJMedP-1 ss 4.8 requires 24h prior notification to higher HQ.
          Exception: GC I Art.19 allows emergency movement under direct threat without prior clearance —
          but notification must follow as soon as tactically possible.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>Coverage gap quantification:</strong> {gapHrs}h gap
          x {Math.round(planWia / 24)} WIA/h estimated inbound = ~{inboundDuringGap} WIA without R2B access.
          Each T1 delayed beyond 45 min = golden hour breach and potential DOW.
        </Typography>
        <Typography variant="body2">
          <strong>Cold chain is the hidden constraint:</strong> PLT must be transfused or transferred
          within 4h of leaving refrigeration. A {gapHrs}h displacement
          {bloodChainRisk
            ? ' EXCEEDS the 4h PLT threshold — blood cold chain handoff must be planned before moving.'
            : ' is within the 4h PLT threshold if cold chain is maintained throughout transit.'
          }
        </Typography>
      </Paper>
    );

    return (
      <Box>
        {doctrineHeader}

        {noBaseline && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No baseline loaded. Patient count and STRATEVAC sortie demand cannot be computed.
            Load a baseline in Tab 6 to enable quantitative gap analysis.
          </Alert>
        )}

        {/* EXCON config */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>{t('tab7.excon_config')}</Typography>
          {/* IMP-004: Preset quick-load */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mr: 0.5 }}>
              Preset:
            </Typography>
            {INJECT_PRESETS.MTF.map(p => (
              <Chip key={p.key} label={p.label} size="small" variant="outlined" color="secondary"
                onClick={() => setMtfDisplaceInject(prev => ({ ...prev, ...p.fields }))}
                sx={{ fontSize: '0.7rem', height: 22, cursor: 'pointer' }} />
            ))}
          </Box>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('tab7.displacement_cause')}</InputLabel>
                <Select
                  value={mtfDisplaceInject.displacement_cause}
                  label={t('tab7.displacement_cause')}
                  onChange={e => handleMtfField('displacement_cause', e.target.value)}
                >
                  <MenuItem value="INDIRECT_FIRE">Indirect fire threat</MenuItem>
                  <MenuItem value="ENEMY_ADVANCE">Enemy advance</MenuItem>
                  <MenuItem value="OPERATIONAL">Operational redeployment</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('tab7.gap_estimate')}</InputLabel>
                <Select
                  value={mtfDisplaceInject.estimated_gap_hrs}
                  label={t('tab7.gap_estimate')}
                  onChange={e => handleMtfField('estimated_gap_hrs', Number(e.target.value))}
                >
                  {[1, 2, 4, 6, 8, 12].map(h => (
                    <MenuItem key={h} value={h}>{h}h coverage gap</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth size="small"
                label={t('tab7.inject_on_day')}
                type="number"
                value={mtfDisplaceInject.day}
                onChange={e => handleMtfField('day', Number(e.target.value))}
                inputProps={{ min: 1, max: opDays }}
                helperText={`Operation day 1-${opDays}`}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Situation card */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'error.main', borderWidth: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Situation — Day {dayLbl(mtfDisplaceInject.day)} — {mtfDisplaceInject.displacement_cause.replace(/_/g, ' ')} — {gapHrs}h gap
          </Typography>
          <Grid container spacing={1} sx={{ mb: 1.5 }}>
            <Grid size={{ xs: 3 }}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: occupiedBeds > 0 ? 'error.50' : 'grey.100', borderRadius: 1 }}>
                <Typography variant="h6" fontWeight="bold" color={occupiedBeds > 0 ? 'error.main' : 'text.secondary'}>
                  {r2bBeds > 0 ? occupiedBeds : 'N/A'}
                </Typography>
                <Typography variant="caption" display="block">{t('tab7.inpatients_risk')}</Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                  {r2bBeds > 0 ? `${Math.round(peakPct)}% occupancy` : 'no baseline'}
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 3 }}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'warning.50', borderRadius: 1 }}>
                <Typography variant="h6" fontWeight="bold" color="warning.dark">
                  ~{inboundDuringGap}
                </Typography>
                <Typography variant="caption" display="block">{t('tab7.inbound_gap')}</Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                  {gapHrs}h x {Math.round(planWia / 24)} WIA/h
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 3 }}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'error.50', borderRadius: 1 }}>
                <Typography variant="h6" fontWeight="bold" color="error.main">
                  {r2bBeds > 0 ? patientsAtRisk : 'N/A'}
                </Typography>
                <Typography variant="caption" display="block">{t('tab7.total_risk')}</Typography>
                <Typography variant="caption" display="block" color="text.secondary">in + inbound</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 3 }}>
              <Box sx={{ textAlign: 'center', p: 1,
                bgcolor: stratSorties > 0 && !stratFeasible ? 'warning.50' : 'info.50', borderRadius: 1 }}>
                <Typography variant="h6" fontWeight="bold"
                  color={stratSorties > 0 && !stratFeasible ? 'warning.dark' : 'info.main'}>
                  {stratSorties > 0 ? stratSorties : '—'}
                </Typography>
                <Typography variant="caption" display="block">{t('tab7.stratevac_sorties')}</Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                  {stratSorties > 0 ? (stratFeasible ? 'feasible' : 'exceeds capacity') : 'no inpatients'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {bloodChainRisk && (
              <Chip label="PLT cold chain at risk (gap > 2h)" size="small" color="warning" />
            )}
            {surgicalGapRisk && (
              <Chip label="Surgical team gap > 4h" size="small" color="error" />
            )}
            {r2bBeds === 0 && (
              <Chip label={t('tab7.no_baseline')} size="small" color="default" />
            )}
          </Box>
        </Paper>

        {/* 4 Options */}
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
          Command Decision — Day {dayLbl(mtfDisplaceInject.day)}
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, sm: 6 }}>{optA}</Grid>
          <Grid size={{ xs: 12, sm: 6 }}>{optB}</Grid>
          <Grid size={{ xs: 12, sm: 6 }}>{optC}</Grid>
          <Grid size={{ xs: 12, sm: 6 }}>{optD}</Grid>
        </Grid>

        {consequence}

        {/* F1: Structured decision fields */}
        {renderDecisionMeta(mtfDisplaceInject, setMtfDisplaceInject, TAB7_STORAGE_KEYS.MTF_DISPLACE_INJECT)}

        {debrief}

        {selected && (
          <Box sx={{ mt: 2 }}>
            <Button size="small" variant="outlined" color="secondary" onClick={handleMtfReset}>
              {t('tab7.reset_decision')}
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  // ============================================================================
  // RENDER: LOC Interdiction Inject (V4-TAB7-007)
  // ============================================================================

  function renderLOCInterdictInject() {
    const doctrineHeader = (
      <Alert severity="error" sx={{ mb: 2 }}>
        <AlertTitle>{t('tab7.loc_inject.atp_ref')}</AlertTitle>
        Medical supply continuity is a command responsibility. Route interdiction starts the blood
        DOS clock. The correct response is an immediate branch plan: air resupply (Option A),
        rationing (Option B), HNS acquisition (Option D), or a combination. Option C (absorb and
        accept) is non-compliant with ATP-4.02 when no mitigation is actioned.
      </Alert>
    );

    const noBaseline = !baseline?.results;

    // -- Baseline data reads ---------------------------------------------------
    // BUG-T63 fix: add more fallback paths for blood DOS from logistics projection.
    // baseline.results paths may not contain supply_adequacy — also check logistics projection.
    const _logProj         = lsRead('psp_logistics_projection');
    const bloodDosPlan     = baseline?.results?.supply_adequacy?.blood_dos
                               ?? baseline?.results?.totals?.blood_dos
                               ?? baseline?.results?.medical_requirements?.blood?.days_of_supply
                               ?? _logProj?.target_mdos_days
                               ?? 0;
    const bloodDosLog      = Number(dailyLog[locInterdictInject.day]?.dos) || 0;
    const bloodDos         = bloodDosLog > 0 ? bloodDosLog : bloodDosPlan;
    const dailyBloodPrbc   = baseline?.results?.totals?.blood_consumed_prbc_daily
                               ?? baseline?.results?.blood_requirements?.daily?.prbc
                               ?? baseline?.results?.medical_requirements?.blood?.daily_prbc
                               ?? 0;
    const stratWeekly      = baseline?.results?.stratevac_schedule_analysis?.weekly_capacity
                               ?? baseline?.results?.stratevac_weekly ?? 0;
    const stratDaily       = Math.round(stratWeekly / 7);

    // -- Derived ---------------------------------------------------------------
    const closedDays         = Number(locInterdictInject.route_closed_days) || 3;
    const daysToBloodCritical = bloodDos > 2 ? bloodDos - 2 : 0;
    const rationedDailyBlood = Math.round(dailyBloodPrbc * 0.70);
    const rationedDos        = rationedDailyBlood > 0
      ? Math.round(bloodDos * (dailyBloodPrbc / rationedDailyBlood))
      : bloodDos;
    // BUG-T63 fix: null = no data (no baseline); true/false only when bloodDos is known
    const rationingBridges   = noBaseline || bloodDos === 0 ? null : rationedDos >= closedDays;
    const bloodPerSortie     = 40;  // ~40 units PRBC+FFP per STRATEVAC cargo sortie (planning figure)
    const sortiesForResupply = dailyBloodPrbc > 0
      ? Math.ceil(dailyBloodPrbc * closedDays / bloodPerSortie)
      : null;
    const airResupplyFeasible = stratDaily > 0 && sortiesForResupply != null
      && sortiesForResupply <= stratDaily * closedDays;
    const dosCritical        = bloodDos > 0 && bloodDos <= closedDays;
    const dosBorderline      = bloodDos > closedDays && bloodDos <= closedDays + 2;

    // -- Helpers ---------------------------------------------------------------
    function handleLocField(field, val) {
      setLocInterdictInject(prev => {
        const next = { ...prev, [field]: val };
        lsWrite(TAB7_STORAGE_KEYS.LOC_INTERDICT_INJECT, next);
        return next;
      });
    }
    function handleLocDecision(opt) {
      setLocInterdictInject(prev => {
        const next = { ...prev, decision: opt, decided_at: new Date().toISOString() };
        lsWrite(TAB7_STORAGE_KEYS.LOC_INTERDICT_INJECT, next);
        return next;
      });
    }
    function handleLocReset() {
      setLocInterdictInject(prev => {
        const next = { ...prev, decision: null, decided_at: null };
        lsWrite(TAB7_STORAGE_KEYS.LOC_INTERDICT_INJECT, next);
        return next;
      });
    }

    const selected = locInterdictInject.decision;

    const optionStyle = (opt) => {
      const isRisk = opt === 'C';
      const isSelected = selected === opt;
      return {
        p: 2, cursor: 'pointer', borderWidth: 2, borderStyle: 'solid',
        borderColor: isSelected
          ? (isRisk ? 'error.main' : 'success.main')
          : 'divider',
        bgcolor: isSelected
          ? (isRisk ? 'error.50' : 'success.50')
          : 'background.paper',
        transition: 'border-color 0.15s, background-color 0.15s',
      };
    };

    // -- Options ---------------------------------------------------------------
    const optA = (
      <Paper variant="outlined" sx={optionStyle('A')} onClick={() => handleLocDecision('A')}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <AirplanemodeActiveIcon color={airResupplyFeasible ? 'success' : 'warning'} />
          <Typography fontWeight="bold">{t('tab7.loc_inject.opt_a')}</Typography>
          <Chip
            label={sortiesForResupply != null
              ? (airResupplyFeasible ? 'STRATEVAC CARGO — FEASIBLE' : 'STRATEVAC CARGO — SHORTFALL')
              : 'STRATEVAC — no baseline data'}
            size="small"
            color={airResupplyFeasible ? 'success' : 'warning'}
            sx={{ ml: 'auto' }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary">
          {sortiesForResupply != null
            ? `${sortiesForResupply} sorties × ${bloodPerSortie} units = ${sortiesForResupply * bloodPerSortie} units total.`
            : 'Sortie demand cannot be computed without baseline blood data.'
          }
          {' '}Bypasses the ground route entirely. File MEDLOG-SITREP to request cargo mission.
        </Typography>
      </Paper>
    );

    const optB = (
      <Paper variant="outlined" sx={optionStyle('B')} onClick={() => handleLocDecision('B')}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CheckCircleIcon color={rationingBridges === null ? 'disabled' : rationingBridges ? 'success' : 'warning'} />
          <Typography fontWeight="bold">{t('tab7.loc_inject.opt_b')}</Typography>
          <Chip
            label={rationingBridges === null ? 'LOAD BASELINE' : rationingBridges ? 'RATIONING SUFFICIENT' : 'RATIONING INSUFFICIENT'}
            size="small"
            color={rationingBridges === null ? 'default' : rationingBridges ? 'success' : 'warning'}
            sx={{ ml: 'auto' }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary">
          Defer elective T2/T3 transfusion. Maintain surgical blood for T1-LLE only.
          Reduces consumption by ~30%.{' '}
          {bloodDos > 0
            ? `DOS: ${bloodDos} days → ${rationedDos} days with rationing. Route closure: ${closedDays} days.`
            : 'Blood DOS unknown without baseline.'}
          {rationingBridges === false && bloodDos > 0 && ' Combine with Option A or D.'}
        </Typography>
      </Paper>
    );

    const optC = (
      <Tooltip title={`Risk: blood DOS will reach critical at D+${daysToBloodCritical || 'now'}. If route does not reopen in time, surgical capability suspends.`} placement="top">
        <Paper variant="outlined" sx={optionStyle('C')} onClick={() => handleLocDecision('C')}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <WarningAmberIcon color="error" />
            <Typography fontWeight="bold">{t('tab7.loc_inject.opt_c')}</Typography>
            <Chip label={t('tab7.loc_inject.opt_c_status')} size="small" color="error" sx={{ ml: 'auto' }} />
          </Box>
          <Typography variant="body2" color="text.secondary">
            No active mitigation. Blood DOS clock runs unchecked.
            {bloodDos > 0
              ? ` Critical threshold (DOS = 2) reached at day D+${daysToBloodCritical || 'now'}.`
              : ' Critical threshold unknown without baseline.'}
            {' '}Non-compliant with ATP-4.02 if no mitigation is actioned.
          </Typography>
        </Paper>
      </Tooltip>
    );

    const optD = (
      <Paper variant="outlined" sx={optionStyle('D')} onClick={() => handleLocDecision('D')}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CheckCircleIcon color="info" />
          <Typography fontWeight="bold">{t('tab7.loc_inject.opt_d')}</Typography>
          <Chip label={t('tab7.loc_inject.opt_d_status')} size="small" color="info" sx={{ ml: 'auto' }} />
        </Box>
        <Typography variant="body2" color="text.secondary">
          Request blood from HNS medical facility or adjacent NATO MTF.
          Confirm blood type compatibility and cold chain. STANAG 2068 cross-use authorisation required.
          If alternate ground route exists (even 2x time), prioritise over air cargo for PRBC.
        </Typography>
      </Paper>
    );

    // -- Consequences ----------------------------------------------------------
    const consequence = selected && (() => {
      if (selected === 'A') return (
        <Box sx={{ mt: 2 }}>
          <Alert severity={airResupplyFeasible ? 'success' : 'warning'} sx={{ mb: 1.5 }}>
            <AlertTitle>
              <GavelIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
              {airResupplyFeasible ? 'Air resupply feasible — MEDLOG-SITREP filed.' : 'Air resupply shortfall — combine with rationing (Option B).'}
            </AlertTitle>
          </Alert>
          <Typography variant="body2">
            {sortiesForResupply != null
              ? `Sortie demand: ${sortiesForResupply} sorties × ${bloodPerSortie} units = ${sortiesForResupply * bloodPerSortie} units. Baseline: ${stratDaily} sorties/day × ${closedDays} days = ${stratDaily * closedDays} available.`
              : 'Sortie demand unknown — configure blood consumption in Tab 4 and recalculate baseline.'
            }
            {' '}Cold chain: blood cargo requires 4 degrees C cooler manifest. Manifest filed with MEDLOG on departure.
          </Typography>
        </Box>
      );
      if (selected === 'B') return (
        <Box sx={{ mt: 2 }}>
          <Alert severity={rationingBridges === null ? 'info' : rationingBridges ? 'success' : 'warning'} sx={{ mb: 1.5 }}>
            <AlertTitle>
              <GavelIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
              {rationingBridges === null
                ? 'Load a Tab 5 baseline to assess rationing adequacy.'
                : rationingBridges
                  ? `Rationing bridges the gap. DOS extended to ${rationedDos} days.`
                  : `Rationing insufficient. DOS reaches 0 before route reopens. Escalate to Option A or D.`}
            </AlertTitle>
          </Alert>
          <Typography variant="body2">
            Protocol: defer all elective surgical transfusion (T2/T3 non-LLE). Maintain surgical blood
            for T1-LLE only. Audit blood stock daily. Document rationing activation in MEDLOG-SITREP.
            {bloodDos > 0 && ` DOS: ${bloodDos} days x 1.43 (30% reduction factor) = ${rationedDos} days vs ${closedDays} day closure.`}
          </Typography>
        </Box>
      );
      if (selected === 'C') return (
        <Box sx={{ mt: 2 }}>
          <Alert severity="error" sx={{ mb: 1.5 }}>
            <AlertTitle>
              <GavelIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
              ATP-4.02 non-compliant — medical supply continuity not maintained.
            </AlertTitle>
          </Alert>
          <Typography variant="body2">
            Risk accepted with no active mitigation.
            {bloodDos > 0
              ? ` Blood DOS critical (DOS = 2) reached at D+${daysToBloodCritical || 0}.`
              : ' Blood DOS unknown — cannot confirm when critical threshold is reached.'}
            {' '}When DOS reaches 2 days: CASREP CRITICAL must be filed immediately.
            If surgical cases cannot be operated due to blood depletion: preventable DOW is reportable.
            LEGAD review required.
          </Typography>
        </Box>
      );
      if (selected === 'D') return (
        <Box sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 1.5 }}>
            <AlertTitle>
              <GavelIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
              HNS acquisition protocol initiated.
            </AlertTitle>
          </Alert>
          <Typography variant="body2">
            Steps: (1) identify nearest HNS blood facility; (2) confirm available types (O-neg, A+);
            (3) cold chain — PLT max 4h ambient, PRBC max 42 days refrigerated;
            (4) complete STANAG 2068 cross-use authorisation;
            (5) document in MEDLOG-SITREP. Ground route: if any alternate route available (even 2x time),
            prioritise over air cargo for PRBC — simpler cold chain.
          </Typography>
        </Box>
      );
      return null;
    })();

    // -- Debrief ---------------------------------------------------------------
    const debrief = (
      <Paper variant="outlined" sx={{ p: 2, mt: 2, borderColor: 'info.light' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <SchoolIcon color="info" />
          <Typography variant="subtitle2" fontWeight="bold">{t('tab7.loc_inject.debrief_title')}</Typography>
        </Box>
        <Typography variant="body2" paragraph>
          <strong>ATP-4.02 principle:</strong> Medical supply continuity is a command responsibility.
          Route interdiction is a known risk that must appear in the medical CONOPS with a pre-planned
          branch: identified alternate routes, air resupply contingency, and HNS acquisition protocol.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>Blood DOS maths:</strong> DOS = stock / daily_consumption.
          Rationing reduces daily_consumption by 30% and extends DOS by 1 / 0.7 = 1.43x (43% longer).
          {bloodDos > 0 && dailyBloodPrbc > 0
            ? ` With ${bloodDos} days DOS and ${dailyBloodPrbc} PRBC/day: rationing extends to ${rationedDos} days.`
            : ' Load a baseline to compute exact values.'}
        </Typography>
        <Typography variant="body2">
          <strong>Cold chain constraint survives air resupply:</strong> blood cargo must travel
          donor to processing to packaging to flight to MTF cold storage without exceeding 4h ambient
          exposure for PLT. STRATEVAC blood cargo requires special packaging (4 degrees C cooler, manifest
          filed with MEDLOG on departure and confirmed on receipt).
        </Typography>
      </Paper>
    );

    return (
      <Box>
        {doctrineHeader}

        {noBaseline && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No baseline loaded. Blood DOS, resupply sortie demand, and rationing calculations are unavailable.
            Load a baseline in Tab 6 to enable quantitative analysis. Decision options remain available.
          </Alert>
        )}

        {/* EXCON config */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>{t('tab7.excon_config')}</Typography>
          {/* IMP-004: Preset quick-load */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mr: 0.5 }}>
              Preset:
            </Typography>
            {INJECT_PRESETS.LOC.map(p => (
              <Chip key={p.key} label={p.label} size="small" variant="outlined" color="secondary"
                onClick={() => setLocInterdictInject(prev => ({ ...prev, ...p.fields }))}
                sx={{ fontSize: '0.7rem', height: 22, cursor: 'pointer' }} />
            ))}
          </Box>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('tab7.interdiction_cause')}</InputLabel>
                <Select
                  value={locInterdictInject.interdiction_cause}
                  label={t('tab7.interdiction_cause')}
                  onChange={e => handleLocField('interdiction_cause', e.target.value)}
                >
                  <MenuItem value="BRIDGE_DESTRUCTION">Bridge destruction</MenuItem>
                  <MenuItem value="MINE_THREAT">Mine threat</MenuItem>
                  <MenuItem value="ENEMY_CONTROL">Enemy control of route</MenuItem>
                  <MenuItem value="WEATHER_FLOOD">Weather / Flood</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('tab7.route_closed')}</InputLabel>
                <Select
                  value={locInterdictInject.route_closed_days}
                  label={t('tab7.route_closed')}
                  onChange={e => handleLocField('route_closed_days', Number(e.target.value))}
                >
                  {[1, 2, 3, 5, 7].map(d => (
                    <MenuItem key={d} value={d}>{d} day{d > 1 ? 's' : ''}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth size="small"
                label={t('tab7.inject_on_day')}
                type="number"
                value={locInterdictInject.day}
                onChange={e => handleLocField('day', Number(e.target.value))}
                inputProps={{ min: 1, max: opDays }}
                helperText={`Operation day 1-${opDays}`}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Situation card */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2,
          borderColor: dosCritical ? 'error.main' : dosBorderline ? 'warning.main' : 'success.main',
          borderWidth: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Situation — Day {dayLbl(locInterdictInject.day)} — {locInterdictInject.interdiction_cause.replace(/_/g, ' ')} — {closedDays}-day closure
          </Typography>
          <Grid container spacing={1} sx={{ mb: 1.5 }}>
            <Grid size={{ xs: 3 }}>
              <Box sx={{ textAlign: 'center', p: 1, borderRadius: 1,
                bgcolor: dosCritical ? 'error.50' : dosBorderline ? 'warning.50' : 'success.50' }}>
                <Typography variant="h6" fontWeight="bold"
                  color={dosCritical ? 'error.main' : dosBorderline ? 'warning.dark' : 'success.main'}>
                  {bloodDos > 0 ? `${bloodDos}d` : 'N/A'}
                </Typography>
                <Typography variant="caption" display="block">{t('tab7.blood_dos')}</Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                  {bloodDosLog > 0 ? `${dayLbl(locInterdictInject.day)} log` : 'baseline plan'}
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 3 }}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'error.50', borderRadius: 1 }}>
                <Typography variant="h6" fontWeight="bold" color="error.main">
                  {closedDays}d
                </Typography>
                <Typography variant="caption" display="block">{t('tab7.route_closed')}</Typography>
                <Typography variant="caption" display="block" color="text.secondary">expected</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 3 }}>
              <Box sx={{ textAlign: 'center', p: 1,
                bgcolor: daysToBloodCritical <= 1 ? 'error.50' : daysToBloodCritical <= 3 ? 'warning.50' : 'success.50',
                borderRadius: 1 }}>
                <Typography variant="h6" fontWeight="bold"
                  color={daysToBloodCritical <= 1 ? 'error.main' : daysToBloodCritical <= 3 ? 'warning.dark' : 'success.main'}>
                  {bloodDos > 0 ? `D+${daysToBloodCritical}` : 'N/A'}
                </Typography>
                <Typography variant="caption" display="block">{t('tab7.dos_critical')}</Typography>
                <Typography variant="caption" display="block" color="text.secondary">when DOS = 2d</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 3 }}>
              <Box sx={{ textAlign: 'center', p: 1,
                bgcolor: rationingBridges === null ? 'grey.100' : rationingBridges ? 'success.50' : 'warning.50',
                borderRadius: 1 }}>
                <Typography variant="h6" fontWeight="bold"
                  color={rationingBridges === null ? 'text.secondary' : rationingBridges ? 'success.main' : 'warning.dark'}>
                  {bloodDos > 0 ? `${rationedDos}d` : 'N/A'}
                </Typography>
                <Typography variant="caption" display="block">{t('tab7.rationed_dos')}</Typography>
                <Typography variant="caption" display="block"
                  color={rationingBridges === null ? 'text.secondary' : rationingBridges ? 'success.main' : 'warning.dark'}>
                  {rationingBridges === null ? 'no data' : rationingBridges ? 'bridges gap' : 'insufficient'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {dosCritical && (
              <Chip label="BLOOD DOS WILL HIT ZERO before route reopens" size="small" color="error" />
            )}
            {dosBorderline && (
              <Chip label="BORDERLINE — rationing required to survive closure" size="small" color="warning" />
            )}
            {!dosCritical && !dosBorderline && bloodDos > 0 && (
              <Chip label="DOS SUFFICIENT for expected closure (without rationing)" size="small" color="success" />
            )}
            {bloodDos === 0 && (
              <Chip label="Blood DOS unknown — load baseline or enter D+N log" size="small" color="default" />
            )}
          </Box>
        </Paper>

        {/* 4 Options */}
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
          Command Decision — Day {dayLbl(locInterdictInject.day)}
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, sm: 6 }}>{optA}</Grid>
          <Grid size={{ xs: 12, sm: 6 }}>{optB}</Grid>
          <Grid size={{ xs: 12, sm: 6 }}>{optC}</Grid>
          <Grid size={{ xs: 12, sm: 6 }}>{optD}</Grid>
        </Grid>

        {consequence}

        {/* F1: Structured decision fields */}
        {renderDecisionMeta(locInterdictInject, setLocInterdictInject, TAB7_STORAGE_KEYS.LOC_INTERDICT_INJECT)}

        {debrief}

        {selected && (
          <Box sx={{ mt: 2 }}>
            <Button size="small" variant="outlined" color="secondary" onClick={handleLocReset}>
              {t('tab7.reset_decision')}
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  // ============================================================================
  // RENDER: R2E Loss Inject + Branch Plan Framework (TAB7-R2E)
  // ============================================================================

  function renderR2eLossInject() {
    const doctrineHeader = (
      <Alert severity="error" sx={{ mb: 2 }}>
        <AlertTitle>{t('tab7.r2e_inject.ajmedp_ref')}</AlertTitle>
        Loss of the Role 2 Enhanced surgical facility triggers an immediate AJMedP-1 cascade.
        R2B provides damage control (DC) procedures only -- not definitive surgery. All patients
        requiring definitive surgery must be STRATEVAC'd or rerouted to an adjacent R2E.
        Continuation of routine operations from R2B is non-compliant and will result in
        preventable deaths of patients who require surgery within the golden hour.
      </Alert>
    );

    const noBaseline = !baseline?.results;

    // -- Baseline data reads ---------------------------------------------------
    const r2eFacilities = scenario?.medical_configuration?.facilities ?? [];
    // BUG-S82-001 FIX: Tab3 saves facilities with f.type, not f.role. Same mismatch as
    // BUG-S81-003 (validationSystem.js). Check both fields for forward/backward compat.
    const _isR2EFac = (f) => f.type === 'ROLE2_ENHANCED' || f.role === 'ROLE2_ENHANCED';
    const r2eBeds = r2eFacilities
      .filter(_isR2EFac)
      .reduce((s, f) => s + (Number(f.beds) || 0), 0) || 40;
    const stratAvailable = baseline?.results?.stratevac_schedule_analysis?.available ?? false;
    const stratWeekly = baseline?.results?.stratevac_schedule_analysis?.weekly_capacity
      ?? baseline?.results?.stratevac_weekly ?? 0;
    const stratDaily = Math.round(stratWeekly / 7);

    const lossCauseLabel = {
      ENEMY_FIRE: 'Enemy direct/indirect fire',
      AIRSTRIKE:  'Hostile airstrike',
      ACCIDENT:   'Accidental destruction',
      FORCED_DISPLACEMENT: 'Forced displacement under threat',
    }[r2eLossInject.loss_cause] ?? r2eLossInject.loss_cause;

    const lossSeverityLabel = {
      DESTROYED:  'Destroyed -- total loss',
      DISABLED:   'Disabled -- partial capability',
      DISPLACED:  'Displaced -- reconstituting',
    }[r2eLossInject.loss_severity] ?? r2eLossInject.loss_severity;

    // -- Helpers ---------------------------------------------------------------
    function handleR2eField(field, val) {
      setR2eLossInject(prev => {
        const next = { ...prev, [field]: val };
        lsWrite(TAB7_STORAGE_KEYS.R2E_LOSS_INJECT, next);
        return next;
      });
    }
    function handleR2eDecision(key) {
      setR2eLossInject(prev => {
        const next = { ...prev, decision: key, decided_at: new Date().toISOString() };
        lsWrite(TAB7_STORAGE_KEYS.R2E_LOSS_INJECT, next);
        return next;
      });
    }
    function handleR2eCheckbox(i, checked) {
      setR2eLossInject(prev => {
        const cl = [...prev.checklist];
        cl[i] = checked;
        const next = { ...prev, checklist: cl };
        lsWrite(TAB7_STORAGE_KEYS.R2E_LOSS_INJECT, next);
        return next;
      });
    }
    function handleR2eReset() {
      setR2eLossInject(prev => {
        const next = { ...prev, decision: null, decided_at: null };
        lsWrite(TAB7_STORAGE_KEYS.R2E_LOSS_INJECT, next);
        return next;
      });
    }

    const selected = r2eLossInject.decision;

    const optionStyle = (opt) => {
      const isError   = opt === 'CONTINUE_R2B_ROUTINE';
      const isWarning = opt === 'HALT_OPERATIONS';
      const isInfo    = opt === 'ADJACENT_R2E';
      const isSelected = selected === opt;
      return {
        p: 2, cursor: 'pointer', borderWidth: 2, borderStyle: 'solid',
        borderColor: isSelected
          ? (isError ? 'error.main' : isWarning ? 'warning.main' : isInfo ? 'info.main' : 'success.main')
          : 'divider',
        bgcolor: isSelected
          ? (isError ? 'error.50' : isWarning ? 'warning.50' : isInfo ? 'info.50' : 'success.50')
          : 'background.paper',
        transition: 'border-color 0.15s, background-color 0.15s',
      };
    };

    // -- Options ---------------------------------------------------------------
    const optA = (
      <Paper variant="outlined" sx={optionStyle('R2B_SURGE_STRATEVAC')} onClick={() => handleR2eDecision('R2B_SURGE_STRATEVAC')}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CheckCircleIcon color="success" />
          <Typography fontWeight="bold">{t('tab7.r2e_inject.opt_a')}</Typography>
          <Chip label={t('tab7.r2e_inject.opt_a_status')} size="small" color="success" sx={{ ml: 'auto' }} />
        </Box>
        <Typography variant="body2" color="text.secondary">
          Surge R2B to maximum DC throughput. Request emergency STRATEVAC uplift for all surgical
          cases. Notify higher HQ immediately. Update CASREP with R2E status, reconstitution
          timeline, and daily STRATEVAC sortie demand.
          {stratDaily > 0 && ` Baseline STRATEVAC: ${stratDaily} patients/day.`}
        </Typography>
      </Paper>
    );

    const optB = (
      <Paper variant="outlined" sx={optionStyle('ADJACENT_R2E')} onClick={() => handleR2eDecision('ADJACENT_R2E')}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CheckCircleIcon color="info" />
          <Typography fontWeight="bold">{t('tab7.r2e_inject.opt_b')}</Typography>
          <Chip label={t('tab7.r2e_inject.opt_b_status')} size="small" color="info" sx={{ ml: 'auto' }} />
        </Box>
        <Typography variant="body2" color="text.secondary">
          Coordinate with adjacent formation R2E for overflow surgical capacity. Confirm bed
          availability and transit distance before diverting T1 patients. Patient tracking
          documentation must follow all transferred cases. Notify higher HQ of diversion protocol.
        </Typography>
      </Paper>
    );

    const optC = (
      <Paper variant="outlined" sx={optionStyle('CONTINUE_R2B_ROUTINE')} onClick={() => handleR2eDecision('CONTINUE_R2B_ROUTINE')}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <ErrorIcon color="error" />
          <Typography fontWeight="bold">{t('tab7.r2e_inject.opt_c')}</Typography>
          <Chip label={t('tab7.r2e_inject.opt_c_status')} size="small" color="error" sx={{ ml: 'auto' }} />
        </Box>
        <Typography variant="body2" color="text.secondary">
          R2B scope: triage, resuscitation, DC procedures, holding for STRATEVAC. Not definitive surgery.
        </Typography>
        <Typography variant="body2" color="error.main" sx={{ mt: 0.5, fontWeight: 'bold' }}>
          NON-COMPLIANT: R2B provides DC only. Definitive surgery is unavailable. Patients will die.
        </Typography>
      </Paper>
    );

    const optD = (
      <Paper variant="outlined" sx={optionStyle('HALT_OPERATIONS')} onClick={() => handleR2eDecision('HALT_OPERATIONS')}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <WarningAmberIcon color="warning" />
          <Typography fontWeight="bold">{t('tab7.r2e_inject.opt_d')}</Typography>
          <Chip label={t('tab7.r2e_inject.opt_d_status')} size="small" color="warning" sx={{ ml: 'auto' }} />
        </Box>
        <Typography variant="body2" color="text.secondary">
          Operationally untenable if casualties are ongoing. Acceptable only if no combat operations
          are planned and reconstitution timeline is confirmed short. Requires higher HQ approval
          and must be paired with a STRATEVAC bridge for any current T1 patients.
        </Typography>
      </Paper>
    );

    // -- Consequence -----------------------------------------------------------
    const consequence = selected && (() => {
      if (selected === 'R2B_SURGE_STRATEVAC') return (
        <Alert severity="success" sx={{ mt: 2 }}>
          <AlertTitle>Branch plan activated. STRATEVAC uplift requested. Higher HQ notified.</AlertTitle>
          R2B is at maximum DC throughput. All surgical cases are being routed to STRATEVAC.
          Monitor sortie demand vs. daily capacity ({stratDaily > 0 ? `${stratDaily} pts/day baseline` : 'unknown -- no baseline loaded'}).
          File CASREP SIGNIFICANT with R2E loss, scope of damage, and reconstitution timeline.
        </Alert>
      );
      if (selected === 'ADJACENT_R2E') return (
        <Alert severity="info" sx={{ mt: 2 }}>
          <AlertTitle>Adjacent R2E coordination in progress. Diversion protocol active.</AlertTitle>
          Confirm adjacent R2E capacity before diverting T1 patients. Patient tracking documentation
          must follow all transferred cases. Notify MEDEVAC dispatch of new receiving MTF grid.
          File CASREP with diversion status and expected return to organic R2E.
        </Alert>
      );
      if (selected === 'CONTINUE_R2B_ROUTINE') return (
        <Alert severity="error" sx={{ mt: 2 }}>
          <AlertTitle>NON-COMPLIANT -- AJMedP-1 cascade violated. Preventable deaths expected.</AlertTitle>
          R2B is not a surgical facility. Patients requiring definitive surgery will die within the
          golden hour. Corrective action: immediately activate Option A. LEGAD review required for
          all DOW documentation from this period.
        </Alert>
      );
      if (selected === 'HALT_OPERATIONS') return (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <AlertTitle>Operational halt accepted -- STRATEVAC bridge required for current T1 patients.</AlertTitle>
          Ensure all current T1 patients are covered by STRATEVAC before operations suspend.
          Higher HQ approval required. File CASREP with halt rationale, patient status, and
          reconstitution timeline.
        </Alert>
      );
      return null;
    })();

    // -- Debrief ---------------------------------------------------------------
    const debrief = selected && (
      <Paper variant="outlined" sx={{ p: 2, mt: 2, borderColor: 'info.light' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <SchoolIcon color="info" />
          <Typography variant="subtitle2" fontWeight="bold">{t('tab7.r2e_inject.debrief_title')}</Typography>
        </Box>
        <Typography variant="body2" paragraph>
          <strong>AJMedP-1 MTF Cascade:</strong> R2E loss immediately degrades the theatre surgical node.
          R2B provides DC-only bridge care -- not definitive surgery. Patients requiring surgery
          must be evacuated or rerouted. Every hour without surgical access is a golden hour breach.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>Branch Plan Doctrine (AJMedP-2):</strong> R2E loss is a planned contingency requiring
          a pre-authorised branch plan, not improvised response. The branch plan must be rehearsed,
          include designated STRATEVAC uplift triggers, and be activated within minutes of R2E loss.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>Golden Hour Systemic Breach:</strong> Without surgical capacity, patients requiring
          definitive surgery within the golden hour will die. STRATEVAC uplift is the only compliant
          mitigation when adjacent R2E is unavailable or out of range.
        </Typography>
        <Typography variant="body2">
          <strong>Correct responses:</strong> Option A (primary) or Option B (if adjacent R2E is
          within range and has confirmed surgical capacity).
        </Typography>
      </Paper>
    );

    return (
      <Box>
        {doctrineHeader}

        {noBaseline && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No baseline loaded. STRATEVAC daily capacity cannot be computed.
            Load a baseline in Tab 6 to enable quantitative gap analysis.
          </Alert>
        )}

        {/* EXCON config */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>{t('tab7.excon_config')}</Typography>
          {/* IMP-004: Preset quick-load */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mr: 0.5 }}>
              Preset:
            </Typography>
            {INJECT_PRESETS.R2E.map(p => (
              <Chip key={p.key} label={p.label} size="small" variant="outlined" color="secondary"
                onClick={() => setR2eLossInject(prev => ({ ...prev, ...p.fields }))}
                sx={{ fontSize: '0.7rem', height: 22, cursor: 'pointer' }} />
            ))}
          </Box>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('tab7.loss_cause')}</InputLabel>
                <Select
                  value={r2eLossInject.loss_cause}
                  label={t('tab7.loss_cause')}
                  onChange={e => handleR2eField('loss_cause', e.target.value)}
                >
                  <MenuItem value="ENEMY_FIRE">Enemy direct/indirect fire</MenuItem>
                  <MenuItem value="AIRSTRIKE">Hostile airstrike</MenuItem>
                  <MenuItem value="ACCIDENT">Accidental destruction</MenuItem>
                  <MenuItem value="FORCED_DISPLACEMENT">Forced displacement under threat</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('tab7.severity')}</InputLabel>
                <Select
                  value={r2eLossInject.loss_severity}
                  label={t('tab7.severity')}
                  onChange={e => handleR2eField('loss_severity', e.target.value)}
                >
                  <MenuItem value="DESTROYED">Destroyed -- total loss</MenuItem>
                  <MenuItem value="DISABLED">Disabled -- partial capability</MenuItem>
                  <MenuItem value="DISPLACED">Displaced -- reconstituting</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth size="small"
                label={t('tab7.inject_on_day')}
                type="number"
                value={r2eLossInject.day}
                onChange={e => handleR2eField('day', Number(e.target.value))}
                inputProps={{ min: 1, max: opDays }}
                helperText={`Operation day 1-${opDays}`}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Situation card */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'error.main', borderWidth: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Situation -- Day {dayLbl(r2eLossInject.day)} -- R2E {lossSeverityLabel}
          </Typography>
          <Grid container spacing={1} sx={{ mb: 1.5 }}>
            <Grid size={{ xs: 3 }}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'error.50', borderRadius: 1 }}>
                <Typography variant="h6" fontWeight="bold" color="error.main">
                  {lossCauseLabel}
                </Typography>
                <Typography variant="caption" display="block">{t('tab7.incident_cause')}</Typography>
                <Chip label={r2eLossInject.loss_severity} size="small" color="error" sx={{ mt: 0.5 }} />
              </Box>
            </Grid>
            <Grid size={{ xs: 3 }}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="h6" fontWeight="bold">{r2eBeds}</Typography>
                <Typography variant="caption" display="block">{t('tab7.r2e_beds')}</Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                  {r2eFacilities.filter(_isR2EFac).length > 0
                    ? 'from scenario facilities'
                    : 'default fallback'}
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 3 }}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'warning.50', borderRadius: 1 }}>
                <Typography variant="body2" fontWeight="bold" color="warning.dark">
                  DC procedures only
                </Typography>
                <Typography variant="caption" display="block">{t('tab7.r2b_surgical')}</Typography>
                <Typography variant="caption" display="block" color="warning.main">
                  no definitive surgery
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 3 }}>
              <Box sx={{
                textAlign: 'center', p: 1,
                bgcolor: stratAvailable ? 'success.50' : 'error.50',
                borderRadius: 1
              }}>
                <Typography variant="h6" fontWeight="bold" color={stratAvailable ? 'success.main' : 'error.main'}>
                  {stratAvailable ? 'Available' : 'NOT AVAILABLE'}
                </Typography>
                <Typography variant="caption" display="block">{t('tab7.stratevac_status')}</Typography>
                {stratDaily > 0 && (
                  <Typography variant="caption" display="block" color="text.secondary">
                    {stratDaily} pts/day
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Branch Plan Checklist */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'warning.main' }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Branch Plan Checklist -- Actions Required on R2E Loss
          </Typography>
          {[
            'Notify higher HQ of R2E loss and estimated downtime',
            'Activate branch plan -- surge R2B DC throughput',
            'Request emergency STRATEVAC uplift for all surgical cases',
            'Coordinate with adjacent formation R2E for overflow surgical capacity',
            'Establish reconstitution timeline for R2E or identify replacement MTF',
          ].map((label, i) => (
            <FormControlLabel
              key={i}
              control={
                <Checkbox
                  checked={r2eLossInject.checklist[i] ?? false}
                  onChange={e => handleR2eCheckbox(i, e.target.checked)}
                  size="small"
                />
              }
              label={<Typography variant="body2">{label}</Typography>}
              sx={{ display: 'flex', mb: 0.5 }}
            />
          ))}
        </Paper>

        {/* Decision options */}
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          Commander's Decision -- select your course of action:
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, sm: 6 }}>{optA}</Grid>
          <Grid size={{ xs: 12, sm: 6 }}>{optB}</Grid>
          <Grid size={{ xs: 12, sm: 6 }}>{optC}</Grid>
          <Grid size={{ xs: 12, sm: 6 }}>{optD}</Grid>
        </Grid>

        {consequence}

        {/* F1: Structured decision fields */}
        {renderDecisionMeta(r2eLossInject, setR2eLossInject, TAB7_STORAGE_KEYS.R2E_LOSS_INJECT)}

        {selected && (
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button size="small" variant="text" color="inherit" onClick={handleR2eReset}>
              {t('tab7.reset_decision')}
            </Button>
          </Box>
        )}

        {debrief}
      </Box>
    );
  }

  // ============================================================================
  // RENDER: Exercise Projections — 6-column planning horizon grid (Session A)
  // ============================================================================

  function renderExerciseProjections() {
    const res      = injectResult?.results ?? baseline?.results ?? null;
    const baseRes  = baseline?.results ?? null;
    const curDay   = clockDisplay.op_day;
    const totalDays = scenario?.operation_parameters?.duration_days ?? opDays;

    // Build 6 column definitions centred on current op_day
    const columns = [
      { window: 'ASSESS',   day: curDay - 1, label: 'Last 24h' },
      { window: 'ORIENT',   day: curDay,     label: 'Current'  },
      { window: 'REVIEW',   day: curDay + 1, label: '+24h'     },
      { window: 'CONFIRM',  day: curDay + 2, label: '+48h'     },
      { window: 'APPROVE',  day: curDay + 3, label: '+72h'     },
      { window: 'GUIDANCE', day: curDay + 4, label: '+96h'     },
    ].filter(c => c.day >= 1 && c.day <= totalDays);

    // Day data helpers
    const getProjected = (results, day) =>
      results?.by_day?.find(d => d.day === day)?.totals ?? null;
    const getActual = (day) => {
      const entry = dailyLog[day];
      if (!entry) return null;
      const kia  = Number(entry.kia)  || 0;
      const wia  = Number(entry.wia)  || 0;
      const dnbi = Number(entry.dnbi) || 0;
      const dow  = Number(entry.dow)  || 0;
      return (kia || wia || dnbi || dow) ? { kia, wia, dnbi, dow } : null;
    };

    const fmtN = (v) => (v == null ? '\u2014' : Math.round(v).toLocaleString());

    return (
      <Box>
        {!res && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No baseline loaded. Run a calculation in Tab 5 and load it in Tab 6
            to see projections here.
          </Alert>
        )}

        {/* Planning window header row */}
        <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${columns.length}, 1fr)`, gap: 1, mb: 1 }}>
          {columns.map(col => (
            <Box key={col.window} sx={{ textAlign: 'center' }}>
              <Chip
                label={col.window}
                size="small"
                color={WINDOW_COLOR[col.window] || 'default'}
                sx={{ fontSize: '0.65rem', height: 20, width: '100%', mb: 0.25 }}
              />
              <Typography variant="caption" color="text.secondary" display="block">
                {dayLbl(col.day)}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.6rem' }}>
                {col.label}
              </Typography>
            </Box>
          ))}
        </Box>

        <Divider sx={{ mb: 1 }} />

        {/* Data rows */}
        {[
          { label: 'DoW',  field: 'dow',  color: 'error.main'   },
          { label: 'WIA',  field: 'wia',  color: 'warning.main' },
          { label: 'KIA',  field: 'kia',  color: 'text.primary' },
          { label: 'DNBI', field: 'dnbi', color: 'text.secondary'},
        ].map(row => (
          <Box
            key={row.field}
            sx={{ display: 'grid', gridTemplateColumns: `repeat(${columns.length}, 1fr)`, gap: 1, mb: 0.75 }}
          >
            {columns.map(col => {
              const isPast    = col.window === 'ASSESS';
              const isCurrent = col.window === 'ORIENT';
              const actual    = getActual(col.day);
              const projected = getProjected(res, col.day);
              const baseline0 = getProjected(baseRes, col.day);
              const val = actual ? actual[row.field] : projected ? projected[row.field] : null;
              const hasActual = actual && actual[row.field] != null;
              const changed = baseline0 && projected
                && Math.abs((projected[row.field] ?? 0) - (baseline0[row.field] ?? 0)) > 1;

              return (
                <Paper
                  key={col.window}
                  variant="outlined"
                  sx={{
                    p: 0.5,
                    textAlign: 'center',
                    bgcolor: isCurrent ? 'action.selected' : isPast ? 'action.hover' : 'background.paper',
                    opacity: col.window === 'GUIDANCE' ? 0.7 : 1,
                    borderColor: isCurrent ? 'primary.main' : undefined,
                  }}
                >
                  {row.field === 'dow' && (
                    <Typography variant="caption" color="text.disabled" display="block" sx={{ fontSize: '0.58rem', mb: 0.1 }}>
                      {row.label} {hasActual ? '[ACT]' : isPast ? '[-]' : col.window === 'GUIDANCE' ? '[~]' : '[proj]'}
                    </Typography>
                  )}
                  {row.field !== 'dow' && (
                    <Typography variant="caption" color="text.disabled" display="block" sx={{ fontSize: '0.58rem', mb: 0.1 }}>
                      {row.label}
                    </Typography>
                  )}
                  <Typography
                    variant="body2"
                    fontWeight={isCurrent ? 'bold' : 'normal'}
                    sx={{ color: hasActual ? 'secondary.main' : row.color, fontSize: isCurrent ? '0.9rem' : '0.8rem' }}
                  >
                    {fmtN(val)}
                  </Typography>
                  {changed && !hasActual && (
                    <Chip
                      label={((projected[row.field] ?? 0) > (baseline0[row.field] ?? 0) ? '+' : '') +
                        Math.round((projected[row.field] ?? 0) - (baseline0[row.field] ?? 0))}
                      size="small"
                      color={(projected[row.field] ?? 0) > (baseline0[row.field] ?? 0) ? 'error' : 'success'}
                      sx={{ fontSize: '0.55rem', height: 14, mt: 0.25 }}
                    />
                  )}
                </Paper>
              );
            })}
          </Box>
        ))}

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          [ACT] = actual entered in daily log &nbsp;|&nbsp;
          [proj] = calculator projection &nbsp;|&nbsp;
          [~] = estimate (low confidence) &nbsp;|&nbsp;
          Coloured delta chip = change from baseline after injects/countermeasures
        </Typography>
      </Box>
    );
  }

  // ============================================================================
  // RENDER: Countermeasures Panel — positive injects (Session A)
  // ============================================================================

  function renderCountermeasures() {
    const curDay   = clockDisplay.op_day;
    const totalDays = scenario?.operation_parameters?.duration_days ?? opDays;
    const brigadeList = brigades.map(b => ({ id: b.id, label: b.name || b.id }));

    const cmDefs = [
      {
        key: 'blood_request',
        label: 'Blood Request from Depot',
        icon: <HealthAndSafetyIcon color="error" fontSize="small" />,
        desc: 'Request blood products from corps/theatre depot',
        docLabel: 'AJMedP-7 blood chain',
        fields: (cm) => (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small" type="number" label={t('tab7.units_requested')} sx={{ width: 140 }}
              value={cm.units}
              onChange={e => handleCountermeasureChange('blood_request', { units: Number(e.target.value) || 0 })}
              inputProps={{ min: 1, max: 9999 }}
            />
            <TextField
              size="small" type="number" label={t('tab7.arrival_day_dn')} sx={{ width: 150 }}
              InputLabelProps={{ shrink: true }} placeholder="e.g. 3"
              value={cm.arrival_day ?? ''}
              onChange={e => handleCountermeasureChange('blood_request', { arrival_day: Number(e.target.value) || null })}
              inputProps={{ min: curDay, max: totalDays }}
            />
          </Box>
        ),
      },
      {
        key: 'ambulance_request',
        label: 'Ambulance Request from Corps Reserve',
        icon: <HealthAndSafetyIcon color="warning" fontSize="small" />,
        desc: 'Request additional ground evacuation vehicles',
        docLabel: 'AJMedP-2 R1 evac',
        fields: (cm) => (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small" type="number" label={t('tab7.vehicles')} sx={{ width: 120 }}
              value={cm.count}
              onChange={e => handleCountermeasureChange('ambulance_request', { count: Number(e.target.value) || 1 })}
              inputProps={{ min: 1, max: 20 }}
            />
            <TextField
              size="small" type="number" label={t('tab7.arrival_day_dn')} sx={{ width: 150 }}
              InputLabelProps={{ shrink: true }} placeholder="e.g. 3"
              value={cm.arrival_day ?? ''}
              onChange={e => handleCountermeasureChange('ambulance_request', { arrival_day: Number(e.target.value) || null })}
              inputProps={{ min: curDay, max: totalDays }}
            />
          </Box>
        ),
      },
      {
        key: 'medevac_request',
        label: 'MEDEVAC Aircraft Request',
        icon: <AirplanemodeActiveIcon color="primary" fontSize="small" />,
        desc: 'Request additional MEDEVAC helicopters from theatre reserve',
        docLabel: 'AJMedP-2 aeromedical',
        fields: (cm) => (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small" type="number" label={t('tab7.aircraft')} sx={{ width: 120 }}
              value={cm.count}
              onChange={e => handleCountermeasureChange('medevac_request', { count: Number(e.target.value) || 1 })}
              inputProps={{ min: 1, max: 10 }}
            />
            <TextField
              size="small" type="number" label={t('tab7.arrival_day_dn')} sx={{ width: 150 }}
              InputLabelProps={{ shrink: true }} placeholder="e.g. 3"
              value={cm.arrival_day ?? ''}
              onChange={e => handleCountermeasureChange('medevac_request', { arrival_day: Number(e.target.value) || null })}
              inputProps={{ min: curDay, max: totalDays }}
            />
          </Box>
        ),
      },
      {
        key: 'frt_deploy',
        label: 'FRT Deployment',
        icon: <HealthAndSafetyIcon color="success" fontSize="small" />,
        desc: 'Deploy Forward Resuscitation Team to increase surgical capacity',
        docLabel: 'AJMedP-1 FRT',
        fields: (cm) => (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {brigadeList.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>{t('tab7.brigade')}</InputLabel>
                <Select
                  value={cm.brigade_id ?? ''}
                  label={t('tab7.brigade')}
                  onChange={e => handleCountermeasureChange('frt_deploy', { brigade_id: e.target.value || null })}
                >
                  <MenuItem value="">Corps-wide</MenuItem>
                  {brigadeList.map(b => <MenuItem key={b.id} value={b.id}>{b.label}</MenuItem>)}
                </Select>
              </FormControl>
            )}
            <TextField
              size="small" type="number" label={t('tab7.effective_day_dn')} sx={{ width: 160 }}
              value={cm.effective_day ?? ''}
              onChange={e => handleCountermeasureChange('frt_deploy', { effective_day: Number(e.target.value) || null })}
              inputProps={{ min: curDay, max: totalDays }}
            />
          </Box>
        ),
      },
      {
        key: 'axp_preposition',
        label: 'AXP Preposition',
        icon: <AddCircleOutlineIcon color="primary" fontSize="small" />,
        desc: 'Preposition Aid Exchange Point to reduce evacuation distance',
        docLabel: 'AJMedP-1 AXP',
        fields: (cm) => (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {brigadeList.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>{t('tab7.brigade')}</InputLabel>
                <Select
                  value={cm.brigade_id ?? ''}
                  label={t('tab7.brigade')}
                  onChange={e => handleCountermeasureChange('axp_preposition', { brigade_id: e.target.value || null })}
                >
                  <MenuItem value="">All brigades</MenuItem>
                  {brigadeList.map(b => <MenuItem key={b.id} value={b.id}>{b.label}</MenuItem>)}
                </Select>
              </FormControl>
            )}
            <TextField
              size="small" type="number" label={t('tab7.distance_reduction_km')} sx={{ width: 180 }}
              value={cm.distance_reduction_km}
              onChange={e => handleCountermeasureChange('axp_preposition', { distance_reduction_km: Number(e.target.value) || 5 })}
              inputProps={{ min: 1, max: 50 }}
            />
            <TextField
              size="small" type="number" label={t('tab7.effective_day_dn')} sx={{ width: 160 }}
              value={cm.effective_day ?? ''}
              onChange={e => handleCountermeasureChange('axp_preposition', { effective_day: Number(e.target.value) || null })}
              inputProps={{ min: curDay, max: totalDays }}
            />
          </Box>
        ),
      },
      {
        key: 'mtf_reposition',
        label: 'MTF Reposition',
        icon: <MapIcon color="primary" fontSize="small" />,
        desc: 'Move medical treatment facility forward or rearward',
        docLabel: 'AJMedP-1 MTF',
        fields: (cm) => (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>{t('tab7.direction')}</InputLabel>
              <Select
                value={cm.direction}
                label={t('tab7.direction')}
                onChange={e => handleCountermeasureChange('mtf_reposition', { direction: e.target.value })}
              >
                <MenuItem value="FORWARD">Forward (reduce distance)</MenuItem>
                <MenuItem value="REARWARD">Rearward (increase distance)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small" type="number" label={t('tab7.distance_km')} sx={{ width: 130 }}
              value={cm.distance_km}
              onChange={e => handleCountermeasureChange('mtf_reposition', { distance_km: Number(e.target.value) || 5 })}
              inputProps={{ min: 1, max: 100 }}
            />
            <TextField
              size="small" type="number" label={t('tab7.effective_day_dn')} sx={{ width: 160 }}
              value={cm.effective_day ?? ''}
              onChange={e => handleCountermeasureChange('mtf_reposition', { effective_day: Number(e.target.value) || null })}
              inputProps={{ min: curDay, max: totalDays }}
            />
          </Box>
        ),
      },
    ];

    const activeCMCount = Object.values(countermeasures).filter(v => v.active).length;

    return (
      <Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          Countermeasures modify the medical planning baseline positively. Activate one or more,
          then click <strong>Run Calculation</strong> in the Impact Assessment panel to see the combined
          effect on the projection. Lead times are doctrine-based — the tool will warn if your
          effective day is unrealistically close to the current day.
        </Alert>

        {cmDefs.map(def => {
          const cm = countermeasures[def.key];
          const minDay = curDay + (CM_LEAD_TIME[def.key] || 1);
          const effectiveDay = cm.arrival_day ?? cm.effective_day ?? null;
          const leadTimeViolation = effectiveDay != null && effectiveDay < minDay;
          return (
            <Paper key={def.key} variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: cm.active ? 1.5 : 0 }}>
                {def.icon}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight="medium">{def.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{def.desc}</Typography>
                </Box>
                <Chip label={def.docLabel} size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 18 }} />
                <Tooltip title={cm.active ? 'Deactivate' : 'Activate this countermeasure'}>
                  <Chip
                    label={cm.active ? 'ACTIVE' : 'OFF'}
                    size="small"
                    color={cm.active ? 'success' : 'default'}
                    onClick={() => handleCountermeasureChange(def.key, { active: !cm.active })}
                    sx={{ minWidth: 56, cursor: 'pointer' }}
                  />
                </Tooltip>
              </Box>

              {cm.active && (
                <Box>
                  {def.fields(cm)}
                  {leadTimeViolation && (
                    <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
                      Doctrine minimum lead time: {CM_LEAD_TIME[def.key]} day(s) from current D+
                      {curDay - 1}. Earliest realistic effective day: {dayLbl(minDay)}.
                    </Alert>
                  )}
                </Box>
              )}
            </Paper>
          );
        })}

        {activeCMCount > 0 && (
          <Alert severity="success" sx={{ mt: 1 }}>
            {activeCMCount} countermeasure{activeCMCount !== 1 ? 's' : ''} active.
            Go to <strong>Phase 11 — Impact Assessment</strong> and click
            <strong> Run Calculation</strong> to see the combined projection.
          </Alert>
        )}
      </Box>
    );
  }

  // ============================================================================
  // RENDER: Post-Inject Impact Assessment (ENH-031b)
  // ============================================================================

  function renderInjectImpact() {

    // -- Active inject summary -------------------------------------------------
    const activeInjects = [
      mroeInject.decision        && { id: 'epw',      label: 'EPW/EWIA inject',       decision: mroeInject.decision,        canCalc: true },
      civInject.decision         && { id: 'civ',      label: 'Civilian Encounter',     decision: civInject.decision,         canCalc: true },
      mascalInject.decision      && { id: 'mascal',   label: 'MASCAL Event',           decision: mascalInject.decision,      canCalc: mascalInject.decision !== 'B' },
      medevacLossInject.decision && { id: 'medevac',  label: 'MEDEVAC Asset Loss',     decision: medevacLossInject.decision, canCalc: true },
      mtfDisplaceInject.decision && { id: 'mtf',      label: 'MTF Displacement',       decision: mtfDisplaceInject.decision, canCalc: false },
      locInterdictInject.decision && { id: 'loc',     label: 'LOC Interdiction',       decision: locInterdictInject.decision,canCalc: false },
      r2eLossInject.decision        && { id: 'r2e',           label: 'R2E Loss Branch Plan',      decision: r2eLossInject.decision,        canCalc: false },
      counterattackInject.decision  && { id: 'counterattack', label: 'Enemy Counterattack',        decision: counterattackInject.decision,  canCalc: true  },
      weatherInject.decision        && { id: 'weather',       label: 'Weather Change',             decision: weatherInject.decision,        canCalc: true  },
      convoyInject.decision         && { id: 'convoy',        label: 'Supply Convoy Ambush',       decision: convoyInject.decision,         canCalc: true  },
    ].filter(Boolean);

    const hasDecisions  = activeInjects.length > 0;
    const calcableCount = activeInjects.filter(i => i.canCalc).length;

    // -- Baseline scenario retrieval -------------------------------------------
    // COA-BASELINE-001: Priority 1 = selected CoA snapshot (frozen plan).
    // Priority 2 = calculationService (sessionStorage — fullest results).
    // Priority 3 = Tab 6 approved plan / exercise baseline.
    function getBaseScenario() {
      // Priority 1: Explicitly selected CoA snapshot (frozen plan)
      if (coaBaselineSlot !== 'LIVE') {
        const key = coaBaselineSlot === 'COA_A' ? 'psp_cre_coa_1' : 'psp_cre_coa_2';
        const coa = lsRead(key);
        if (coa?.snapshot && coa?.results) return { scenario: coa.snapshot, results: coa.results };
      }
      // Priority 2: calculationService (sessionStorage — fullest results)
      const full = getCalculatedBaseline();
      if (full?.scenario) return { scenario: full.scenario, results: full.results };
      // Priority 3: Tab 6 approved plan / exercise baseline
      if (baseline?.scenario) return { scenario: baseline.scenario, results: baseline.results };
      return null;
    }

    // -- buildInjectedScenario -------------------------------------------------
    function buildInjectedScenario(baseScenario) {
      // Deep clone — do not mutate the stored baseline
      const mod = JSON.parse(JSON.stringify(baseScenario));
      const ops = mod.operation_parameters = mod.operation_parameters || {};
      const events = ops.discrete_events = Array.isArray(ops.discrete_events)
        ? [...ops.discrete_events] : [];

      // 1. MASCAL Event → ENH-041 discrete event (A/C/D only; B = absorb, no protocol)
      if (mascalInject.decision && mascalInject.decision !== 'B') {
        const evtTypeMap = {
          ARTILLERY: 'ARTILLERY_STRIKE',
          IED:       'IED_MASS_CASUALTY',
          AIRSTRIKE: 'ARTILLERY_STRIKE', // closest type in catalog
        };
        events.push({
          id:            'inject_mascal_031b',
          event_type:    evtTypeMap[mascalInject.event_type] || 'ARTILLERY_STRIKE',
          day:           Number(mascalInject.day) || 4,
          brigade_id:    null,  // null → applies to all brigades proportionally (ENH-041 line 9771)
          casualty_count: Number(mascalInject.casualty_count) || 0,
          kia_fraction:  0.10,  // 10% KIA — MASCAL surge, triage reduces immediate KIA
          description:   `EXCON MASCAL inject ${dayLbl(mascalInject.day)} — ${mascalInject.event_type}`,
        });
      }

      // 2. EPW/EWIA inject → escalate mroe_config.epw_volume
      if (mroeInject.decision) {
        const epwCount = Number(mroeInject.epw_count) || 0;
        const epwVol   = epwCount > 50 ? 'HIGH' : epwCount > 20 ? 'MODERATE' : 'LOW';
        ops.mroe_config = {
          ...(ops.mroe_config || {}),
          epw_volume: epwVol,
          epw_level:  epwVol,   // backward compat alias
        };
      }

      // 3. Civilian Encounter → escalate mroe_config.civilian_density
      if (civInject.decision && civInject.decision !== 'C' &&
          !(civInject.scenario_type === 'MASS' && civInject.decision === 'D')) {
        const civCount = Number(civInject.civ_count) || 0;
        const civDens  = civInject.scenario_type === 'INDIVIDUAL'
          ? 'LOW'
          : civCount >= 300 ? 'HIGH' : civCount >= 100 ? 'MODERATE' : 'LOW';
        ops.mroe_config = {
          ...(ops.mroe_config || {}),
          civilian_density: civDens,
          civilian_level:   civDens,  // backward compat
        };
      }

      // 4. MEDEVAC Asset Loss → reduce nominal_daily_sorties in tactical_evac_config
      if (medevacLossInject.decision) {
        const med    = mod.medical_configuration = mod.medical_configuration || {};
        const tec    = med.tactical_evac_config  = { ...(med.tactical_evac_config || {}) };
        const air    = tec.air_pool              = { ...(tec.air_pool || {}) };
        const nomSorties = Number(air.nominal_daily_sorties) || 0;
        const hCount     = Number(air.helicopter_count ?? air.allocated_aircraft ?? 0);
        const sPerAir    = hCount > 0 ? Math.round(nomSorties / hCount) : 1;
        const lost       = Number(medevacLossInject.platforms_lost) || 1;
        air.nominal_daily_sorties = Math.max(0, nomSorties - lost * sPerAir);
        // Also log as a discrete event marker (0 casualties — ENH-041 catalog)
        events.push({
          id:            'inject_medevac_loss_031b',
          event_type:    'MEDEVAC_ASSET_LOSS',
          day:           Number(medevacLossInject.day) || 3,
          brigade_id:    null,
          casualty_count: 0,  // capacity event, not casualty event
          description:   `EXCON MEDEVAC loss ${dayLbl(medevacLossInject.day)} — ${lost} aircraft`,
        });
      }

      // 5. MTF Displacement + LOC Interdiction → marker events only (no calculator param)
      if (mtfDisplaceInject.decision) {
        events.push({
          id: 'inject_mtf_031b',
          event_type: 'FACILITY_STRIKE',  // closest available type (displacement trigger)
          day: Number(mtfDisplaceInject.day) || 5,
          brigade_id: null,
          casualty_count: 0,              // no staff casualties modelled — coverage gap is temporal
          description: `EXCON MTF displacement ${dayLbl(mtfDisplaceInject.day)}`,
        });
      }
      if (locInterdictInject.decision) {
        events.push({
          id: 'inject_loc_031b',
          event_type: 'LOC_INTERDICTION',
          day: Number(locInterdictInject.day) || 6,
          brigade_id: null,
          casualty_count: 0,              // logistics event — blood DOS shown in consequence block
          description: `EXCON LOC interdiction ${dayLbl(locInterdictInject.day)}`,
        });
      }

      // 6. R2E Loss → marker event (qualitative; STRATEVAC/reroute consequence in inject block)
      if (r2eLossInject.decision) {
        events.push({
          id: 'inject_r2e_loss_031b',
          event_type: 'FACILITY_STRIKE',  // closest catalog type for surgical node loss
          day: Number(r2eLossInject.day) || 3,
          brigade_id: null,
          casualty_count: 0,
          description: `EXCON R2E loss ${dayLbl(r2eLossInject.day ?? 3)} — branch plan: ${r2eLossInject.decision}`,
        });
      }

      // 7. TAB7-CA: Enemy Counterattack → discrete surge event + decision-based response
      if (counterattackInject.decision) {
        const surgeCas = Number(counterattackInject.surge_casualties) || 35;
        const hitBde   = brigades[counterattackInject.brigade_index ?? 0];
        events.push({
          id:            'inject_counterattack_031b',
          event_type:    'ARTILLERY_STRIKE',  // closest catalog type for ground assault surge
          day:           Number(counterattackInject.day) || 5,
          brigade_id:    hitBde?.id || null,
          casualty_count: surgeCas,
          kia_fraction:  0.12,
          description:   `EXCON Counterattack ${dayLbl(counterattackInject.day)} — ${counterattackInject.attack_type} — ${surgeCas} WIA surge`,
        });
        // Decision A: pre-position FRT + boost sorties
        if (counterattackInject.decision === 'A') {
          const med2 = mod.medical_configuration = mod.medical_configuration || {};
          const sc2  = med2.surgical_config = { ...(med2.surgical_config || {}) };
          const ft2  = sc2.frt_teams = { ...(sc2.frt_teams || {}) };
          ft2.count  = (Number(ft2.count) || 0) + 1;
          const tec2 = med2.tactical_evac_config = { ...(med2.tactical_evac_config || {}) };
          const air2 = tec2.air_pool = { ...(tec2.air_pool || {}) };
          air2.nominal_daily_sorties = Math.max(0, (Number(air2.nominal_daily_sorties) || 0) + 2);
        }
        // Decision C: emergency blood request
        if (counterattackInject.decision === 'C') {
          const med2 = mod.medical_configuration = mod.medical_configuration || {};
          med2.blood_stock_units = (Number(med2.blood_stock_units) || 0) + 40;
        }
        // Decision D: STRATEVAC uplift (proxy: blood + MEDEVAC sorties)
        if (counterattackInject.decision === 'D') {
          const med2 = mod.medical_configuration = mod.medical_configuration || {};
          med2.blood_stock_units = (Number(med2.blood_stock_units) || 0) + 20;
          const tec2 = med2.tactical_evac_config = { ...(med2.tactical_evac_config || {}) };
          const air2 = tec2.air_pool = { ...(tec2.air_pool || {}) };
          air2.nominal_daily_sorties = Math.max(0, (Number(air2.nominal_daily_sorties) || 0) + 3);
        }
        // Decision B: absorb — discrete event already injected, no param changes
      }

      // 8. TAB7-WX: Weather Change → MEDEVAC degradation + optional ground uplift
      if (weatherInject.decision) {
        const durDays = Number(weatherInject.duration_days) || 2;
        events.push({
          id:            'inject_weather_031b',
          event_type:    'LOC_INTERDICTION',  // closest type: capability-degradation marker
          day:           Number(weatherInject.day) || 7,
          brigade_id:    null,
          casualty_count: 0,
          description:   `EXCON Weather (${weatherInject.weather_type}) ${dayLbl(weatherInject.day)} — ${durDays}d MEDEVAC degradation`,
        });
        // Reduce MEDEVAC sorties based on decision
        const degradeFactors = { A: 0.80, B: 0.80, C: 0.60, D: 0.40 };
        const factor = degradeFactors[weatherInject.decision] ?? 0.80;
        const medW   = mod.medical_configuration = mod.medical_configuration || {};
        const tecW   = medW.tactical_evac_config  = { ...(medW.tactical_evac_config || {}) };
        const airW   = tecW.air_pool              = { ...(tecW.air_pool || {}) };
        airW.nominal_daily_sorties = Math.round(
          Math.max(0, (Number(airW.nominal_daily_sorties) || 4) * (1 - factor))
        );
        // Decisions A & B: activate ground CASEVAC (+2 vehicles)
        if (weatherInject.decision === 'A' || weatherInject.decision === 'B') {
          const gndW = tecW.ground_pool = { ...(tecW.ground_pool || {}) };
          gndW.vehicle_count = (Number(gndW.vehicle_count) || 0) + 2;
        }
      }

      // 9. TAB7-CV: Supply Convoy Ambush → blood stock degradation + response
      if (convoyInject.decision) {
        const bloodLost = Number(convoyInject.blood_units_lost) || 50;
        events.push({
          id:            'inject_convoy_031b',
          event_type:    'LOC_INTERDICTION',
          day:           Number(convoyInject.day) || 6,
          brigade_id:    null,
          casualty_count: 0,
          description:   `EXCON Convoy ambush ${dayLbl(convoyInject.day)} — ${bloodLost} PRBC + ${convoyInject.class_viii_dos_lost} DOS Class VIII lost`,
        });
        // Apply the blood loss first (base impact for all decisions)
        const medC = mod.medical_configuration = mod.medical_configuration || {};
        medC.blood_stock_units = Math.max(0, (Number(medC.blood_stock_units) || 0) - bloodLost);
        // Decision A: emergency air resupply — recovers 70%
        if (convoyInject.decision === 'A') {
          medC.blood_stock_units = (Number(medC.blood_stock_units) || 0) + Math.round(bloodLost * 0.7);
        }
        // Decision C: HNS blood support — recovers 50%
        if (convoyInject.decision === 'C') {
          medC.blood_stock_units = (Number(medC.blood_stock_units) || 0) + Math.round(bloodLost * 0.5);
        }
        // Decision B: rationing protocol — blood stays down, consequence shown in UI
        // Decision D: accept degraded stock — no recovery, full blood_stock_units loss remains
      }

      // Session A — Countermeasures (positive parameter modifications)
      const med = mod.medical_configuration = mod.medical_configuration || {};
      const tec = med.tactical_evac_config   = { ...(med.tactical_evac_config || {}) };

      // Blood request — add units to existing stock
      if (countermeasures.blood_request.active) {
        med.blood_stock_units = (Number(med.blood_stock_units) || 0)
          + Number(countermeasures.blood_request.units || 0);
      }

      // Ambulance request — add vehicles to ground pool
      if (countermeasures.ambulance_request.active) {
        const gnd = tec.ground_pool = { ...(tec.ground_pool || {}) };
        gnd.vehicle_count = (Number(gnd.vehicle_count) || 0)
          + Number(countermeasures.ambulance_request.count || 0);
      }

      // MEDEVAC request — add aircraft to air pool
      if (countermeasures.medevac_request.active) {
        const air = tec.air_pool = { ...(tec.air_pool || {}) };
        const added = Number(countermeasures.medevac_request.count || 0);
        air.helicopter_count = (Number(air.helicopter_count) || 0) + added;
        const sortsPerAc = air.helicopter_count > 0
          ? Math.round((air.nominal_daily_sorties || 4) / (air.helicopter_count - added))
          : 1;
        air.nominal_daily_sorties = (Number(air.nominal_daily_sorties) || 0)
          + added * sortsPerAc;
      }

      // FRT deployment — add forward resuscitation team (surgical capacity)
      if (countermeasures.frt_deploy.active) {
        const sc = med.surgical_config = { ...(med.surgical_config || {}) };
        const ft = sc.frt_teams = { ...(sc.frt_teams || {}) };
        ft.count = (Number(ft.count) || 0) + 1;
      }

      // AXP preposition — reduce evacuation distance for all brigades
      if (countermeasures.axp_preposition.active) {
        const reduction = Number(countermeasures.axp_preposition.distance_reduction_km) || 10;
        const corps_cfg = mod.operation_parameters = mod.operation_parameters || {};
        corps_cfg.axp_distance_reduction_km =
          (Number(corps_cfg.axp_distance_reduction_km) || 0) + reduction;
        // Also reduce per-brigade evacuation distances if present
        (med.per_facility_evacuation_assets || []).forEach((fa, i) => {
          if (fa.evacuation_distance_km) {
            med.per_facility_evacuation_assets[i] = {
              ...fa,
              evacuation_distance_km: Math.max(1, fa.evacuation_distance_km - reduction),
            };
          }
        });
      }

      // MTF reposition — reduce corps evacuation distance
      if (countermeasures.mtf_reposition.active) {
        const distKm = Number(countermeasures.mtf_reposition.distance_km) || 15;
        const sign = countermeasures.mtf_reposition.direction === 'FORWARD' ? -1 : 1;
        (med.per_facility_evacuation_assets || []).forEach((fa, i) => {
          if (fa.evacuation_distance_km) {
            med.per_facility_evacuation_assets[i] = {
              ...fa,
              evacuation_distance_km: Math.max(1, fa.evacuation_distance_km + sign * distKm),
            };
          }
        });
      }

      return mod;
    }

    // -- runInjectCalculation --------------------------------------------------
    async function runInjectCalculation() {
      const base = getBaseScenario();
      if (!base?.scenario) return;
      setInjectCalculating(true);
      await new Promise(resolve => setTimeout(resolve, 40)); // yield: let React paint "Calculating..."
      try {
        const modifiedScenario = buildInjectedScenario(base.scenario);
        const injectResults    = calculateCasualties(modifiedScenario);
        const resultObj = {
          results:        injectResults,
          base_results:   base.results,     // stored for delta comparison
          calculated_at:  new Date().toISOString(),
          inject_summary: activeInjects.map(i => ({ id: i.id, label: i.label, decision: i.decision, can_calc: i.canCalc })),
        };
        lsWrite(TAB7_STORAGE_KEYS.INJECT_RESULT, resultObj);
        setInjectResult(resultObj);

        // F2: Auto-log session history entry
        const _injectMetas = [
          { id: 'mroe',    label: 'MRoE',    state: mroeInject },
          { id: 'civ',     label: 'CIV',     state: civInject },
          { id: 'mascal',  label: 'MASCAL',  state: mascalInject },
          { id: 'medevac', label: 'MEDEVAC', state: medevacLossInject },
          { id: 'mtf',     label: 'MTF',     state: mtfDisplaceInject },
          { id: 'loc',     label: 'LOC',     state: locInterdictInject },
          { id: 'r2e',           label: 'R2E',           state: r2eLossInject },
          { id: 'counterattack', label: 'COUNTERATTACK', state: counterattackInject },
          { id: 'weather',       label: 'WEATHER',       state: weatherInject },
          { id: 'convoy',        label: 'CONVOY',        state: convoyInject },
        ].filter(m => m.state.decision !== null);

        const _picki = (paths) => pick(injectResults, ...paths);
        const _pickb = (paths) => pick(base.results,   ...paths);
        const _diff  = (paths) => {
          const iv = _picki(paths); const bv = _pickb(paths);
          return iv != null && bv != null ? Math.round((iv - bv) * 10) / 10 : null;
        };

        // Session A — active countermeasures included in history entry
        const _activeCMs = Object.entries(countermeasures)
          .filter(([, v]) => v.active)
          .map(([k, v]) => ({ id: k, effective_day: v.arrival_day ?? v.effective_day ?? null }));

        // Session C — Item 18: correct planning window
        // clockDisplay.op_day is 1-indexed (op_day=1 = D+0). inject.day is D+N notation.
        // Convert: currentDayInDPlus = op_day - 1. Use earliest inject event day for offset.
        const _currentDayInDPlus = Math.max(0, clockDisplay.op_day - 1);
        const _injectEventDays = _injectMetas
          .map(m => Number(m.state?.day))
          .filter(d => !Number.isNaN(d) && d > 0);
        const _earliestEventDay = _injectEventDays.length > 0
          ? Math.min(..._injectEventDays)
          : _currentDayInDPlus;
        const _planning_window = getPlanningWindow(_earliestEventDay, _currentDayInDPlus);

        const _histEntry = {
          id:             crypto.randomUUID(),
          timestamp:      new Date().toISOString(),
          type:           _activeCMs.length > 0 ? 'COUNTERMEASURE' : 'INJECT',
          op_day:         clockDisplay.op_day,
          planning_window: _planning_window,
          inject_summary: resultObj.inject_summary,
          inject_metas:   _injectMetas.map(m => ({
            id:             m.id,
            label:          m.label,
            decision:       m.state.decision,
            threat_class:   m.state.threat_class,
            rec_action:     m.state.rec_action,
            confidence:     m.state.confidence,
            decision_notes: m.state.decision_notes,
          })),
          countermeasure_metas: _activeCMs,
          delta: {
            total_dow:   _diff(['by_corps.total_dow']),
            blood_units: _diff(['medical_requirements.blood.total_units', 'medical_requirements.blood.prbc']),
            mascal_bdes: _diff(['mascal_analysis.system_summary.brigades_ever_critical']),
          },
        };
        const _updatedHistory = [...sessionHistory, _histEntry];
        lsWrite(TAB7_STORAGE_KEYS.SESSION_HISTORY, _updatedHistory);
        setSessionHistory(_updatedHistory);

        // P2-S6: persist student session history to Supabase (fire-and-forget)
        if (userRole === 'STUDENT' && studentId && exerciseId) {
          exerciseService.saveStudentSession(studentId, exerciseId, _updatedHistory)
            .catch(() => {}); // non-blocking — network failure does not break the exercise
        }

        // P2-S5: student broadcasts their decision to trainer's live dashboard
        if (userRole === 'STUDENT' && sessionCode && studentId) {
          realtimeService.send('student_decision', {
            student_id:   studentId,
            student_name: userName,
            op_day:       _histEntry.op_day,
            decisions:    _histEntry.inject_metas.filter(m => m.decision),
            delta:        _histEntry.delta,
            timestamp:    _histEntry.timestamp,
          });
        }

        // Session B — save debrief snapshot (keep last 10 for Tab 8 progression trace)
        const _existingSnaps = lsRead(TAB7_STORAGE_KEYS.DEBRIEF_SNAPSHOTS) || [];
        const _snap = {
          id:                   crypto.randomUUID(),
          timestamp:            new Date().toISOString(),
          op_day:               clockDisplay.op_day,
          type:                 _histEntry.type,
          planning_window:      _histEntry.planning_window,
          by_day:               injectResults?.by_day ?? null,
          by_corps:             injectResults?.by_corps ?? null,
          medical_requirements: injectResults?.medical_requirements ?? null,
          inject_summary:       resultObj.inject_summary,
        };
        const _updatedSnaps = [..._existingSnaps, _snap].slice(-10);
        lsWrite(TAB7_STORAGE_KEYS.DEBRIEF_SNAPSHOTS, _updatedSnaps);

        // Session C — Item 20: snapshot confirmation Snackbar
        setSnackbar({
          open:     true,
          message:  `Debrief snapshot saved — ${dayLbl(_currentDayInDPlus)} | ${_planning_window} | ${resultObj.inject_summary.length} decision(s)`,
          severity: 'success',
        });
      } catch (_err) {
        // calculation error — do not crash the tab
      } finally {
        setInjectCalculating(false);
      }
    }

    // -- Delta metric extraction -----------------------------------------------
    function pick(results, ...paths) {
      for (const path of paths) {
        let cur = results;
        for (const k of path.split('.')) { cur = cur?.[k]; }
        if (cur != null) return Number(cur);
      }
      return null;
    }

    // BUG-S82-002 FIX: All 4 non-Golden-Hour paths were wrong against the actual calculator output.
    // Previous → correct mapping:
    //   blood_dos:   'supply_adequacy.blood_dos'                     → doesn't exist; use blood units required
    //   peak_r2b:    'mascal_analysis.peak_r2b_pct'                  → never emitted; use brigades_ever_critical
    //   total_dow:   'totals.total_dow'                              → by_corps.total_dow
    //   mascal_days: 'mascal_analysis.system_summary.total_mascal_days' → first_critical_day
    const METRICS = [
      { key: 'golden_hour',  label: 'Golden Hour',          unit: '%', higherIsBetter: true,
        paths: ['medical_system_analysis.overall_golden_hour_percent', 'summary.golden_hour_compliance_pct'] },
      { key: 'blood_req',    label: 'Blood Required (units)', unit: '',  higherIsBetter: false,
        paths: ['medical_requirements.blood.total_units', 'medical_requirements.blood.prbc'] },
      { key: 'mascal_bdes',  label: 'Brigades in MASCAL',   unit: '',  higherIsBetter: false,
        paths: ['mascal_analysis.system_summary.brigades_ever_critical'] },
      { key: 'total_dow',    label: 'Total DOW',             unit: '',  higherIsBetter: false,
        paths: ['by_corps.total_dow'] },
      { key: 'first_mascal', label: 'First MASCAL Day',      unit: 'd', higherIsBetter: false,
        paths: ['mascal_analysis.system_summary.first_critical_day'] },
    ];

    const baseHasData = !!(getCalculatedBaseline()?.results ?? baseline?.results);

    // -- UI --------------------------------------------------------------------
    return (
      <Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>ENH-031b — Post-Inject Recalculation</AlertTitle>
          Applies all active inject decisions to a modified copy of the baseline scenario and
          runs the full casualty calculator. Results show the projected change in key metrics.
          Injects with quantitative calculator parameters (MASCAL event, EPW load, civilian density,
          MEDEVAC sortie capacity) produce real deltas. MTF displacement and LOC interdiction
          effects are logged as discrete event markers; their time-based and logistics impacts
          are shown in each inject's consequence block.
        </Alert>

        {!hasDecisions && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No inject decisions recorded. Make at least one decision in Phases 4-9 above to
            enable post-inject recalculation.
          </Alert>
        )}

        {!baseHasData && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No baseline calculation found. Run the full baseline calculation in Tab 5, then load
            it in Tab 6 before using this feature.
          </Alert>
        )}

        {/* Active inject summary */}
        {hasDecisions && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Active Decisions ({activeInjects.length} injects)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              {activeInjects.map(inj => (
                <Chip
                  key={inj.id}
                  label={`${inj.label}: Opt ${inj.decision}${inj.canCalc ? '' : ' (qualitative)'}`}
                  size="small"
                  color={inj.canCalc ? 'primary' : 'default'}
                  variant={inj.canCalc ? 'filled' : 'outlined'}
                />
              ))}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {calcableCount} of {activeInjects.length} inject(s) modify calculator parameters.
              {activeInjects.some(i => !i.canCalc) && ' Qualitative-only injects are logged but do not change output metrics.'}
            </Typography>
          </Paper>
        )}

        {/* Non-cumulation teaching note */}
        <Alert severity="info" icon={<SchoolIcon fontSize="small" />} sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Each inject is calculated independently against the baseline.</strong> Results show the isolated
            impact of all active decisions combined in a single recalculation — they do not represent a cumulative
            battlefield state. If multiple injects are active, press <em>Run Post-Inject Calculation</em> once to
            see their combined delta vs baseline.
          </Typography>
        </Alert>

        {/* Calculate button */}
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<TimelineIcon />}
            onClick={runInjectCalculation}
            disabled={injectCalculating || !hasDecisions || !baseHasData}
          >
            {injectCalculating ? 'Calculating...' : 'Run Post-Inject Calculation'}
          </Button>
          {injectResult && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
              Last run: {new Date(injectResult.calculated_at).toLocaleTimeString()}
            </Typography>
          )}
        </Box>

        {/* Delta results table */}
        {injectResult?.results && injectResult?.base_results && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Baseline vs Post-Inject Delta
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Metric</strong></TableCell>
                  <TableCell align="right"><strong>Baseline</strong></TableCell>
                  <TableCell align="right"><strong>Post-Inject</strong></TableCell>
                  <TableCell align="center"><strong>Delta</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {METRICS.map(m => {
                  const baseVal   = pick(injectResult.base_results, ...m.paths);
                  const injectVal = pick(injectResult.results,      ...m.paths);
                  const delta     = baseVal != null && injectVal != null
                    ? Math.round((injectVal - baseVal) * 10) / 10
                    : null;
                  const improved  = delta != null && (m.higherIsBetter ? delta > 0 : delta < 0);
                  const degraded  = delta != null && (m.higherIsBetter ? delta < 0 : delta > 0);
                  const chipColor = improved ? 'success' : degraded ? 'error' : 'default';
                  const deltaLabel = delta == null
                    ? 'N/A'
                    : `${delta > 0 ? '+' : ''}${delta}${m.unit}`;
                  return (
                    <TableRow key={m.key}>
                      <TableCell>{m.label}</TableCell>
                      <TableCell align="right">
                        {baseVal != null ? `${Math.round(baseVal * 10) / 10}${m.unit}` : 'N/A'}
                      </TableCell>
                      <TableCell align="right">
                        {injectVal != null ? `${Math.round(injectVal * 10) / 10}${m.unit}` : 'N/A'}
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={deltaLabel} size="small" color={chipColor} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Applied: {injectResult.inject_summary?.filter(i => i.can_calc).map(i => i.label).join(', ') || 'none'}.
              Qualitative only (not in delta): {injectResult.inject_summary?.filter(i => !i.can_calc).map(i => i.label).join(', ') || 'none'}.
            </Typography>
          </Paper>
        )}

        {injectResult?.results && !injectResult?.base_results && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Baseline results unavailable for delta comparison. Re-run calculation in Tab 5 to restore
            the baseline results from sessionStorage, then recalculate here.
          </Alert>
        )}
      </Box>
    );
  }

  // ============================================================================
  // SF-3: STUDENT SIMPLIFIED VIEW
  // ============================================================================

  function renderStudentView() {
    const injectStateMap = {
      MROE:          mroeInject,
      CIV:           civInject,
      MASCAL:        mascalInject,
      MEDEVAC:       medevacLossInject,
      MTF:           mtfDisplaceInject,
      LOC:           locInterdictInject,
      R2E:           r2eLossInject,
      COUNTERATTACK: counterattackInject,
      WEATHER:       weatherInject,
      CONVOY:        convoyInject,
    };

    const config       = activeStudentInjectType ? STUDENT_INJECT_CONFIG[activeStudentInjectType] : null;
    const injectState  = activeStudentInjectType ? injectStateMap[activeStudentInjectType] : null;
    const currentDecision = injectState?.decision ?? null;

    // CIV inject has two option sets keyed by scenario type
    const options = (() => {
      if (!config) return [];
      if (activeStudentInjectType === 'CIV') {
        const scenType = civInject.scenario_type || 'INDIVIDUAL';
        return config.options[scenType] || config.options.INDIVIDUAL;
      }
      return config.options;
    })();

    // R2E uses string decision keys — displayKey shows A/B/C/D to the student
    const getDisplayKey = (opt) => opt.displayKey || opt.key;
    const selectedOpt  = options.find(o => o.key === currentDecision);

    return (
      <Box>
        {/* Student identity bar */}
        <Paper
          variant="outlined"
          sx={{
            p: 1.5, mb: 2,
            display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
            bgcolor: 'primary.50', borderColor: 'primary.200',
          }}
        >
          <SchoolIcon color="primary" fontSize="small" />
          <Typography variant="body2" fontWeight={600} color="primary.dark">
            {userName}
          </Typography>
          <Chip
            label={sessionCode}
            size="small" variant="outlined" color="primary"
            sx={{ fontFamily: 'monospace', letterSpacing: 1 }}
          />
          <Box sx={{ flex: 1 }} />
          {exerciseClock.running && (
            <Chip
              label={`${dayLbl(clockDisplay.op_day)}  ·  ${clockDisplay.elapsed}`}
              size="small" color="warning"
              sx={{ fontFamily: 'monospace' }}
            />
          )}
        </Paper>

        {/* Waiting state — no inject fired yet */}
        {!activeStudentInjectType && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <CircularProgress size={36} sx={{ mb: 2, color: 'text.disabled' }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('tab7.student_waiting')}
            </Typography>
            <Typography variant="body2" color="text.disabled">
              {t('tab7.student_waiting_subtitle')}
            </Typography>
          </Box>
        )}

        {/* SF-5: Decision history — all injects decided so far, oldest first */}
        {(() => {
          const decided = Object.entries(injectStateMap)
            .filter(([, s]) => s?.decision != null)
            .sort((a, b) => {
              const ta = a[1].decided_at ? new Date(a[1].decided_at).getTime() : 0;
              const tb = b[1].decided_at ? new Date(b[1].decided_at).getTime() : 0;
              return ta - tb;
            })
            .map(([type, s]) => {
              const cfg = STUDENT_INJECT_CONFIG[type];
              const opts = type === 'CIV'
                ? (cfg.options[s.scenario_type || 'INDIVIDUAL'] || cfg.options.INDIVIDUAL)
                : cfg.options;
              const opt  = opts?.find(o => o.key === s.decision);
              const dk   = opt?.displayKey || opt?.key || s.decision;
              return {
                type,
                title: cfg.title,
                label: opt ? `${dk} — ${opt.label}` : s.decision,
                notes: s.decision_notes || '',
              };
            });
          if (!decided.length) return null;
          return (
            <Box sx={{ mb: 3 }}>
              <Typography variant="overline" color="text.secondary" fontWeight={700}
                display="block" sx={{ mb: 1 }}>
                {t('tab7.student_decisions_session')}
              </Typography>
              {decided.map((item, i) => (
                <Paper key={item.type} variant="outlined"
                  sx={{ p: 1.5, mb: 1, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Chip label={i + 1} size="small" color="success"
                    sx={{ mt: 0.25, flexShrink: 0, fontWeight: 700, minWidth: 28 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary"
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block' }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="success.dark">
                      {item.label}
                    </Typography>
                    {item.notes && (
                      <Typography variant="caption" color="text.secondary"
                        display="block" sx={{ mt: 0.25, fontStyle: 'italic' }}>
                        "{item.notes}"
                      </Typography>
                    )}
                  </Box>
                  <CheckCircleIcon color="success" fontSize="small" sx={{ flexShrink: 0, mt: 0.25 }} />
                </Paper>
              ))}
              <Divider sx={{ mt: 2, mb: 2 }} />
            </Box>
          );
        })()}

        {/* Active inject card */}
        {config && (
          <Box>
            {/* Inject header */}
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1, mb: 2,
              p: 1.5, borderRadius: 2, border: '2px solid',
              bgcolor:     currentDecision ? 'success.50' : 'warning.50',
              borderColor: currentDecision ? 'success.main' : 'warning.main',
            }}>
              <Typography variant="h6" fontWeight={700}
                color={currentDecision ? 'success.dark' : 'warning.dark'}>
                ⚡ {config.title}
              </Typography>
              {exerciseClock.running && (
                <Chip label={`${dayLbl(clockDisplay.op_day)}`} size="small" color="warning" />
              )}
              <Box sx={{ flex: 1 }} />
              {currentDecision && selectedOpt && (
                <Chip
                  label={`✓ ${getDisplayKey(selectedOpt)} submitted`}
                  color="success" size="small" sx={{ fontWeight: 700 }}
                />
              )}
            </Box>

            {/* Situation */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2.5, bgcolor: 'grey.50' }}>
              <Typography variant="overline" color="text.secondary" display="block"
                sx={{ mb: 0.5, fontWeight: 600 }}>
                {t('tab7.student_situation')}
              </Typography>
              <Typography variant="body1">{config.situation}</Typography>
            </Paper>

            {currentDecision ? (
              /* Locked — decision already submitted */
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={700}>
                  {t('tab7.student_decision_submitted', { label: selectedOpt ? `${getDisplayKey(selectedOpt)} — ${selectedOpt.label}` : currentDecision })}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t('tab7.student_waiting_trainer')}
                </Typography>
              </Alert>
            ) : (
              /* Option cards — tap to submit immediately */
              <>
                <Typography variant="overline" color="text.secondary" display="block"
                  sx={{ mb: 1, fontWeight: 600 }}>
                  {t('tab7.student_your_decision')}
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {options.map((opt) => (
                    <Grid size={{ xs: 12, sm: 6 }} key={opt.key}>
                      <Paper
                        variant="outlined"
                        onClick={() => handleStudentDecisionSubmit(activeStudentInjectType, opt.key)}
                        sx={{
                          p: 2, cursor: 'pointer', height: '100%',
                          border: '2px solid', borderColor: 'divider', borderRadius: 2,
                          transition: 'border-color 0.15s, box-shadow 0.15s, background-color 0.15s',
                          '&:hover': { borderColor: 'primary.main', boxShadow: 3, bgcolor: 'primary.50' },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={getDisplayKey(opt)}
                            size="small" color="primary"
                            sx={{ fontWeight: 700, minWidth: 28, flexShrink: 0 }}
                          />
                          <Typography variant="subtitle2" fontWeight={600}>
                            {opt.label}
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>

                {/* Notes — saved to inject state decision_notes field */}
                <TextField
                  fullWidth multiline rows={2}
                  label={t('tab7.notes_optional')}
                  placeholder="Record your reasoning before deciding…"
                  size="small"
                  value={injectState?.decision_notes || ''}
                  onChange={(e) => {
                    const noteSetterMap = {
                      MROE:          [setMroeInject,           TAB7_STORAGE_KEYS.MROE_INJECT],
                      CIV:           [setCivInject,             TAB7_STORAGE_KEYS.CIV_INJECT],
                      MASCAL:        [setMascalInject,          TAB7_STORAGE_KEYS.MASCAL_INJECT],
                      MEDEVAC:       [setMedevacLossInject,     TAB7_STORAGE_KEYS.MEDEVAC_LOSS_INJECT],
                      MTF:           [setMtfDisplaceInject,     TAB7_STORAGE_KEYS.MTF_DISPLACE_INJECT],
                      LOC:           [setLocInterdictInject,    TAB7_STORAGE_KEYS.LOC_INTERDICT_INJECT],
                      R2E:           [setR2eLossInject,         TAB7_STORAGE_KEYS.R2E_LOSS_INJECT],
                      COUNTERATTACK: [setCounterattackInject,   TAB7_STORAGE_KEYS.COUNTERATTACK_INJECT],
                      WEATHER:       [setWeatherInject,         TAB7_STORAGE_KEYS.WEATHER_INJECT],
                      CONVOY:        [setConvoyInject,          TAB7_STORAGE_KEYS.CONVOY_INJECT],
                    };
                    const [setter] = noteSetterMap[activeStudentInjectType] || [];
                    if (setter) setter(prev => ({ ...prev, decision_notes: e.target.value }));
                  }}
                  sx={{ mb: 2 }}
                />
              </>
            )}
          </Box>
        )}
      </Box>
    );
  }

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <Box sx={{ p: 2, overflowX: 'hidden', boxSizing: 'border-box' }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <TimelineIcon color="primary" fontSize="large" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight="bold">
            {t('tab7.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('tab7.subtitle')}
          </Typography>
        </Box>
        {/* COA-BASELINE-001: Exercise baseline selector */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
          <Typography variant="caption" color="text.secondary"
            sx={{ fontWeight: 700, fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {t('tab7.exercise_baseline')}
          </Typography>
          <ToggleButtonGroup value={coaBaselineSlot} exclusive size="small"
            onChange={(_, v) => handleCoaBaselineChange(v)}>
            <ToggleButton value="LIVE"  sx={{ fontSize: '0.65rem', py: 0.25, px: 1 }}>{t('tab7.baseline_live')}</ToggleButton>
            <ToggleButton value="COA_A" sx={{ fontSize: '0.65rem', py: 0.25, px: 1 }}
              disabled={!availableCoaSlots.COA_A}>CoA A</ToggleButton>
            <ToggleButton value="COA_B" sx={{ fontSize: '0.65rem', py: 0.25, px: 1 }}
              disabled={!availableCoaSlots.COA_B}>CoA B</ToggleButton>
          </ToggleButtonGroup>
          {coaBaselineSlot !== 'LIVE' && availableCoaSlots[coaBaselineSlot] && (
            <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.6rem' }}>
              {new Date(availableCoaSlots[coaBaselineSlot].saved_at).toLocaleString()}
            </Typography>
          )}
        </Box>
        <Chip label="V4 Partial" color="info" icon={<ConstructionIcon />} size="small" />
        {/* Baseline status + load button */}
        {baseline ? (
          <Chip
            label={t('tab7.baseline_loaded', { name: baseline.name })}
            color="success"
            size="small"
            icon={<CheckCircleIcon />}
            sx={{ fontSize: '0.72rem' }}
          />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={<GetAppIcon />}
              onClick={handleLoadTab5Baseline}
            >
              {t('tab7.load_tab5_baseline')}
            </Button>
            {baselineLoadError && (
              <Typography variant="caption" color="error">{baselineLoadError}</Typography>
            )}
          </Box>
        )}
      </Box>

      {/* P2-S2: Trainer panel — only rendered for TRAINER role, hidden entirely from students/guests */}
      {userRole === 'TRAINER' && (
        <Tab7TrainerPanel
          scenario={scenario}
          onExerciseSelected={setTrainerSessionCode}  // P2-S4: connects realtime channel
        />
      )}

      {/* Improvement 4: sessionStorage loss warning — approved plan exists in localStorage
          but results were not re-hydrated (browser closed and reopened). */}
      {baseline?.source === 'approved' && !baseline?.results && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Exercise baseline results not available.</strong>{' '}
          The approved plan scenario was restored but calculation results were lost when the browser was closed.
          Go to Tab 6, re-approve the planning basis, then return here.
        </Alert>
      )}

      {/* Session C — Scenario Level Selector */}
      {sessionHistory.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: exerciseLevel ? 'primary.main' : 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <SchoolIcon color={exerciseLevel ? 'primary' : 'action'} />
            <Typography variant="subtitle2" fontWeight="bold">
              Exercise Level
            </Typography>
            {exerciseLevel && (
              <Chip
                size="small"
                label={LEVEL_PRESETS[exerciseLevel]?.label}
                color={LEVEL_PRESETS[exerciseLevel]?.color}
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {[1, 2, 3, 4].map(lvl => {
              const preset = LEVEL_PRESETS[lvl];
              const selected = exerciseLevel === lvl;
              return (
                <Paper
                  key={lvl}
                  variant={selected ? 'elevation' : 'outlined'}
                  elevation={selected ? 3 : 0}
                  onClick={() => handleSelectLevel(lvl)}
                  sx={{
                    p: 1.5, flex: '1 1 180px', cursor: 'pointer', userSelect: 'none',
                    borderColor: selected ? `${preset.color}.main` : 'divider',
                    borderWidth: selected ? 2 : 1,
                    transition: 'all 0.15s',
                    '&:hover': { borderColor: `${preset.color}.main`, boxShadow: 1 },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <Chip
                      size="small"
                      label={`Level ${lvl}`}
                      color={preset.color}
                      sx={{ fontSize: '0.65rem', height: 18 }}
                    />
                  </Box>
                  <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 0.25 }}>
                    {preset.label.split(' — ')[1]}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', lineHeight: 1.3 }}>
                    {preset.description}
                  </Typography>
                  {preset.script.length > 0 && (
                    <Box sx={{ mt: 0.75, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {preset.script.map((s, i) => (
                        <Chip
                          key={i}
                          size="small"
                          label={`${s.inject_type} ${dayLbl(s.op_day)}`}
                          variant="outlined"
                          sx={{ fontSize: '0.6rem', height: 16 }}
                        />
                      ))}
                    </Box>
                  )}
                </Paper>
              );
            })}
          </Box>
          {exerciseLevel && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {`Clock compression set to ${LEVEL_PRESETS[exerciseLevel].compression_min} min/day. Inject script pre-populated. Start the exercise clock to begin.`}
            </Typography>
          )}
        </Paper>
      ) : (
        exerciseLevel && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <SchoolIcon fontSize="small" color="primary" />
            <Chip
              size="small"
              label={LEVEL_PRESETS[exerciseLevel]?.label}
              color={LEVEL_PRESETS[exerciseLevel]?.color}
              sx={{ fontSize: '0.7rem', height: 20 }}
            />
            <Typography variant="caption" color="text.secondary">
              Exercise in progress — level locked
            </Typography>
          </Box>
        )
      )}

      {/* F4 — Exercise Clock */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <TimerIcon color={exerciseClock.running ? 'secondary' : 'action'} />
          {/* Current day + elapsed + Zulu date (CLOCK-EXT-001) */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
              <Chip
                label={PLANNING_WINDOWS[Math.min(5, Math.max(0, clockDisplay.op_day - 1))]}
                size="small"
                color={WINDOW_COLOR[getPlanningWindow(clockDisplay.op_day, clockDisplay.op_day)] || 'primary'}
                sx={{ fontSize: '0.65rem', height: 20 }}
              />
              <Typography variant="h6" fontWeight="bold" sx={{ fontFamily: 'monospace', color: exerciseClock.running ? 'secondary.main' : 'text.primary' }}>
                {dayLbl(clockDisplay.op_day)}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                {clockDisplay.elapsed}
              </Typography>
            </Box>
            {exerciseCalendarDate && (
              <Typography variant="caption"
                sx={{ fontFamily: 'monospace', color: 'primary.main', fontSize: '0.72rem', ml: 0.25 }}>
                {exerciseCalendarDate}
              </Typography>
            )}
          </Box>

          <Box sx={{ flex: 1 }} />

          {/* Trainer step controls (only when stopped) */}
          {!exerciseClock.running && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title={t('tab7.clock_step_back')}>
                <span>
                  <IconButton size="small" onClick={() => handleClockStep(-1)} disabled={clockDisplay.op_day <= 1}>
                    <NavigateBeforeIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('tab7.clock_step_forward')}>
                <span>
                  <IconButton size="small" onClick={() => handleClockStep(1)}>
                    <NavigateNextIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('tab7.clock_jump_tooltip')}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarTodayIcon fontSize="small" color="action" />
                  <TextField
                    size="small"
                    type="number"
                    label={t('tab7.clock_jump_label')}
                    sx={{ width: 80 }}
                    inputProps={{ min: 1, max: scenario?.operation_parameters?.duration_days ?? opDays }}
                    defaultValue={clockDisplay.op_day}
                    key={clockDisplay.op_day}
                    onBlur={e => handleClockJump(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleClockJump(e.target.value); }}
                  />
                </Box>
              </Tooltip>
            </Box>
          )}

          {/* Compression selector */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>{t('tab7.clock_day_equals')}</Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={exerciseClock.compression_min}
              onChange={handleCompressionChange}
              disabled={exerciseClock.running}
            >
              {[
                { val: 15,   label: '15m' },
                { val: 30,   label: '30m' },
                { val: 60,   label: '1h'  },
                { val: 120,  label: '2h'  },
                { val: 360,  label: '6h'  },
                { val: 720,  label: '12h' },
                { val: 1440, label: '24h' },
              ].map(opt => (
                <ToggleButton key={opt.val} value={opt.val} sx={{ py: 0.25, px: 0.75, fontSize: '0.7rem' }}>
                  {opt.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {/* R2-09: exercise start date and Zulu time = the Tab 2 operation start; set once, locked after the clock has started */}
          <TextField
            size="small"
            type="date"
            label={t('tab7.clock_start_date')}
            value={_scenarioStartDate}
            onChange={e => handleExerciseStartChange(e.target.value, undefined)}
            sx={{ width: 160 }}
            InputLabelProps={{ shrink: true }}
            disabled={!!exerciseClock.started_at || userRole === 'STUDENT'}
            helperText={exerciseClock.started_at ? t('tab7.clock_start_locked') : undefined}
          />
          {_scenarioStartDate && (
            <TextField
              size="small"
              type="time"
              label={t('tab7.clock_start_time')}
              value={_scenarioStartTimeZ}
              onChange={e => handleExerciseStartChange(undefined, e.target.value)}
              sx={{ width: 120 }}
              InputLabelProps={{ shrink: true }}
              disabled={!!exerciseClock.started_at || userRole === 'STUDENT'}
            />
          )}
          {_scenarioStartDate && _scenarioDuration >= 1 && (
            <Typography variant="caption" color="text.secondary">
              {t('tab7.clock_end_date', { end: formatZulu(endInstant(_scenarioStartDate, _scenarioStartTimeZ, _scenarioDuration)) })}
            </Typography>
          )}

          {/* Start / Stop */}
          <Button
            size="small"
            variant={exerciseClock.running ? 'contained' : 'outlined'}
            color={exerciseClock.running ? 'error' : 'secondary'}
            startIcon={exerciseClock.running ? <StopIcon /> : <PlayArrowIcon />}
            onClick={handleClockToggle}
          >
            {exerciseClock.running ? 'Pause' : 'Run'}
          </Button>
        </Box>
      </Paper>

      {/* ══ Exercise Projections — Planning Horizon Grid (Session A) ═══════ */}
      <Accordion
        expanded={phaseExpanded.projections}
        onChange={(_, o) => setPhaseExpanded(p => ({ ...p, projections: o }))}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <ShowChartIcon color="primary" />
            <Typography fontWeight="bold">{t('tab7.exercise_projections')}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              {t('tab7.exercise_projections_caption')}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Chip label={`${dayLbl(clockDisplay.op_day)}`} size="small" color="primary" variant="outlined" />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 1 }}>
          {renderExerciseProjections()}
        </AccordionDetails>
      </Accordion>

      {/* BUG-84-020: developer status banner removed — not user-facing */}

      {/* ══ Phase 1 — Tactical Map ══════════════════════════════════════════ */}
      <Accordion
        expanded={phaseExpanded.map}
        onChange={(_, o) => setPhaseExpanded(p => ({ ...p, map: o }))}
        sx={{ mb: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <MapIcon color="primary" />
            <Typography fontWeight="bold">{t('tab7.tactical_map')}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              {t('tab7.tactical_map_caption')}
            </Typography>
            <Chip
              label={t('tab7.display_only')}
              size="small"
              color="warning"
              variant="outlined"
              sx={{ fontSize: '0.65rem', height: 20, ml: 0.5 }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 1 }}>
          {renderSchematicMap()}
        </AccordionDetails>
      </Accordion>

      {/* ══ Phase 2 — Daily Reporting ════════════════════════════════════════ */}
      <Accordion
        expanded={phaseExpanded.daily}
        onChange={(_, o) => setPhaseExpanded(p => ({ ...p, daily: o }))}
        sx={{ mb: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <TimelineIcon color="warning" />
            <Typography fontWeight="bold">{t('tab7.daily_operational_reporting')}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              {t('tab7.daily_reporting_caption')}
            </Typography>
            <Box sx={{ flex: 1 }} />
            {Object.keys(dailyLog).length > 0 && (
              <Chip label={`${Object.keys(dailyLog).length} day(s) logged`} size="small" color="info" sx={{ fontSize: '0.68rem' }} />
            )}
            {casrepLog.filter(c => c.status === 'OPEN').length > 0 && (
              <Chip label={`${casrepLog.filter(c => c.status === 'OPEN').length} open CASREP`} size="small" color="warning" sx={{ fontSize: '0.68rem' }} />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 1 }}>
          {renderDailyReporting()}
        </AccordionDetails>
      </Accordion>

      {/* ══ Phase 3 — Battlefield Reports (moved from Tab 6) ═════════════════ */}
      <Accordion
        expanded={phaseExpanded.reports}
        onChange={(_, o) => setPhaseExpanded(p => ({ ...p, reports: o }))}
        sx={{ mb: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReportProblemIcon color="error" />
            <Typography fontWeight="bold">{t('tab7.battlefield_reports')}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              {t('tab7.battlefield_caption')}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 1 }}>
          {renderBattlefieldReports()}
        </AccordionDetails>
      </Accordion>

      {/* SF-3: Student sees simplified responder panel; trainer sees full inject accordions (Phases 4–11) */}
      {userRole === 'STUDENT' ? renderStudentView() : (<>

      {/* ══ Phase 4 — MRoE Dilemma Inject (V4-TAB7-002) ══════════════════════ */}
      <Accordion
        expanded={phaseExpanded.inject}
        onChange={(_, o) => setPhaseExpanded(p => ({ ...p, inject: o }))}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <GavelIcon color="error" />
            <Typography fontWeight="bold">{t('tab7.injects.mroe_title')}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              GC III Art. 12 · three decision options · consequence analysis
            </Typography>
            <Box sx={{ flex: 1 }} />
            {mroeInject.decision && (
              <Chip
                label={`Decision: Opt ${mroeInject.decision}`}
                size="small"
                color={mroeInject.decision === 'A' ? 'success' : mroeInject.decision === 'B' ? 'warning' : 'info'}
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 1 }}>
          {renderMRoEInject()}
        </AccordionDetails>
      </Accordion>

      {/* Phase 5 — Civilian Encounter Inject (V4-TAB7-003) */}
      <Accordion
        expanded={phaseExpanded.civInject}
        onChange={(_, o) => setPhaseExpanded(p => ({ ...p, civInject: o }))}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <PeopleIcon color="warning" />
            <Typography fontWeight="bold">{t('tab7.injects.civilian_title')}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              GC IV Art. 16 · individual and mass scenarios · doctrine teaching
            </Typography>
            <Box sx={{ flex: 1 }} />
            {civInject.decision && (
              <Chip
                label={`Decision: Opt ${civInject.decision}`}
                size="small"
                color={
                  (civInject.scenario_type === 'INDIVIDUAL' && civInject.decision === 'C') ||
                  (civInject.scenario_type === 'MASS'       && civInject.decision === 'D')
                    ? 'error'
                    : (civInject.decision === 'A' ? 'success' : 'warning')
                }
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 1 }}>
          {renderCivilianInject()}
        </AccordionDetails>
      </Accordion>

      {/* Phase 6 -- MASCAL Event Inject (V4-TAB7-004) */}
      <Accordion
        expanded={phaseExpanded.mascalInject}
        onChange={(_, o) => setPhaseExpanded(p => ({ ...p, mascalInject: o }))}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <FlashOnIcon color="error" />
            <Typography fontWeight="bold">{t('tab7.injects.mascal_title')}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              AJMedP-1 ss 3.19 · artillery / IED / airstrike · protocol teaching
            </Typography>
            <Box sx={{ flex: 1 }} />
            {mascalInject.decision && (
              <Chip
                label={`Decision: Opt ${mascalInject.decision}`}
                size="small"
                color={mascalInject.decision === 'B' ? 'warning' : 'success'}
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 1 }}>
          {renderMASCALInject()}
        </AccordionDetails>
      </Accordion>

      {/* Phase 7 -- MEDEVAC Asset Loss Inject (V4-TAB7-005) */}
      <Accordion
        expanded={phaseExpanded.medevacLossInject}
        onChange={(_, o) => setPhaseExpanded(p => ({ ...p, medevacLossInject: o }))}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <AirplanemodeActiveIcon color="warning" />
            <Typography fontWeight="bold">{t('tab7.injects.medevac_loss_title')}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              AJMedP-2 golden hour · sortie deficit · CASEVAC substitution
            </Typography>
            <Box sx={{ flex: 1 }} />
            {medevacLossInject.decision && (
              <Chip
                label={`Decision: Opt ${medevacLossInject.decision}`}
                size="small"
                color={medevacLossInject.decision === 'B' ? 'warning' : 'success'}
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 1 }}>
          {renderMEDEVACLossInject()}
        </AccordionDetails>
      </Accordion>

      {/* Phase 8 -- MTF Displacement Inject (V4-TAB7-006) */}
      <Accordion
        expanded={phaseExpanded.mtfDisplaceInject}
        onChange={(_, o) => setPhaseExpanded(p => ({ ...p, mtfDisplaceInject: o }))}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <ReportProblemIcon color="error" />
            <Typography fontWeight="bold">{t('tab7.injects.mtf_displace_title')}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              AJMedP-1 ss 4.8 · coverage gap · cold chain · pre-notification
            </Typography>
            <Box sx={{ flex: 1 }} />
            {mtfDisplaceInject.decision && (
              <Chip
                label={`Decision: Opt ${mtfDisplaceInject.decision}`}
                size="small"
                color={mtfDisplaceInject.decision === 'D' ? 'error' : mtfDisplaceInject.decision === 'C' ? 'warning' : 'success'}
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 1 }}>
          {renderMTFDisplaceInject()}
        </AccordionDetails>
      </Accordion>

      {/* Phase 9 -- LOC Interdiction Inject (V4-TAB7-007) */}
      <Accordion
        expanded={phaseExpanded.locInterdictInject}
        onChange={(_, o) => setPhaseExpanded(p => ({ ...p, locInterdictInject: o }))}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <WarningAmberIcon color="error" />
            <Typography fontWeight="bold">{t('tab7.injects.loc_interdict_title')}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              ATP-4.02 · blood DOS clock · air resupply · rationing protocol
            </Typography>
            <Box sx={{ flex: 1 }} />
            {locInterdictInject.decision && (
              <Chip
                label={`Decision: Opt ${locInterdictInject.decision}`}
                size="small"
                color={locInterdictInject.decision === 'C' ? 'error' : 'success'}
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 1 }}>
          {renderLOCInterdictInject()}
        </AccordionDetails>
      </Accordion>

      {/* Phase 10 -- R2E Loss Inject / Branch Plan Framework (TAB7-R2E) */}
      <Accordion
        expanded={phaseExpanded.r2eLossInject}
        onChange={(_, o) => setPhaseExpanded(p => ({ ...p, r2eLossInject: o }))}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <ReportProblemIcon color="error" />
            <Typography fontWeight="bold">{t('tab7.injects.r2e_loss_title')}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              AJMedP-1 cascade · branch plan · golden hour breach · STRATEVAC uplift
            </Typography>
            <Box sx={{ flex: 1 }} />
            {r2eLossInject.decision && (
              <Chip
                label={`Decision: ${r2eLossInject.decision === 'R2B_SURGE_STRATEVAC' ? 'Opt A' : r2eLossInject.decision === 'ADJACENT_R2E' ? 'Opt B' : r2eLossInject.decision === 'CONTINUE_R2B_ROUTINE' ? 'Opt C' : 'Opt D'}`}
                size="small"
                color={r2eLossInject.decision === 'CONTINUE_R2B_ROUTINE' ? 'error' : r2eLossInject.decision === 'HALT_OPERATIONS' ? 'warning' : 'success'}
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 1 }}>
          {renderR2eLossInject()}
        </AccordionDetails>
      </Accordion>

      {/* Phase 12 — Enemy Counterattack Inject (TAB7-CA) */}
      <Accordion
        expanded={phaseExpanded.counterattackInject}
        onChange={(_, o) => setPhaseExpanded(p => ({ ...p, counterattackInject: o }))}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <FlashOnIcon color="error" />
            <Typography fontWeight="bold">{t('tab7.phase12_title')}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              AJMedP-1 ss 3.15 · surge response · FRT pre-positioning · STRATEVAC uplift
            </Typography>
            <Box sx={{ flex: 1 }} />
            {counterattackInject.decision && (
              <Chip
                label={`Decision: Opt ${counterattackInject.decision}`}
                size="small"
                color={counterattackInject.decision === 'B' ? 'warning' : 'success'}
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 1 }}>
          {renderCounterattackInject()}
        </AccordionDetails>
      </Accordion>

      {/* Phase 13 — Weather Change Inject (TAB7-WX) */}
      <Accordion
        expanded={phaseExpanded.weatherInject}
        onChange={(_, o) => setPhaseExpanded(p => ({ ...p, weatherInject: o }))}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <WarningAmberIcon color="info" />
            <Typography fontWeight="bold">{t('tab7.phase13_title')}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              AJMedP-2 Ch.6 · MEDEVAC degradation · ground CASEVAC · DNBI spike
            </Typography>
            <Box sx={{ flex: 1 }} />
            {weatherInject.decision && (
              <Chip
                label={`Decision: Opt ${weatherInject.decision}`}
                size="small"
                color={weatherInject.decision === 'D' ? 'secondary' : weatherInject.decision === 'A' ? 'warning' : 'success'}
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 1 }}>
          {renderWeatherInject()}
        </AccordionDetails>
      </Accordion>

      {/* Phase 14 — Supply Convoy Ambush Inject (TAB7-CV) */}
      <Accordion
        expanded={phaseExpanded.convoyInject}
        onChange={(_, o) => setPhaseExpanded(p => ({ ...p, convoyInject: o }))}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <ReportProblemIcon color="warning" />
            <Typography fontWeight="bold">{t('tab7.phase14_title')}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              ATP-4.02 · blood DOS clock · air resupply · HNS · rationing protocol
            </Typography>
            <Box sx={{ flex: 1 }} />
            {convoyInject.decision && (
              <Chip
                label={`Decision: Opt ${convoyInject.decision}`}
                size="small"
                color={convoyInject.decision === 'D' ? 'error' : convoyInject.decision === 'B' ? 'warning' : 'success'}
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 1 }}>
          {renderConvoyInject()}
        </AccordionDetails>
      </Accordion>

      {/* ══ Phase 11 — Countermeasures (Session A) ══════════════════════════ */}
      <Accordion
        expanded={phaseExpanded.countermeasures}
        onChange={(_, o) => setPhaseExpanded(p => ({ ...p, countermeasures: o }))}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <HealthAndSafetyIcon color="success" />
            <Typography fontWeight="bold">{t('tab7.phase11_counter_title')}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              Proactive actions &middot; Blood / Fleet / Surgical / AXP / MTF &middot; AJMedP-1
            </Typography>
            <Box sx={{ flex: 1 }} />
            {Object.values(countermeasures).some(v => v.active) && (
              <Chip
                label={`${Object.values(countermeasures).filter(v => v.active).length} active`}
                size="small"
                color="success"
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 1 }}>
          {renderCountermeasures()}
        </AccordionDetails>
      </Accordion>

      </>)} {/* end SF-3: trainer-only inject section */}

      {/* IMP-014 — Daily Medical SITREP */}
      <Accordion
        expanded={phaseExpanded.sitrep}
        onChange={(_, o) => setPhaseExpanded(p => ({ ...p, sitrep: o }))}
        sx={{ mb: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssessmentIcon color="primary" />
            <Typography fontWeight="bold">{t('tab7.daily_sitrep')}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t('tab7.daily_sitrep_caption')}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 1 }}>
          {(() => {
            const res  = baseline?.results ?? getCalculatedBaseline()?.results ?? null;
            const snap = baseline?.snapshot ?? getCalculatedBaseline()?.scenario ?? null;
            const opDuration = snap?.operation_parameters?.operation_duration ?? 10;

            if (!res) {
              return (
                <Alert severity="warning">
                  No baseline results loaded. Load a Tab 5 baseline first to generate a SITREP.
                </Alert>
              );
            }

            const sitrep = buildSitrep(res, snap, sitrepDay);

            // Sub-unit lists for selectors
            const divOptions = sitrep.divisionRows;
            const bdeOptions = sitrep.brigadeRows;

            // Resolve active sub-unit
            const activeDivId = sitrepSubUnit || divOptions[0]?.id || '';
            const activeBdeId = sitrepSubUnit || bdeOptions[0]?.id || '';

            const statusColor = (s) =>
              s === 'MASCAL' || s === 'CRITICAL' ? 'error.main' :
              s === 'WARNING' ? 'warning.main' : 'success.main';

            return (
              <Box>
                {/* Controls */}
                <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>{t('tab7.op_day')}</InputLabel>
                      <Select
                        value={sitrepDay}
                        label={t('tab7.op_day')}
                        onChange={e => { setSitrepDay(Number(e.target.value)); setSitrepData(null); }}
                      >
                        {Array.from({ length: opDuration }, (_, i) => i + 1).map(d => (
                          <MenuItem key={d} value={d}>{dayLbl(d)} (Day {d})</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <InputLabel>{t('tab7.echelon')}</InputLabel>
                      <Select
                        value={sitrepEchelon}
                        label={t('tab7.echelon')}
                        onChange={e => { setSitrepEchelon(e.target.value); setSitrepSubUnit(''); setSitrepData(null); }}
                      >
                        <MenuItem value="CORPS">Corps</MenuItem>
                        <MenuItem value="DIVISION">Division</MenuItem>
                        <MenuItem value="BRIGADE">Brigade</MenuItem>
                      </Select>
                    </FormControl>
                    {sitrepEchelon === 'DIVISION' && (
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>{t('tab7.division')}</InputLabel>
                        <Select
                          value={sitrepSubUnit || divOptions[0]?.id || ''}
                          label={t('tab7.division')}
                          onChange={e => { setSitrepSubUnit(e.target.value); setSitrepData(null); }}
                        >
                          {divOptions.map(d => (
                            <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    {sitrepEchelon === 'BRIGADE' && (
                      <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel>{t('tab7.brigade')}</InputLabel>
                        <Select
                          value={sitrepSubUnit || bdeOptions[0]?.id || ''}
                          label={t('tab7.brigade')}
                          onChange={e => { setSitrepSubUnit(e.target.value); setSitrepData(null); }}
                        >
                          {bdeOptions.map(b => (
                            <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AssessmentIcon />}
                      onClick={() => setSitrepData(sitrep)}
                    >
                      Generate SITREP
                    </Button>
                  </Box>
                </Paper>

                {/* Output */}
                {sitrepData && (() => {
                  const sd = sitrepData;

                  // Rows to show depending on echelon
                  let casualtyRows = [];
                  let medRows = [];
                  let title = '';

                  if (sitrepEchelon === 'CORPS') {
                    title = `Corps — Medical SITREP — ${dayLbl(sitrepDay)}`;
                    casualtyRows = sd.divisionRows;
                    medRows = sd.r2eDivs.map(d => ({
                      unit: d.division_name, role: 'R2E',
                      beds: d.beds, occ: d.occ, status: d.status, admissions: d.admissions,
                    }));
                    if (sd.r3) {
                      medRows.push({
                        unit: 'Corps R3', role: 'R3',
                        beds: sd.r3.beds ?? res?.mascal_analysis?.total_r3_beds ?? 0,
                        occ: sd.r3.occupancy_percent ?? 0,
                        status: sd.r3.status ?? 'NORMAL',
                        admissions: sd.r3.admissions ?? 0,
                      });
                    }
                  } else if (sitrepEchelon === 'DIVISION') {
                    const div = sd.divisionRows.find(d => d.id === activeDivId) || sd.divisionRows[0];
                    title = `${div?.name || 'Division'} — Medical SITREP — ${dayLbl(sitrepDay)}`;
                    casualtyRows = div?.brigades || [];
                    const r2eDivEntry = sd.r2eDivs.find(d => d.division_id === activeDivId);
                    medRows = (div?.brigades || []).map(br => {
                      const r2b = sd.r2Bdes.find(b => b.brigade_id === br.id);
                      return { unit: br.name, role: 'R2', beds: r2b?.beds ?? 0, occ: r2b?.occ ?? 0, status: r2b?.status ?? 'NORMAL', admissions: null };
                    });
                    if (r2eDivEntry) {
                      medRows.push({ unit: `${div?.name} R2E`, role: 'R2E', beds: r2eDivEntry.beds, occ: r2eDivEntry.occ, status: r2eDivEntry.status, admissions: r2eDivEntry.admissions });
                    }
                  } else {
                    const bde = sd.brigadeRows.find(b => b.id === activeBdeId) || sd.brigadeRows[0];
                    title = `${bde?.name || 'Brigade'} — Medical SITREP — ${dayLbl(sitrepDay)}`;
                    casualtyRows = bde ? [bde] : [];
                    const r2b = bde ? sd.r2Bdes.find(b => b.brigade_id === bde.id) : null;
                    medRows = r2b ? [{ unit: `${bde?.name} R2`, role: 'R2', beds: r2b.beds, occ: r2b.occ, status: r2b.status, admissions: null }] : [];
                  }

                  const totalCas = casualtyRows.reduce((a, r) => ({
                    kia: a.kia + r.kia, wia: a.wia + r.wia,
                    dow: a.dow + r.dow, dnbi: a.dnbi + r.dnbi, rtd: a.rtd + r.rtd,
                    t1:  a.t1 + r.t1,
                  }), { kia: 0, wia: 0, dow: 0, dnbi: 0, rtd: 0, t1: 0 });

                  return (
                    <Box>
                      {/* Header */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <Typography variant="subtitle1" fontWeight="bold">{title}</Typography>
                        <Chip label={`Generated ${new Date().toLocaleTimeString()}`} size="small" variant="outlined" />
                      </Box>

                      {/* G1 — Casualty Summary */}
                      <Typography variant="caption" color="primary" fontWeight="bold" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                        G1 — Casualty Summary (this day)
                      </Typography>
                      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, mt: 0.5 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'primary.main' }}>
                              {['Unit', 'KIA', 'DOW', 'WIA', 'DNBI', 'Total', 'RTD', 'T1 Surgical'].map(h => (
                                <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold', py: 0.75 }}>{h}</TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {casualtyRows.map((r, i) => (
                              <TableRow key={i} hover>
                                <TableCell sx={{ fontWeight: 500 }}>{r.name}</TableCell>
                                <TableCell sx={{ color: r.kia > 0 ? 'error.main' : 'inherit' }}>{r.kia}</TableCell>
                                <TableCell sx={{ color: r.dow > 0 ? 'error.main' : 'inherit' }}>{r.dow}</TableCell>
                                <TableCell>{r.wia}</TableCell>
                                <TableCell>{r.dnbi}</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>{r.kia + r.wia + r.dow + r.dnbi}</TableCell>
                                <TableCell sx={{ color: 'success.main' }}>{r.rtd}</TableCell>
                                <TableCell sx={{ color: r.t1 > 0 ? 'warning.main' : 'inherit' }}>{r.t1}</TableCell>
                              </TableRow>
                            ))}
                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                              <TableCell sx={{ fontWeight: 'bold' }}>TOTAL</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', color: 'error.main' }}>{totalCas.kia}</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', color: 'error.main' }}>{totalCas.dow}</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>{totalCas.wia}</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>{totalCas.dnbi}</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>{totalCas.kia + totalCas.wia + totalCas.dow + totalCas.dnbi}</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', color: 'success.main' }}>{totalCas.rtd}</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', color: 'warning.main' }}>{totalCas.t1}</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {/* G4 — Medical System Status */}
                      <Typography variant="caption" color="primary" fontWeight="bold" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                        G4 — Medical System Status
                      </Typography>
                      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, mt: 0.5 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.800' }}>
                              {['Facility', 'Role', 'Beds', 'Occupancy %', 'Status'].map(h => (
                                <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold', py: 0.75 }}>{h}</TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {medRows.length > 0 ? medRows.map((r, i) => (
                              <TableRow key={i} hover>
                                <TableCell sx={{ fontWeight: 500 }}>{r.unit}</TableCell>
                                <TableCell><Chip label={r.role} size="small" variant="outlined" /></TableCell>
                                <TableCell>{r.beds}</TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ color: statusColor(r.status), fontWeight: 'bold' }}>
                                      {r.occ.toFixed(0)}%
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>{sitrepStatusChip(r.status)}</TableCell>
                              </TableRow>
                            )) : (
                              <TableRow><TableCell colSpan={5} align="center">No facility data for this echelon</TableCell></TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {/* G3 — Operational Flags */}
                      <Typography variant="caption" color="primary" fontWeight="bold" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                        G3 — Operational Flags
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5, mb: 1.5 }}>
                        {casualtyRows.some(r => r.mascal === 'MASCAL' || r.mascal === 'CRITICAL') && (
                          <Chip icon={<ReportProblemIcon />} label="MASCAL — at least 1 facility critical" color="error" />
                        )}
                        {casualtyRows.some(r => r.evac_overwhelmed) && (
                          <Chip icon={<WarningAmberIcon />} label="MEDEVAC capacity overwhelmed" color="warning" />
                        )}
                        {casualtyRows.some(r => r.gh_ok === false) && (
                          <Chip icon={<WarningAmberIcon />} label="Golden Hour at risk" color="warning" />
                        )}
                        {casualtyRows.every(r => r.mascal === 'NORMAL' && !r.evac_overwhelmed && r.gh_ok !== false) && (
                          <Chip icon={<CheckCircleIcon />} label="No critical flags this day" color="success" />
                        )}
                        {/* T1 surgical demand */}
                        <Chip label={`T1 surgical: ${totalCas.t1} today`} variant="outlined"
                          color={totalCas.t1 > 10 ? 'warning' : 'default'} />
                        {/* Surgical backlog (pending T1 queue) */}
                        {sd.surgBacklogTotal > 0 && (
                          <Chip icon={<WarningAmberIcon />}
                            label={`Surgical backlog: ${sd.surgBacklogTotal} pending`}
                            variant="outlined" color="warning" />
                        )}
                        {/* Blood pRBC consumed today */}
                        <Chip label={`Blood pRBC today: ${casualtyRows.reduce((a, r) => a + (r.blood_prbc || 0), 0).toFixed(0)} units`}
                          variant="outlined" />
                        {/* Cumulative blood remaining */}
                        {sd.bloodPRBCRemaining !== null && (
                          <Chip
                            label={`pRBC remaining: ${sd.bloodPRBCRemaining} / ${sd.initialPRBC} units`}
                            variant="outlined"
                            color={sd.initialPRBC > 0 && sd.bloodPRBCRemaining / sd.initialPRBC < 0.25 ? 'error' :
                                   sd.initialPRBC > 0 && sd.bloodPRBCRemaining / sd.initialPRBC < 0.50 ? 'warning' : 'default'}
                          />
                        )}
                        {/* MEDEVAC sorties planned */}
                        {sd.medevacDailySorties > 0 && (
                          <Chip label={`MEDEVAC: ${sd.medevacDailySorties} sorties/day`} variant="outlined" />
                        )}
                      </Box>

                      {/* Export SITREP as text */}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button size="small" variant="outlined" startIcon={<AssessmentIcon />}
                          onClick={() => {
                            const lines = [
                              `MEDICAL SITREP — ${title}`,
                              `Generated: ${new Date().toLocaleString()}`,
                              '',
                              '=== G1 CASUALTY SUMMARY (THIS DAY) ===',
                              ['Unit', 'KIA', 'DOW', 'WIA', 'DNBI', 'Total', 'RTD', 'T1'].join('\t'),
                              ...casualtyRows.map(r =>
                                [r.name, r.kia, r.dow, r.wia, r.dnbi, r.kia+r.wia+r.dow+r.dnbi, r.rtd, r.t1].join('\t')
                              ),
                              ['TOTAL', totalCas.kia, totalCas.dow, totalCas.wia, totalCas.dnbi,
                               totalCas.kia+totalCas.wia+totalCas.dow+totalCas.dnbi, totalCas.rtd, totalCas.t1].join('\t'),
                              '',
                              '=== G4 MEDICAL SYSTEM STATUS ===',
                              ['Facility', 'Role', 'Beds', 'Occ%', 'Status'].join('\t'),
                              ...medRows.map(r =>
                                [r.unit, r.role, r.beds, r.occ.toFixed(0)+'%', r.status].join('\t')
                              ),
                              '',
                              '=== G3 OPERATIONAL FLAGS ===',
                              `T1 surgical demand today: ${totalCas.t1}`,
                              sd.surgBacklogTotal > 0 ? `Surgical backlog: ${sd.surgBacklogTotal} pending` : 'Surgical backlog: CLEAR',
                              `Blood pRBC consumed today: ${casualtyRows.reduce((a, r) => a + (r.blood_prbc || 0), 0).toFixed(0)} units`,
                              sd.bloodPRBCRemaining !== null
                                ? `pRBC remaining (cumulative D1-D${sitrepDay}): ${sd.bloodPRBCRemaining} / ${sd.initialPRBC} units`
                                : 'pRBC remaining: N/A (no initial stock configured)',
                              sd.medevacDailySorties > 0 ? `MEDEVAC: ${sd.medevacDailySorties} sorties/day planned` : '',
                              casualtyRows.some(r => r.mascal === 'MASCAL' || r.mascal === 'CRITICAL') ? 'FLAG: MASCAL — at least 1 facility critical' : '',
                              casualtyRows.some(r => r.evac_overwhelmed) ? 'FLAG: MEDEVAC capacity overwhelmed' : '',
                              casualtyRows.some(r => r.gh_ok === false) ? 'FLAG: Golden Hour at risk' : '',
                            ].filter(l => l !== '').join('\n');
                            try {
                              navigator.clipboard.writeText(lines);
                            } catch {
                              const blob = new Blob([lines], { type: 'text/plain' });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a'); a.href = url;
                              a.download = `SITREP_D${sitrepDay}_${sitrepEchelon}.txt`;
                              document.body.appendChild(a); a.click();
                              document.body.removeChild(a); URL.revokeObjectURL(url);
                            }
                          }}>
                          Copy SITREP
                        </Button>
                      </Box>
                    </Box>
                  );
                })()}
              </Box>
            );
          })()}
        </AccordionDetails>
      </Accordion>

      {/* F5 — Inject Script */}
      <Accordion
        expanded={phaseExpanded.injectScript}
        onChange={(_, o) => setPhaseExpanded(p => ({ ...p, injectScript: o }))}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <ListAltIcon color="action" />
            <Typography fontWeight="bold">{t('tab7.inject_script_title')}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              {t('tab7.inject_script_caption')}
            </Typography>
            <Box sx={{ flex: 1 }} />
            {/* Scheduling countdown */}
            {schedulingActive && nextScheduled && (
              <Chip
                icon={<AlarmIcon />}
                label={`Next: ${nextScheduled.label} in ${nextScheduled.formatted}`}
                size="small" color="warning"
                sx={{ fontSize: '0.65rem', height: 22 }}
              />
            )}
            {injectScript.length > 0 && (
              <Chip label={`${injectScript.length} entries`} size="small" color="default" />
            )}
            {/* Auto-Schedule toggle — trainer only, has scheduled entries */}
            {userRole === 'TRAINER' && injectScript.some(e => e.scheduled_time && !e.fired) && (
              <Button
                size="small"
                variant={schedulingActive ? 'contained' : 'outlined'}
                color={schedulingActive ? 'success' : 'inherit'}
                startIcon={<AlarmIcon />}
                onClick={e => { e.stopPropagation(); setSchedulingActive(p => !p); }}
                sx={{ ml: 0.5, fontSize: '0.65rem', py: 0.25, minWidth: 0 }}
              >
                {schedulingActive ? 'Auto ON' : 'Auto OFF'}
              </Button>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 1 }}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Build an ordered list of injects to fire during the exercise. Each entry pre-fills
              the corresponding inject panel with configured parameters.
            </Typography>

            {/* Script list */}
            {injectScript.length === 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                No script entries yet. Add entries below to plan your inject sequence.
              </Alert>
            )}

            {injectScript.length > 0 && (
              <Table size="small" sx={{ mb: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40 }}><strong>#</strong></TableCell>
                    <TableCell><strong>Label</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>D+N</strong></TableCell>
                    <TableCell><strong>Sched</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell align="center"><strong>Action</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {injectScript.map((entry, idx) => (
                    <TableRow key={entry.id} sx={{ opacity: entry.fired ? 0.6 : 1 }}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{entry.label}</TableCell>
                      <TableCell>
                        <Chip label={entry.inject_type} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                      </TableCell>
                      <TableCell>{dayLbl(entry.op_day)}</TableCell>
                      <TableCell>
                        {entry.scheduled_time
                          ? <Chip icon={<AlarmIcon />} label={entry.scheduled_time} size="small"
                              color={entry.fired ? 'default' : 'info'} variant="outlined"
                              sx={{ fontSize: '0.65rem', height: 20 }} />
                          : <Typography variant="caption" color="text.disabled">—</Typography>
                        }
                      </TableCell>
                      <TableCell>
                        {entry.fired
                          ? <Chip label={entry.auto_fired ? '⏰ Auto' : 'Fired'} size="small"
                              color="success" sx={{ fontSize: '0.65rem', height: 20 }} />
                          : <Chip label="Ready" size="small" color="primary" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                        }
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Button
                            size="small"
                            variant="contained"
                            color={entry.fired ? 'inherit' : 'secondary'}
                            disabled={entry.fired}
                            sx={{ fontSize: '0.65rem', py: 0.25, px: 1, minWidth: 0 }}
                            onClick={() => {
                              // Mark fired + scroll to inject panel
                              const updated = injectScript.map((e, i) =>
                                i === idx ? { ...e, fired: true, fired_at: new Date().toISOString() } : e
                              );
                              lsWrite(TAB7_STORAGE_KEYS.INJECT_SCRIPT, updated);
                              setInjectScript(updated);
                              // P2-S4: broadcast inject event to all connected students
                              if (userRole === 'TRAINER' && trainerSessionCode) {
                                realtimeService.send('inject_fired', {
                                  inject_type: entry.inject_type,
                                  op_day:      entry.op_day,
                                  label:       entry.label,
                                });
                              }
                              // Expand the matching phase accordion and scroll
                              const phaseMap = {
                                MROE: 'mroeInject', CIV: 'civInject', MASCAL: 'mascalInject',
                                MEDEVAC: 'medevacLossInject', MTF: 'mtfDisplaceInject',
                                LOC: 'locInterdictInject', R2E: 'r2eLossInject',
                              };
                              const phKey = phaseMap[entry.inject_type];
                              if (phKey) setPhaseExpanded(p => ({ ...p, [phKey]: true }));
                              setTimeout(() => {
                                const el = document.querySelector(`[data-inject-phase="${entry.inject_type}"]`);
                                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                              }, 200);
                            }}
                          >
                            {entry.fired ? 'Fired' : 'Load & Fire'}
                          </Button>
                          <IconButton
                            size="small"
                            onClick={() => {
                              const updated = injectScript.filter((_, i) => i !== idx);
                              lsWrite(TAB7_STORAGE_KEYS.INJECT_SCRIPT, updated);
                              setInjectScript(updated);
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {/* Add entry */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Add Script Entry</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                  size="small"
                  label={t('tab7.label')}
                  value={scriptDraft.label}
                  onChange={e => setScriptDraft(d => ({ ...d, label: e.target.value }))}
                  sx={{ minWidth: 180 }}
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>{t('tab7.inject_type')}</InputLabel>
                  <Select
                    label={t('tab7.inject_type')}
                    value={scriptDraft.inject_type}
                    onChange={e => setScriptDraft(d => ({ ...d, inject_type: e.target.value }))}
                  >
                    <MenuItem value="MROE">MRoE</MenuItem>
                    <MenuItem value="CIV">Civilian Density</MenuItem>
                    <MenuItem value="MASCAL">MASCAL Event</MenuItem>
                    <MenuItem value="MEDEVAC">MEDEVAC Loss</MenuItem>
                    <MenuItem value="MTF">MTF Displacement</MenuItem>
                    <MenuItem value="LOC">LOC Interdiction</MenuItem>
                    <MenuItem value="R2E">R2E Loss</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label={t('tab7.op_day')}
                  type="number"
                  inputProps={{ min: 1, max: 30 }}
                  value={scriptDraft.op_day}
                  onChange={e => setScriptDraft(d => ({ ...d, op_day: Number(e.target.value) || 1 }))}
                  sx={{ width: 90 }}
                />
                <TextField
                  size="small"
                  label={t('tab7.scheduled_time')}
                  type="time"
                  value={scriptDraft.scheduled_time}
                  onChange={e => setScriptDraft(d => ({ ...d, scheduled_time: e.target.value }))}
                  sx={{ width: 130 }}
                  InputLabelProps={{ shrink: true }}
                  helperText="Optional — auto-fire"
                />
                <Button
                  variant="contained"
                  size="small"
                  disabled={!scriptDraft.label.trim()}
                  onClick={() => {
                    const newEntry = {
                      id:             crypto.randomUUID(),
                      label:          scriptDraft.label.trim(),
                      inject_type:    scriptDraft.inject_type,
                      op_day:         scriptDraft.op_day,
                      scheduled_time: scriptDraft.scheduled_time || null,
                      fired:          false,
                      fired_at:       null,
                      auto_fired:     false,
                      params:         {},
                    };
                    const updated = [...injectScript, newEntry];
                    lsWrite(TAB7_STORAGE_KEYS.INJECT_SCRIPT, updated);
                    setInjectScript(updated);
                    setScriptDraft(d => ({ ...d, label: '', scheduled_time: '' }));
                  }}
                >
                  Add
                </Button>
                {injectScript.length > 0 && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    onClick={() => {
                      if (!window.confirm('Reset all script entries?')) return;
                      lsWrite(TAB7_STORAGE_KEYS.INJECT_SCRIPT, []);
                      setInjectScript([]);
                    }}
                  >
                    Reset Script
                  </Button>
                )}
              </Box>
            </Paper>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Phase 11 -- Post-Inject Impact Assessment (ENH-031b) */}
      <Accordion
        expanded={phaseExpanded.injectImpact}
        onChange={(_, o) => setPhaseExpanded(p => ({ ...p, injectImpact: o }))}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <TimelineIcon color="secondary" />
            <Typography fontWeight="bold">{t('tab7.post_inject')}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              ENH-031b · recalculation · baseline vs post-inject delta
            </Typography>
            <Box sx={{ flex: 1 }} />
            {injectResult && (
              <Chip label="Calculated" size="small" color="secondary" />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 1 }}>
          {renderInjectImpact()}
        </AccordionDetails>
      </Accordion>

      {/* P3-IMP-001 — Custom Inject Panel (student-only, shown when trainer fires one) */}
      {userRole === 'STUDENT' && activeCustomInject && (
        <Box
          data-custom-inject-panel
          sx={{
            mb: 2, p: 2,
            border: '2px solid',
            borderColor: customInjectDecision ? 'success.main' : 'warning.main',
            borderRadius: 2,
            bgcolor: customInjectDecision ? 'success.50' : 'warning.50',
          }}
        >
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={700} color={customInjectDecision ? 'success.dark' : 'warning.dark'}>
              ⚡ Custom Inject — {dayLbl(activeCustomInject.op_day)}
            </Typography>
            <Box sx={{ flex: 1 }} />
            {customInjectDecision && (
              <Button size="small" variant="text" color="inherit"
                onClick={() => { setActiveCustomInject(null); setCustomInjectDecision(null); }}>
                Dismiss
              </Button>
            )}
          </Box>

          {/* Title + narrative */}
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
            {activeCustomInject.title}
          </Typography>
          {activeCustomInject.narrative && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, whiteSpace: 'pre-wrap' }}>
              {activeCustomInject.narrative}
            </Typography>
          )}

          {/* Chosen badge OR option buttons */}
          {customInjectDecision ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={`✓ Option ${customInjectDecision.key}: ${customInjectDecision.label}`}
                color="success" size="small"
                sx={{ fontWeight: 700, fontSize: '0.8rem' }}
              />
              {(customInjectDecision.delta_dow !== 0 || customInjectDecision.delta_blood !== 0) && (
                <>
                  {customInjectDecision.delta_dow !== 0 && (
                    <Chip
                      label={`ΔDoW ${customInjectDecision.delta_dow > 0 ? '+' : ''}${customInjectDecision.delta_dow}`}
                      size="small" variant="outlined"
                      color={customInjectDecision.delta_dow > 0 ? 'error' : 'success'}
                      sx={{ fontSize: '0.72rem' }}
                    />
                  )}
                  {customInjectDecision.delta_blood !== 0 && (
                    <Chip
                      label={`ΔBlood ${customInjectDecision.delta_blood > 0 ? '+' : ''}${customInjectDecision.delta_blood}u`}
                      size="small" variant="outlined"
                      color={customInjectDecision.delta_blood > 0 ? 'error' : 'success'}
                      sx={{ fontSize: '0.72rem' }}
                    />
                  )}
                </>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {(activeCustomInject.options || []).map(opt => (
                <Button
                  key={opt.key}
                  variant="outlined"
                  color="warning"
                  size="small"
                  sx={{ fontWeight: 700, minWidth: 80 }}
                  onClick={() => handleCustomInjectChoose(activeCustomInject, opt)}
                >
                  {opt.key}: {opt.label || `Option ${opt.key}`}
                </Button>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* F3 — Decision Log (renamed from "Session Debrief" — Session A) */}
      {sessionHistory.length > 0 && (
        <Accordion
          expanded={phaseExpanded.debrief}
          onChange={(_, o) => setPhaseExpanded(p => ({ ...p, debrief: o }))}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <ListAltIcon color="secondary" />
              <Typography fontWeight="bold">{t('tab7.decision_log')}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                {t('tab7.decision_log_caption')}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Chip label={`${sessionHistory.length} entr${sessionHistory.length !== 1 ? 'ies' : 'y'}`} size="small" color="secondary" />
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 1 }}>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<WarningAmberIcon />}
                  onClick={() => setShowClearDialog(true)}
                >
                  Clear Session
                </Button>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 28 }}><strong>#</strong></TableCell>
                    <TableCell><strong>Time</strong></TableCell>
                    <TableCell><strong>D+N</strong></TableCell>
                    <TableCell><strong>Window</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Events</strong></TableCell>
                    <TableCell><strong>Threat</strong></TableCell>
                    <TableCell><strong>Action</strong></TableCell>
                    <TableCell><strong>Conf.</strong></TableCell>
                    <TableCell align="right"><strong>ΔDOW</strong></TableCell>
                    <TableCell align="right"><strong>ΔBlood</strong></TableCell>
                    <TableCell align="right"><strong>ΔMASCAL</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessionHistory.map((entry, idx) => {
                    const metas = entry.inject_metas || [];
                    const threats   = [...new Set(metas.map(m => m.threat_class).filter(Boolean))];
                    const actions   = [...new Set(metas.map(m => m.rec_action).filter(Boolean))];
                    const confs     = [...new Set(metas.map(m => m.confidence).filter(Boolean))];
                    const worstConf = confs.includes('LOW') ? 'LOW' : confs.includes('MEDIUM') ? 'MEDIUM' : confs.includes('HIGH') ? 'HIGH' : null;
                    const threatColor = threats.includes('LIFE') || threats.includes('BOTH') ? 'error' : threats.includes('MISSION') ? 'warning' : 'default';
                    const actionColor = actions.includes('EVACUATE') ? 'error' : actions.includes('PREPOSITION') ? 'warning' : 'default';
                    const confColor   = worstConf === 'LOW' ? 'error' : worstConf === 'MEDIUM' ? 'warning' : worstConf === 'HIGH' ? 'success' : 'default';
                    const { total_dow, blood_units, mascal_bdes } = entry.delta || {};
                    const deltaStyle = (val) => val == null ? {} : val > 0 ? { color: 'error.main' } : val < 0 ? { color: 'success.main' } : {};
                    const fmtDelta   = (val) => val == null ? '\u2014' : (val > 0 ? '+' : '') + val;
                    const entryWindow = entry.planning_window || 'ORIENT';
                    const entryType   = entry.type || 'INJECT';
                    const entryDay    = entry.op_day != null ? `${dayLbl(entry.op_day)}` : '\u2014';
                    const cmLabels    = (entry.countermeasure_metas || []).map(c => c.id.replace(/_/g, ' '));
                    return (
                      <TableRow key={entry.id}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="caption">
                            {new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{entryDay}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={entryWindow}
                            size="small"
                            color={WINDOW_COLOR[entryWindow] || 'default'}
                            sx={{ fontSize: '0.6rem', height: 18 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={entryType === 'COUNTERMEASURE' ? 'CM' : 'INJ'}
                            size="small"
                            color={entryType === 'COUNTERMEASURE' ? 'success' : 'error'}
                            variant="outlined"
                            sx={{ fontSize: '0.6rem', height: 18 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap' }}>
                            {(entry.inject_summary || []).filter(i => i.decision).map(i => (
                              <Chip key={i.id} label={i.label} size="small" variant="outlined"
                                sx={{ fontSize: '0.6rem', height: 18 }} />
                            ))}
                            {cmLabels.map((lbl, i) => (
                              <Chip key={i} label={lbl} size="small" color="success" variant="outlined"
                                sx={{ fontSize: '0.6rem', height: 18 }} />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {threats.length > 0
                            ? <Chip label={threats.join('/')} size="small" color={threatColor} sx={{ fontSize: '0.65rem', height: 20 }} />
                            : <Typography variant="caption" color="text.disabled">\u2014</Typography>
                          }
                        </TableCell>
                        <TableCell>
                          {actions.length > 0
                            ? <Chip label={actions.join('/')} size="small" color={actionColor} sx={{ fontSize: '0.65rem', height: 20 }} />
                            : <Typography variant="caption" color="text.disabled">\u2014</Typography>
                          }
                        </TableCell>
                        <TableCell>
                          {worstConf
                            ? <Chip label={worstConf} size="small" color={confColor} sx={{ fontSize: '0.65rem', height: 20 }} />
                            : <Typography variant="caption" color="text.disabled">\u2014</Typography>
                          }
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption" sx={deltaStyle(total_dow)}>{fmtDelta(total_dow)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption" sx={deltaStyle(blood_units)}>{fmtDelta(blood_units)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption" sx={deltaStyle(mascal_bdes)}>{fmtDelta(mascal_bdes)}</Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Bottom Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => onNavigate && onNavigate(5)}
        >
          Back to Tab 6 — COA Analysis
        </Button>
        <Button
          variant="contained"
          color="primary"
          endIcon={<NavigateNextIcon />}
          onClick={() => {
            onComplete && onComplete();
            onNavigate && onNavigate(7);
          }}
        >
          Go to Exercise Debrief
        </Button>
      </Box>

      {/* Session C — Debrief snapshot Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%' }} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* IMP-013 — Clear Session confirmation dialog */}
      <Dialog open={showClearDialog} onClose={() => setShowClearDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmberIcon color="error" />
          Clear Session — Confirm Reset
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            This will permanently reset all exercise run data. The following will be cleared:
          </Typography>
          <List dense disablePadding>
            {[
              'All inject decisions (option selected, threat class, recommended action, confidence, notes)',
              'All active countermeasures (blood request, ambulance, MEDEVAC, FRT, AXP, MTF)',
              'Exercise clock (stopped and reset to D+1)',
              'Post-inject calculation result',
              'Decision log (session history)',
              'Debrief snapshots (exercise progression trace)',
            ].map((item) => (
              <ListItem key={item} sx={{ py: 0.25, pl: 1 }}>
                <ListItemText
                  primary={item}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                />
              </ListItem>
            ))}
          </List>
          <Alert severity="info" sx={{ mt: 2 }} >
            <Typography variant="body2">
              Inject configuration (scenario type, day, parameters) and the loaded baseline are <strong>not</strong> affected.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button variant="outlined" onClick={() => setShowClearDialog(false)}>
            Cancel
          </Button>
          <Button variant="contained" color="error" startIcon={<WarningAmberIcon />} onClick={handleClearSession}>
            Clear Session
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
});

export default Tab7Exercise;
